import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import useAutoReload from '../hooks/useAutoReload';

// #642 — the BUILD_SHA the tablet was built with. Set by the CRA build at
// container-build time via the `REACT_APP_BUILD_SHA` env var (which the
// Dockerfile takes as a `--build-arg BUILD_SHA=$GITHUB_SHA`). On local
// dev (`npm start`) the var is unset and we fall back to `'dev'`, which
// `useAutoReload` treats as the "skip the staleness check" bypass so
// developers don't get auto-reloaded every reconnect.
const LOADED_BUILD = process.env.REACT_APP_BUILD_SHA || 'dev';
import CompanionFace from '../components/CompanionFace';
// StatusMessage replaced by inline status rendering inside CallStrip
// (#405). Kept available via direct import if a future caller needs it.
import AudioPlayer from '../components/AudioPlayer';
import PhotoCarousel from '../components/PhotoCarousel';
import VideoOverlay from '../components/VideoOverlay';
// #1089 — standardized activity scaffold (state row + transcription + X),
// the single chrome owner for every in-call activity.
import ActivityFrame, { FRAME_SPLIT_MIN_WIDTH } from '../components/ActivityFrame';
import MusicPlayer from '../components/MusicPlayer';
// #448 — NowPlayingPill replaced by full-screen MusicView. Import
// retained briefly during migration would trigger an unused-import
// warning, so the line is removed entirely.
import MusicView from '../components/MusicView';
import IdleDimOverlay from '../components/IdleDimOverlay';
import DominoGame from '../components/DominoGame';
import SolitaireGame from '../components/SolitaireGame';
import GameCarousel from '../components/GameCarousel';
import CallStrip from '../components/CallStrip';
// YouSaidCard removed from the focal area (Bas, 2026-05-08). The
// component file + tests stay around in case it's wanted back.
import BertaReplyPanel from '../components/BertaReplyPanel';
import UserTranscriptRow from '../components/UserTranscriptRow';
import { createSendChain } from '../utils/sendChain';
import { buildAudioEndPayload } from '../utils/audioEndPayload';
// #703 — tablet phone-home for Alpha diagnostics. Helpers send a
// device snapshot at call start and time-series events on reconnect /
// errors over the existing voice WebSocket.
import {
  collectDeviceSnapshot as _collectClientSnapshot,
  sendClientDiagnostics as _sendClientDiagnostics,
  sendClientEvent as _sendClientEvent,
} from '../utils/clientDiagnostics';
// #718 — tablet console capture. Installing here at module load
// ensures the wrapper catches messages from app start, not just from
// when this component mounts. Idempotent — safe under
// React.StrictMode and hot reloads.
import {
  installConsoleCapture as _installConsoleCapture,
  flushConsoleCapture as _flushConsoleCapture,
} from '../utils/consoleCapture';

_installConsoleCapture();
// #606 — AudioWorklet PCM capture, drop-in replacement for MediaRecorder
// for the streaming-to-Deepgram path. Returns a MediaRecorder-shaped
// object so the existing mediaRecorderRef call sites work unchanged.
// Returns null when the worklet can't load (old browser, CSP, etc.) so
// we fall back to MediaRecorder transparently.
import { createPcmCapture, createPcmAudioContext } from '../audio/pcmCapture';
// Audio self-test (Settings → Audio self-test) — pure helpers that turn
// the captured PCM frames back into Web-Audio-playable Float32 samples.
import { int16ToFloat32, pcmDurationMs, selfTestMicConstraints } from '../audio/pcmPlayback';
// #897 — Per-call warm-up timings + observability helper. Tracks
// getUserMedia / WS-open / first-chunk / first-partial offsets during
// the "Berta is connecting…" UI moment and emits one structured
// console line at end-of-call (picked up by #718 client_console_logs).
import { createWarmupTimings } from '../audio/audioWarmup';
import { videoLifecycleSignal } from '../utils/videoLifecycleSignal';
import { shouldFireOverlayCloseMicFallback } from '../utils/overlayCloseMicFallback';
import { useLang, formatString } from '../i18n/LanguageContext';

// #441 Phase 2 — i18n bucket for the TabletApp screens (settings
// overlay, calling, idle, hangup, mic warning). Defensive
// fallbacks for every key so the component renders English when
// the LanguageProvider isn't in the tree.
function _useScreenStrings() {
  const ctx = useLang();
  const t = ctx && ctx.t && ctx.t.tablet;
  return {
    settings: {
      title:               (t && t.settings && t.settings.title) || 'Settings',
      sectionStyle:        (t && t.settings && t.settings.sectionCompanionStyle) || 'Companion style',
      styleSmiley:         (t && t.settings && t.settings.styleSmiley) || '😊 Smiley',
      styleWaveform:       (t && t.settings && t.settings.styleWaveform) || '📊 Waveform',
      styleTextOnly:       (t && t.settings && t.settings.styleTextOnly) || '🔤 Text only',
      gearAria:            (t && t.settings && t.settings.gearAria) || 'Settings',
      refreshAria:         (t && t.settings && t.settings.refreshAria) || 'Refresh the app (long-press)',
    },
    idle:    { tapToCall: (t && t.idle    && t.idle.tapToCall)    || 'Tap to call' },
    calling: { template:  (t && t.calling && t.calling.callingX)  || 'Calling {name}…' },
    incoming:{ from:      (t && t.incoming && t.incoming.callingFrom) || 'Incoming call from {name}',
               accept:    (t && t.incoming && t.incoming.accept) || 'Answer',
               decline:   (t && t.incoming && t.incoming.decline) || 'Decline' },
    hangup:  { button:    (t && t.hangup && t.hangup.button) || '📵 Hang up' },
    // #544 — mic-warning + first-empty-turn reassurance. Both were
    // hardcoded English in the listening/done WS-event handler;
    // rewired through i18n with defensive fallbacks per the same
    // pattern as everything else in this helper.
    micwarning: {
      takeYourTime:    (t && t.micwarning && t.micwarning.takeYourTime)
                       || "Don't worry, take your time",
      checkMicrophone: (t && t.micwarning && t.micwarning.checkMicrophone)
                       || 'Having trouble hearing you — check your microphone',
      // #551 — getUserMedia error toasts.
      accessDenied:    (t && t.micwarning && t.micwarning.accessDenied)
                       || 'Microphone access needed. Please allow it in your browser settings.',
      notFound:        (t && t.micwarning && t.micwarning.notFound)
                       || 'No microphone found. Please connect a microphone.',
      generic:         (t && t.micwarning && t.micwarning.generic)
                       || 'Microphone error — please try again',
    },
    // #551 — settings overlay mic-test panel + section heading.
    settingsMic: {
      sectionLabel:    (t && t.settings && t.settings.sectionMicrophone) || 'Microphone',
      idleLabel:       (t && t.mictest && t.mictest.idle)      || '🎤 Test microphone',
      recordingLabel:  (t && t.mictest && t.mictest.recording) || '⏹ Stop and check',
      checkingLabel:   (t && t.mictest && t.mictest.checking)  || '⏳ Checking…',
      weHeardTemplate: (t && t.mictest && t.mictest.weHeard)   || '✅ We heard: "{text}"',
      notHeardLabel:   (t && t.mictest && t.mictest.notHeard)  || "❌ We couldn't hear you clearly",
    },
    // Audio self-test (record-and-playback) panel in the settings overlay.
    selfTest: {
      section:     (t && t.selftest && t.selftest.section)     || '🎧 Audio self-test',
      record:      (t && t.selftest && t.selftest.record)      || '🔴 Record',
      recordAgain: (t && t.selftest && t.selftest.recordAgain) || '🔴 Record again',
      stop:        (t && t.selftest && t.selftest.stop)        || '⏹ Stop recording',
      play:        (t && t.selftest && t.selftest.play)        || '▶️ Play back',
      playing:     (t && t.selftest && t.selftest.playing)     || '🔊 Playing…',
      hint:        (t && t.selftest && t.selftest.hint)        || 'Records exactly what is sent to the server, then plays it back so you can hear the captured quality.',
      error:       (t && t.selftest && t.selftest.error)       || '❌ Recording failed — high-fidelity (PCM) capture is unavailable in this browser.',
      raw:          (t && t.selftest && t.selftest.raw)          || '🎛 Raw mic (no noise-suppression / AGC)',
      rawHint:      (t && t.selftest && t.selftest.rawHint)      || 'Off = exactly what calls send. On = unprocessed mic, to compare tone / warmth.',
      tagRaw:       (t && t.selftest && t.selftest.tagRaw)       || 'raw',
      tagProcessed: (t && t.selftest && t.selftest.tagProcessed) || 'as sent',
    },
    // #551 — incoming-call screen had three hardcoded strings.
    // Existing `incoming.from/.accept/.decline` keys covered the
    // formal labels but the JSX renders user-facing copy ("Answer"
    // / "Not now" / "{name} is calling you") that's slightly
    // different in register, so we expose them as their own keys.
    incomingScreen: {
      callingYouTemplate: (t && t.incoming && t.incoming.callingYou) || '{name} is calling you',
      answer:             (t && t.incoming && t.incoming.accept)     || 'Answer',
      notNow:             (t && t.incoming && t.incoming.notNow)     || 'Not now',
    },
    // #551 — idle-screen big call button. Was '📞 Call {name}'
    // hardcoded.
    idleCall: {
      buttonTemplate: (t && t.idle && t.idle.callButton) || '📞 Call {name}',
    },
    // #551 — "Call ended" reply-panel text set in three places when
    // the WS observes the call ended.
    callEnded: (t && t.calling && t.calling.callEnded) || 'Call ended',
    // #551 — fallback domino_error message when the server-supplied
    // body is missing.
    dominoMoveError: (t && t.domino && t.domino.moveError)
               || "That move didn't work — try another tile.",
    // #551 — tablet activation / setup screens (rendered before
    // the call UI loads when the tablet is being linked or has no
    // userId yet).
    setup: {
      activatingTitle: (t && t.setup && t.setup.activatingTitle) || 'Setting up your tablet…',
      activatingBody:  (t && t.setup && t.setup.activatingBody)  || 'Please wait while we link this tablet to your account.',
      failedTitle:     (t && t.setup && t.setup.failedTitle)     || 'Setup failed',
      // The body uses {strongOpen}/{strongClose} markers so the
      // emphasis can survive translation. The setup screen
      // renderer below splits on these tokens to insert a real
      // <strong> element.
      failedBody:      (t && t.setup && t.setup.failedBody)      || 'Please go back to the {strongOpen}family portal{strongClose} and scan a new QR code.',
      notSetUpTitle:   (t && t.setup && t.setup.notSetUpTitle)   || 'This tablet is not set up yet',
      notSetUpBody:    (t && t.setup && t.setup.notSetUpBody)    || 'Ask a family member to open the Amigo Mio portal and scan the QR code with this tablet.',
      // #1120 — explicit setup confirm, shown after opening a ?setup= link
      // and BEFORE the tablet is actually paired. {name}/{companion} are
      // filled from the read-only /tablet/setup-info lookup.
      confirmTitle:     (t && t.setup && t.setup.confirmTitle)     || 'Set up this tablet?',
      confirmBody:      (t && t.setup && t.setup.confirmBody)      || 'This tablet will be set up for {name}, with {companion} as their companion.',
      confirmBodyNoName:(t && t.setup && t.setup.confirmBodyNoName)|| 'This tablet will be set up for your loved one.',
      confirmButton:    (t && t.setup && t.setup.confirmButton)    || 'Set up this tablet',
      confirmCancel:    (t && t.setup && t.setup.confirmCancel)    || 'Not now',
    },
  };
}

// #551 — render a translation string with {strongOpen}…{strongClose}
// markers as JSX where the marked span becomes a <strong> element.
// Falls back to the raw string if the markers are missing (e.g. a
// translation forgot them).
function _renderWithStrong(template) {
  if (!template || typeof template !== 'string') return template || '';
  const m = template.match(/^([\s\S]*?)\{strongOpen\}([\s\S]*?)\{strongClose\}([\s\S]*)$/);
  if (!m) return template;
  return (
    <>
      {m[1]}<strong>{m[2]}</strong>{m[3]}
    </>
  );
}

// Resolves a STYLE_OPTIONS value to its display label (i18n + fallback).
function _styleLabel(value, scr) {
  if (value === 'smiley')   return scr.settings.styleSmiley;
  if (value === 'waveform') return scr.settings.styleWaveform;
  if (value === 'text-only') return scr.settings.styleTextOnly;
  return value;
}
import { useIdleDim } from '../hooks/useIdleDim';
import useViewportMode from '../hooks/useViewportMode';
// #468 — progressive reveal of `reply_chunk` events. Hook owns its own
// queue + timer so the visible reply paces with the audio instead of
// dumping the full text the moment the LLM finishes streaming.
import useProgressiveReply from '../hooks/useProgressiveReply';
import { useFavicon } from '../utils/useFavicon';
// #1058 — accidental re-dial guard. After a call ends, a stray tap on
// the freshly-rendered idle "Call" button can start a phantom call;
// this gates the user-initiated call path on a short cooldown.
import { POST_HANGUP_COOLDOWN_MS, isWithinReDialCooldown } from '../utils/reDialGuard';

const VERSION = '2.54.0';


// ── Long-press refresh button ─────────────────────────────────────────
//
// Deliberate companion to the #642 useAutoReload hook for the edge case
// it can't cover by design: the very first call after a deploy is
// always on the stale bundle, because `server_hello` (the staleness
// signal) only arrives AFTER the WS opens — by then the call is in
// flight, and the hook's "never reload mid-call" policy is correct.
//
// This button gives Bas (and any future operator) a one-tap manual
// override on the idle screen. Long-press 1 s to fire to guard
// against accidental taps by the elderly user. Mirrored top-left of
// the settings gear top-right so they read as a pair without
// inviting curiosity.
//
// A thin orange progress ring fills during the hold to teach
// "keep holding". On early release the ring resets — no fire, no
// confusion.
function LongPressRefreshButton({ ariaLabel }) {
  const [holding, setHolding] = React.useState(false);
  const timerRef = React.useRef(null);

  // 1 s feels right — long enough that a stray tap doesn't trigger,
  // short enough that an intentional hold doesn't feel laggy.
  const HOLD_MS = 1000;

  const start = () => {
    setHolding(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      window.location.reload();
    }, HOLD_MS);
  };
  const cancel = () => {
    setHolding(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Clean up on unmount in case the button disappears mid-hold
  // (e.g. inbound call lifts the user off the idle screen).
  React.useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const radius = 18;
  const circumference = 2 * Math.PI * radius;

  return (
    <button
      type="button"
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
      onContextMenu={(e) => e.preventDefault()}
      aria-label={ariaLabel}
      style={{
        position: 'fixed',
        top: '16px',
        left: '16px',
        width: '40px',
        height: '40px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        opacity: holding ? 1 : 0.5,
        padding: 0,
        zIndex: 10,
        transition: 'opacity 200ms ease',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'manipulation',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {holding && (
        <svg
          width="40" height="40" viewBox="0 0 40 40"
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          aria-hidden="true"
        >
          <circle
            cx="20" cy="20" r={radius}
            fill="none"
            stroke="#f97316"
            strokeWidth="2"
            strokeLinecap="round"
            // Start at the 12 o'clock position and draw clockwise.
            transform="rotate(-90 20 20)"
            strokeDasharray={circumference}
            strokeDashoffset={circumference}
            style={{
              animation: `lp-refresh-fill ${HOLD_MS}ms linear forwards`,
            }}
          />
        </svg>
      )}
      <span style={{ fontSize: '22px', lineHeight: 1 }} aria-hidden="true">🔄</span>
      <style>{`
        @keyframes lp-refresh-fill {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </button>
  );
}

const STYLE_OPTIONS = [
  { value: 'smiley', label: '😊 Smiley' },
  { value: 'waveform', label: '📊 Waveform' },
  { value: 'text-only', label: '🔤 Text only' },
];
// #390 — WS URL derives from the page's own host, NOT a hardcoded prod
// URL. Without this, the tablet bundle served from staging.amigomio.app
// would still WebSocket to wss://amigomio.app/ws/voice — meaning every
// voice call from the staging UI hit PROD's app container, not staging's.
// (That bug masked everything we tried to test on staging today.)
// Override possible via REACT_APP_WS_URL at build time if needed.
const WS_URL = process.env.REACT_APP_WS_URL ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/voice`
    : 'wss://amigomio.app/ws/voice');

const VAD_SPEECH_THRESHOLD = 0.02;     // static fallback when calibration fails
const VAD_MIN_SPEECH_MS = 300;
const VAD_MAX_RECORD_MS = 60000; // 60s — elderly users often tell long stories
// #337 — adaptive VAD threshold. Sample the room's noise floor for the
// first VAD_CALIBRATION_MS, take the 90th-percentile RMS, and set the
// per-session speaking threshold to floor × MULTIPLIER, clamped to a
// safe range. P90 (rather than mean) is robust if the user starts
// speaking partway through the calibration window — their speech
// becomes the high end and only modestly inflates the noise estimate.
const VAD_CALIBRATION_MS = 400;
const VAD_CALIBRATION_MULTIPLIER = 3.0;
// #604 — wait for silence before starting the 400ms calibration window.
//
// Bas's 2026-05-10 staging cut-off (post-#601 AGC fix): wired close-talk
// headset, he replied to Berta within ~200 ms, the 400 ms calibration
// window started immediately on mic-enable and ended up sampling the
// FIRST 400 ms of his speech instead of room ambient. p90 came out at
// 0.08-0.10 — HIGHER than his speech median (0.064-0.068), which is
// physically impossible for real ambient on a wired close-talk mic.
// Threshold maxed at 0.05 cap; soft phonemes between syllables dropped
// below it, the post-speech silence timer armed, and his turn ended
// mid-thought. He said "she just cut me off" in the call.
//
// Fix: before starting the 400 ms RMS sampling window, run a small
// "wait for silence" phase that requires VAD_SILENCE_SAMPLES_REQUIRED
// consecutive sub-floor samples before declaring "calibration can
// start". If silence never arrives within VAD_WAIT_FOR_SILENCE_MAX_MS
// (the user is talking nonstop from mic-open), bail to the static
// VAD_SPEECH_THRESHOLD as a safe fallback — better to risk an
// occasional false-positive than to silently cut soft speakers off.
//
// 0.015 is below the static fallback (0.02 = VAD_SPEECH_THRESHOLD) and
// well above typical clean-ambient (0.001-0.008), so it cleanly
// separates "real silence" from "speech contamination" without being
// so strict that no real session ever satisfies it. The 6 samples
// (~100 ms at 60 fps) avoids being fooled by single quiet phonemes
// during continuous talk.
const VAD_SILENCE_FLOOR_FOR_CALIBRATION = 0.015;
const VAD_SILENCE_SAMPLES_REQUIRED = 6;
const VAD_WAIT_FOR_SILENCE_MAX_MS = 800;
// Hybrid VAD strategy (see vad_strategy below) — when active, the
// post-speech silence timer becomes a SAFETY NET rather than the
// primary turn-end signal: Deepgram's `utterance_end` event is the
// fast path (smart, language-aware, ~800 ms typical), and this
// generous timer only fires if Deepgram doesn't (provider down,
// network blip, or for users on a future non-Deepgram STT path).
//
// 5000 ms is the safety-net duration. Far more permissive than the
// `client_vad` legacy 1800 ms (or staging's even-shorter 1200 ms),
// which was cutting Bas off mid-thought when he paused 2+ s between
// sentences. With Deepgram's utterance_end usually firing in <1 s
// after speech end, this generous safety net costs nothing in
// happy-path latency — it only matters when the smart signal is
// missing.
const VAD_HYBRID_SAFETY_MS = 5000;
// #492 / #504 — VAD threshold floor.
//
// #492 (Bas 2026-05-08): on a quiet-room call the VAD got stuck
// "listening" for ~60 seconds after he stopped speaking, because
// calibration p90 dropped below 0.0017 → threshold floored at
// 0.005 → ambient noise kept tripping "speech still going". Floor
// raised 0.005 → 0.015.
//
// #504 (same day): 0.015 was too aggressive — Bas's actual speech
// RMS (~0.008-0.012, snr_band=whisper) now sat BELOW the floor, so
// VAD never triggered isSpeaking=true → "initial silence timer"
// fired at 300-800ms → audioBytes < 2000 → classified as mic_error
// (audioEndPayload.js:77) → server skipped STT → empty transcript
// → conversation died.
//
// 0.008 is the compromise: above typical room hum (≤ 0.005-0.007)
// so silence detection terminates, but below typical voiced speech
// (≥ 0.01) so quiet speakers still trigger VAD. Not a perfect single
// value — true fix is band-aware (use #382 speaker_profile to pick
// floor per user). That refactor is out of scope here.
const VAD_THRESHOLD_MIN = 0.008;       // never go below this — pure-noise mic
const VAD_THRESHOLD_MAX = 0.05;        // never go above — would miss quiet voices
// #604 — absolute headroom over the noise floor. Combined with the
// existing multiplicative term (p90 × 3.0) by taking the LOWER of the
// two — more permissive in clean-ambient environments without changing
// behaviour in noisy rooms.
//
// Bas (2026-05-10): wired headset, noise floor p90 = 0.0078 calibrated
// CLEANLY (post-#601 AGC fix worked). Threshold became 0.0078 × 3.0 =
// 0.0234. He spoke immediately and his soft speech never crossed
// 0.0234 → "silence timer fired (initial — never started speaking)" →
// audio sent with reason=vad_silence → server fired silence-ladder →
// Bas frustrated "she just cut me off". The multiplicative formula is
// great in noisy rooms (3× ambient is well above noise spikes) but it
// over-blocks in CLEAN-ambient environments where 3× a tiny floor
// still falls within normal soft-speech RMS range.
//
// 0.005 RMS over noise is roughly equivalent to a +14 dB SNR, which is
// the typical "voiced quiet speech vs room hum" gap. Smaller than that
// and we risk false-positives from breath / fan-blip; larger and we'd
// miss soft speakers (the elderly demographic).
//
// In noisy rooms (p90 ≥ 0.022) the multiplicative term is already
// LOWER than (p90 + 0.005), so this constant has no effect — old
// behaviour preserved. In clean rooms (p90 ≤ 0.022) the additive term
// dominates and gives ~3× more sensitivity.
const VAD_THRESHOLD_HEADROOM = 0.005;
const MIN_RING_MS = 3000;

// #137 — responsive breakpoints. Phone-portrait gets a tighter header
// (no wordmark beside the icon). Everything else uses the full lockup.
const PHONE_PORTRAIT_MAX = 480;

// (Removed in #405 — `_callStatusLabel` was used by the old centred
// header's mini status line. CallStrip now owns the status label.)

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return width;
}

// ── Ring tone generator (European dial tone: 425 Hz, 1s on / 4s off) ──────
function startRingTone() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const gainNode = ctx.createGain();
  gainNode.connect(ctx.destination);
  gainNode.gain.value = 0.15;

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 425;
  osc.start();

  let connected = true;
  osc.connect(gainNode);

  // Ring pattern: 1s on, 4s off
  const interval = setInterval(() => {
    if (connected) {
      osc.disconnect(gainNode);
      connected = false;
    } else {
      osc.connect(gainNode);
      connected = true;
    }
  }, connected ? 1000 : 4000);

  // More precise: use separate timeouts for on/off
  clearInterval(interval);

  let timer = null;
  let running = true;

  function ringCycle() {
    if (!running) return;
    // ON phase
    osc.connect(gainNode);
    timer = setTimeout(() => {
      if (!running) return;
      osc.disconnect(gainNode);
      // OFF phase
      timer = setTimeout(() => {
        if (!running) return;
        ringCycle();
      }, 4000);
    }, 1000);
  }
  ringCycle();

  return {
    stop: () => {
      running = false;
      if (timer) clearTimeout(timer);
      try { osc.disconnect(); } catch {}
      try { osc.stop(); } catch {}
      try { ctx.close(); } catch {}
    }
  };
}

export default function TabletApp({ userId: initialUserId, companionName: initialCompanionName = 'Amigo Mio' }) {
  // Tablet App surface — seafoam favicon. Distinct from Public's
  // deeper teal so a tab strip showing both is unambiguous.
  useFavicon('app');
  const [userId, setUserId] = useState(initialUserId);
  const [companionName, setCompanionName] = useState(initialCompanionName);
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState(null);
  // #1120 — explicit setup confirm: who the ?setup= token is for, and the
  // token held back until the user taps "Set up this tablet".
  const [setupInfo, setSetupInfo] = useState(null);
  const [pendingSetupToken, setPendingSetupToken] = useState(null);
  const [status, setStatus] = useState('idle');
  // #700 follow-up — dedicated wait-mode flag, driven by the server's
  // `wait_mode_state` event (NOT inferred from `status === 'waiting'`).
  // Pre-fix the pause/play icon swap was bound to `status`, which flips
  // to 'transcribing' at the end of every audio-capture turn (line ~1865)
  // and back to 'waiting' when the server's quiet-drop branch pushes
  // its next status event. That made the button visibly flicker
  // play→pause→play between non-wake utterances — Bas's 2026-05-14
  // staging repro. With this dedicated state, the button only flips on
  // the explicit entry/exit lifecycle event, not on intermediate
  // status churn.
  const [waitMode, setWaitMode] = useState(false);
  const [transcript, setTranscript] = useState('');
  // #405 — separate "frozen" copy of the user's most recent COMPLETED
  // utterance, persisted across turns until the call hangs up. Drives
  // the YouSaidCard in the new split layout: while listening the card
  // shows the live partial (`transcript`); when the user finishes, the
  // value freezes here so they retain a visual anchor of "what I just
  // said" while Berta is processing/replying.
  const [frozenUtterance, setFrozenUtterance] = useState('');
  // #381 — set by the server's `connection_hiccup` WS message when the
  // empty-transcript handler classified the failure as mechanical.
  // Drives the small amber indicator in the CallStrip (Q2 → B). Auto-
  // clears the moment a successful transcript arrives (handled in the
  // 'transcript' case below).
  const [connectionHiccup, setConnectionHiccup] = useState(null);
  // #468 — `reply` and `setReply` come from useProgressiveReply now.
  // `setReply(text)` overwrites and FLUSHES any pending chunks (existing
  // toast call sites — "Call ended", "Don't worry…" — keep their old
  // semantics). `enqueueChunk(text)` is the new path used by the
  // `reply_chunk` WS handler so sentences reveal one at a time at speech
  // pace instead of all at once.
  const { reply, setReply, enqueueChunk, flushReply } = useProgressiveReply();
  // #1032 — which side is the current focal speaker, driving the single
  // current-speaker panel in tablet-portrait: 'user' shows the live
  // transcript ("You"); 'companion' shows Berta's reply. Set to 'user' on
  // transcript events and back to 'companion' when she starts speaking.
  const [focalSpeaker, setFocalSpeaker] = useState('companion');
  // #1032 — true once Berta has produced any reply text this call. Before
  // that (the pre-greeting window) an empty focal panel shows the idle
  // placeholder; after it, an empty panel — e.g. the brief gap when she
  // starts a NEW reply (old reply just cleared, first sentence not yet
  // streamed) — shows a blank box instead (Bas 2026-06-08: no stale text).
  const [callHasReply, setCallHasReply] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [companionStyle, setCompanionStyle] = useState(() =>
    localStorage.getItem('companion_style') ?? null
  );
  const [showSettings, setShowSettings] = useState(false);
  // #606 — experimental PCM toggle in Settings. Reads/writes
  // localStorage.disable_pcm_capture (the inverse of "PCM is on") so
  // it stays compatible with the same env-var-shaped flag the audio
  // capture path consults. Default is "PCM on" (i.e. flag absent).
  // Temporary UI — remove once we either lock in PCM-default-on or
  // PCM-opt-in; this exists for Bas to A/B feel between modes without
  // DevTools commands.
  const [pcmDisabled, setPcmDisabled] = useState(
    () => localStorage.getItem('disable_pcm_capture') === 'true'
  );
  // #606 — what audio mode the LAST mic capture actually used.
  // null = no call yet this session, 'pcm' = AudioWorklet PCM,
  // 'webm' = MediaRecorder fallback (or PCM was disabled).
  // Updated inside startListening() after createPcmCapture returns.
  // Surfaced next to the version footer so Bas can verify which path
  // is live without cracking the DevTools console open.
  const [activeAudioMode, setActiveAudioMode] = useState(null);
  function togglePcm() {
    const next = !pcmDisabled;
    if (next) {
      localStorage.setItem('disable_pcm_capture', 'true');
    } else {
      localStorage.removeItem('disable_pcm_capture');
    }
    setPcmDisabled(next);
  }
  // #137 — responsive width tracker; drives the phone-portrait variant
  // (drops the wordmark next to the icon).
  const windowWidth = useWindowWidth();
  // #405 — viewport-keyed split layout for the connected call view.
  // MUST be called unconditionally at the top of the component, before
  // ANY early returns, or React's rules-of-hooks fires error #310
  // ("Rendered more hooks than during the previous render"). Was
  // previously inside the connected branch — broke the build until the
  // pre-connected branches (idle / intake / calling) skipped over the
  // hook call.
  const viewportMode = useViewportMode();
  // Measured call-root dimensions. Drives the focal+strip split decision
  // (`isHorizontalSplit` below) from the actual rendered scaffold rather
  // than the `tablet-landscape` viewport bucket — so a phone in landscape
  // (which sits in `phone-landscape` but is plenty wide enough for the
  // side-by-side layout) gets the same chrome as the production tablet
  // does in landscape. Same FRAME_SPLIT_MIN_WIDTH threshold as
  // ActivityFrame, so the call view and the activity overlays switch
  // together on rotation.
  const callRootRef = useRef(null);
  const [callRootBox, setCallRootBox] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));
  useEffect(() => {
    const el = callRootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0] && entries[0].contentRect;
      if (r) setCallRootBox({ width: Math.round(r.width), height: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // #441 Phase 1 — pull setLang from the i18n context so we can switch
  // the tablet UI to the elderly user's language as soon as the server
  // tells us via user_config.
  const langCtx = useLang();
  // #441 Phase 2 — translated strings for the screens (settings,
  // calling, idle, hangup, etc.). Resolved per-render so language
  // switches via setLang refresh the labels live.
  const scr = _useScreenStrings();
  // #713 — scrRef is a "latest-snapshot" mirror of scr that the
  // long-lived WS handlers (ws.onmessage / ws.onclose / ws.onerror —
  // bound ONCE per WS lifecycle inside ensureWS) read from. Without
  // this, those handlers capture whatever `scr` was at WS-open time
  // in their closure; if the elderly user's language is then applied
  // via setLang AFTER the WS opens (the normal flow — profile fetch
  // races the WS open), the JSX re-renders in the new language but
  // the WS handlers keep firing setReply/setMicWarning with the OLD
  // scr — Bas's 2026-05-14 repro: Dutch mic-warning shown to an
  // English persona because the browser's navigator.language defaulted
  // the LanguageProvider to Dutch before setLang('en') landed. Mirror
  // pattern matches statusRef / callStateRef a few lines below.
  const scrRef = useRef(scr);
  // #137 — reply panel auto-scroll. Sticks to bottom unless the user
  // manually scrolls UP to re-read; resumes when they scroll back to
  // the bottom (standard chat-app pattern).
  const replyPanelRef = useRef(null);
  const replyStickToBottomRef = useRef(true);
  const [photoCarousel, setPhotoCarousel] = useState(null); // { photos: [...] } or null
  const [videoOverlay, setVideoOverlay] = useState(null);   // { youtubeId, title } or null  (#91)
  // #390 — domino game state. Server pushes the full GameState on every
  // change via {type: domino_state}. Null = no game in progress (default).
  // When non-null, the DominoGame overlay renders full-screen and the
  // user interacts with the board; the rest of the tablet UI is hidden
  // behind the overlay (z-index: 100). When the game ends or the user
  // taps "Done", we send {type: domino_quit} and clear the state.
  const [dominoState, setDominoState] = useState(null);
  // #966 — Solitaire (Klondike) overlay state. Same shape contract as
  // dominoState: null = no game in progress; when non-null the
  // SolitaireGame overlay renders full-screen. Deferred-open via
  // pendingSolitaireRef so the board waits for Berta's spoken acceptance
  // to finish (same pattern as domino/video).
  const [solitaireState, setSolitaireState] = useState(null);
  const pendingSolitaireRef = useRef(null);
  // #1029 — game picker carousel. Same shape contract as the game overlays:
  // null = closed; non-null = { games:[ordered ids], personalised:bool }.
  // Opened via {type: games_menu_start}; deferred-open through
  // pendingGamesMenuRef so it waits for Berta's spoken line to finish.
  const [gamesMenuState, setGamesMenuState] = useState(null);
  const pendingGamesMenuRef = useRef(null);
  // Pending video — queued when show_video arrives but Berta is still
  // speaking. Applied to videoOverlay once AudioPlayer drains, so the
  // user hears Berta's last words BEFORE the clip starts. Without this,
  // Berta's "good choice — here it comes" gets cut off mid-sentence by
  // the video starting (Bas's bug #1, 2026-05-03).
  const pendingVideoRef = useRef(null);
  // #390 PR C-3 — same deferral pattern as pendingVideoRef but for the
  // domino game overlay. Bas's PR #393 testing: "she is also still
  // talking and it already is shown" — the LLM tag fires immediately
  // and the overlay opens while Berta is mid-sentence saying
  // "let me deal the tiles". Holding the state in pendingDominoRef
  // until AudioPlayer drains lets her finish her spoken acceptance
  // before the board appears.
  const pendingDominoRef = useRef(null);
  // #477 — same deferral pattern as pendingVideoRef / pendingDominoRef
  // but for play_music. Bas 2026-05-08: Berta asked "what would you
  // like to hear?" and the music started playing IMMEDIATELY — before
  // she finished the question. The [PLAY_MUSIC:query] tag in voice.py
  // dispatches its WS event INLINE during the same sentence loop, so
  // the tablet was firing musicPlayerRef.current.play() before the
  // TTS audio for that sentence had even started. Holding the
  // {youtube_id, title} payload in pendingMusicRef and flushing it
  // from AudioPlayer.onDone (alongside pendingVideoRef + pendingDominoRef)
  // makes music wait for Berta to finish her last word — same proven
  // pattern that fixes #176 (video) and #390 PR C-3 (domino).
  const pendingMusicRef = useRef(null);
  // Music player state (#90 phase 1) — driven imperatively via musicPlayerRef.
  // `nowPlaying` is the React-visible mirror so the now-playing pill renders.
  const musicPlayerRef = useRef(null);
  const [nowPlaying, setNowPlaying] = useState(null); // { youtubeId, title } or null

  // ── Mic test state ──────────────────────────────────────────────────────
  const [micTestState, setMicTestState] = useState('idle'); // idle | recording | checking | result
  const [micTestResult, setMicTestResult] = useState(null); // {text, success}
  const micTestRecorderRef = useRef(null);
  const micTestStreamRef = useRef(null);
  const micTestChunksRef = useRef([]);

  // ── Audio self-test (record-and-playback) state ─────────────────────────
  // Records through the SAME PCM capture path a live call uses
  // (createPcmAudioContext + createPcmCapture → pcm-worklet), accumulates
  // the identical Int16 frames that sendChain would stream to the server,
  // then plays them back locally so Bas can hear where (if anywhere) the
  // capture chain loses quality. Deliberately self-contained — it touches
  // none of the live-call audio plumbing.
  const [selfTestState, setSelfTestState] = useState('idle'); // idle | recording | recorded | playing | error
  const [selfTestMs, setSelfTestMs] = useState(0);            // recorded length, for the hint line
  const selfTestStreamRef = useRef(null);   // getUserMedia MediaStream
  const selfTestCtxRef = useRef(null);      // capture AudioContext (we own + close it)
  const selfTestCaptureRef = useRef(null);  // createPcmCapture handle
  const selfTestChunksRef = useRef([]);     // Array<Blob> of raw Int16 PCM frames
  const selfTestRateRef = useRef(48000);    // native sample rate of the capture context
  const selfTestPlayCtxRef = useRef(null);  // throwaway playback AudioContext
  const selfTestSrcRef = useRef(null);      // active BufferSource during playback
  const selfTestTimerRef = useRef(null);    // auto-stop safety timer
  const selfTestActiveRef = useRef(false);  // true between record-start and stop/reset; guards the async start path against a mid-await overlay close
  // Raw vs. processed capture A/B (#980 follow-up). When raw, getUserMedia
  // drops EC/NS/AGC so the tester can hear whether the browser voice DSP is
  // what dulls a wired headset. The ref records which mode the in-hand clip
  // was captured in, so the hint stays truthful even after toggling.
  const [selfTestRaw, setSelfTestRaw] = useState(false);
  const selfTestRecordedRawRef = useRef(false);
  const [selfTestRecordedRaw, setSelfTestRecordedRaw] = useState(false);

  // ── Incoming (proactive) call state ─────────────────────────────────────
  const [incomingCall, setIncomingCall] = useState(null); // { id, trigger_type } or null

  // ── Phone call state ────────────────────────────────────────────────────
  const [callState, setCallState] = useState('idle'); // idle | calling | connected
  // #1058 — true for POST_HANGUP_COOLDOWN_MS right after a call ends.
  // Dims + disables the idle "Call" button so an accidental second tap
  // immediately after hang-up can't start a phantom 0-turn call.
  const [callJustEnded, setCallJustEnded] = useState(false);
  const [hangUpPending, setHangUpPending] = useState(false);
  const [micWarning, setMicWarning] = useState(null); // null | string message
  const wakeLockRef = useRef(null);
  const configRef = useRef({ vad_silence_ms: 1800, vad_strategy: 'client_vad' });

  // Derived: use visual defaults while waiting for server
  const effectiveStyle = companionStyle || 'smiley';

  // #1120 — Handle ?setup=TOKEN on mount. Opening the link no longer
  // auto-activates (that silently paired whichever device opened it and
  // burned the single-use token — so previewing the link on a phone left
  // the real tablet with "already used"). Instead we look up who the token
  // is for (read-only, no consumption) and show a "Set up this tablet for
  // {name}?" confirm; only the explicit tap (confirmSetup) activates.
  // Activation is idempotent server-side now, so re-pairing works.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setupToken = params.get('setup');
    if (!setupToken) return;

    fetch(`/api/tablet/setup-info?token=${encodeURIComponent(setupToken)}`)
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Invalid setup link'); });
        return r.json();
      })
      .then(info => {
        setSetupInfo(info);
        setPendingSetupToken(setupToken);
      })
      .catch(err => {
        setActivationError(err.message);
      });
  }, []);

  // #1120 — explicit "Set up this tablet" tap. Activates the (idempotent)
  // token, links this device, and drops the ?setup= param from the URL.
  const confirmSetup = useCallback(() => {
    if (!pendingSetupToken) return;
    setActivating(true);
    fetch('/api/tablet/activate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: pendingSetupToken }),
    })
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Activation failed'); });
        return r.json();
      })
      .then(data => {
        localStorage.setItem('tablet_user_id', data.user_id);
        setUserId(data.user_id);
        setCompanionName(data.companion_name || 'Amigo Mio');
        setPendingSetupToken(null);
        setSetupInfo(null);
        window.history.replaceState({}, '', '/app');
        setActivating(false);
      })
      .catch(err => {
        setActivationError(err.message);
        setActivating(false);
      });
  }, [pendingSetupToken]);

  // #1120 — "Not now": dismiss the confirm without pairing this device and
  // strip the ?setup= param so a refresh doesn't re-prompt.
  const cancelSetup = useCallback(() => {
    setPendingSetupToken(null);
    setSetupInfo(null);
    window.history.replaceState({}, '', '/app');
  }, []);

  // ── Fetch system config (VAD settings etc.) ─────────────────────────────
  useEffect(() => {
    fetch('/api/config/public')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) configRef.current = data;
      })
      .catch(() => {});
  }, []);

  // ── Fetch companion name + server defaults on load ───────────────────────
  useEffect(() => {
    if (!userId) return;
    fetch(`/api/elderly/profile?user_id=${userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.companion_name) setCompanionName(data.companion_name);
        // Apply server defaults only if user has never set locally
        if (localStorage.getItem('companion_style') === null) {
          setCompanionStyle(data.companion_style || 'smiley');
        }
        // Switch the tablet UI to the elderly user's configured language
        // so the Idle / Calling / Connected-pre-greeting screens render
        // in the right locale BEFORE the WS connects. Pre-#513 the
        // LanguageProvider initialised from navigator.language (the
        // browser locale), so a Dutch-locale Chromium running for an
        // elderly user with users.language='es' would show "Hallo —
        // ik ben er…" / "Probeer het opnieuw" until the per-call
        // user_config WS message arrived to flip it.
        //
        // Normalise to a 2-letter short tag (`de-DE` → `de`) the same
        // way the server's WS user_config does (voice.py:2339). Without
        // this, a user row with `users.language='de-DE'` would call
        // setLang('de-DE') → translations['de-DE'] is undefined →
        // getTranslations falls all the way back to English. That
        // produced the "sometimes English on idle, German once the
        // call starts" intermittency Bas reported 2026-05-09: the
        // profile fetch took the raw value, the WS message normalised
        // it, so the UI flipped from English-fallback to real German
        // when the call connected.
        if (data.language && langCtx && typeof langCtx.setLang === 'function') {
          try {
            const lang = String(data.language).split('-')[0].toLowerCase();
            langCtx.setLang(lang);
          } catch (e) {
            console.warn('[i18n] setLang from profile failed (non-fatal):', e);
          }
        }
      })
      .catch(() => {});
    // langCtx is intentionally excluded — its identity changes on every
    // setLang call (LanguageProvider re-creates the value object), which
    // would cause this effect to re-fetch the profile on every language
    // switch. We only need it once per userId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ── Poll for pending (proactive) calls ──────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    if (callState !== 'idle') return;

    let cancelled = false;
    function checkPending() {
      fetch('/api/calls/pending', { headers: { 'X-User-Id': userId } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (cancelled) return;
          if (data && data.call) {
            setIncomingCall(data.call);
          }
        })
        .catch(() => {});
    }
    checkPending();
    const iv = setInterval(checkPending, 60000); // re-check every 60s
    return () => { cancelled = true; clearInterval(iv); };
  }, [userId, callState]);

  // Refs for mutable state accessible inside callbacks
  const userIdRef = useRef(userId);
  const statusRef = useRef('idle');
  // #700 follow-up — mirror of `waitMode` so the handlePauseToggle
  // useCallback can read the latest value without re-binding.
  const waitModeRef = useRef(false);
  const callStateRef = useRef('idle');
  const wsRef = useRef(null);
  const sessionActiveRef = useRef(false);
  // #1058 — post-hangup re-dial cooldown bookkeeping. `lastCallEndedAtRef`
  // is the epoch ms of the most recent call end; the user-initiated call
  // path (startUserCall) refuses to start a new call within
  // POST_HANGUP_COOLDOWN_MS of it. `prevCallStateRef` lets the effect
  // below detect the non-idle → idle transition; the timer clears the
  // `callJustEnded` button-dim state when the window passes.
  const lastCallEndedAtRef = useRef(0);
  const prevCallStateRef = useRef('idle');
  const callJustEndedTimerRef = useRef(null);

  // #1058 — stamp the call-end time on EVERY non-idle → idle transition.
  // Explicit hang-up (doHangUp), the ws.onclose drop-to-idle paths, the
  // server idle_timeout handler, and the error/quota paths all funnel
  // through setCallState('idle'); centralising the stamp here (rather
  // than at each end-of-call site) guarantees no path is missed — this
  // is the single source of truth for "a call just ended" (rule 9).
  // Arms the button-dim cooldown too, cleared when the window passes.
  useEffect(() => {
    const prev = prevCallStateRef.current;
    prevCallStateRef.current = callState;
    if (callState === 'idle' && prev !== 'idle') {
      lastCallEndedAtRef.current = Date.now();
      setCallJustEnded(true);
      if (callJustEndedTimerRef.current) clearTimeout(callJustEndedTimerRef.current);
      callJustEndedTimerRef.current = setTimeout(() => {
        callJustEndedTimerRef.current = null;
        setCallJustEnded(false);
      }, POST_HANGUP_COOLDOWN_MS);
    }
  }, [callState]);

  // #642 — silent tablet auto-reload. useAutoReload reads `.current` to
  // decide whether a reload is safe (never during an active call). We
  // already track call lifecycle in `callStateRef` (values: idle |
  // calling | connected). Adapt that into a boolean-flavoured ref the
  // hook can consume without coupling the hook to our state-machine
  // values — the adapter dynamically reflects callStateRef every read.
  const isCallActiveRef = useMemo(() => ({
    get current() { return callStateRef.current !== 'idle'; },
  }), []);
  const { onServerHello: autoReloadOnServerHello, onCallEnd: autoReloadOnCallEnd } = useAutoReload({
    loadedBuild: LOADED_BUILD,
    isCallActiveRef,
  });
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const webmHeaderRef = useRef(null);
  // #606 — wire format of the most recent capture session ('webm' or 'pcm').
  // Used by the killMic-replay path so the re-sent audio_start carries the
  // same format hint the original send used.
  const captureFormatRef = useRef('webm');
  // #606 (2026-05-11 lifecycle rework) — ONE AudioContext per call,
  // created inside the user-gesture handler (handleCall) so the
  // browser starts it 'running' and doesn't drift to 'suspended' on
  // turn 2+. Reused across every turn's createPcmCapture, closed on
  // hangup (inside cleanup()). Null when MediaRecorder fallback is
  // in use (browser doesn't support AudioWorklet OR the context
  // failed to initialise OR pcmDegraded was flipped mid-call).
  const pcmCtxRef = useRef(null);
  // #606 — flipped true when the watchdog detects the AudioContext
  // can't be kept running (e.g. browser suspended it and resume()
  // failed). Subsequent turns skip the PCM path and use MediaRecorder
  // for the rest of the call. Reset to false when a new call starts.
  const pcmDegradedRef = useRef(false);
  // #606 — interval handle for the watchdog that monitors
  // pcmCtxRef.current.state. Set in handleCall, cleared in cleanup.
  const pcmWatchdogRef = useRef(null);
  // #606 — count of consecutive watchdog resume failures. After
  // PCM_WATCHDOG_MAX_FAILS attempts to recover a suspended context,
  // we give up and switch to MediaRecorder for the rest of the call.
  const pcmResumeFailCountRef = useRef(0);
  // #897 — per-call audio warm-up timings collector. Created at
  // handleCall start, marked across the bring-up path (getUserMedia /
  // ws_open / pcm_audio_context_ready / first_audio_chunk /
  // first_stt_partial), dumped on cleanup. The dump emits one
  // structured `[warmup]` console line which the #718 ring buffer
  // captures into client_console_logs.entries for offline analysis.
  const warmupTimingsRef = useRef(null);
  // #897 — idempotent dump helper. Called from every cleanup path
  // (doHangUp / WS onclose / quota cut-off) BEFORE _flushConsoleCapture
  // so the dumped line lands in the ring-buffered batch that gets
  // shipped to client_console_logs. After dumping the ref is cleared
  // so a second call (e.g. doHangUp followed by onclose) doesn't emit
  // a duplicate line.
  const dumpWarmupTimings = useCallback(() => {
    const t = warmupTimingsRef.current;
    if (!t) return;
    warmupTimingsRef.current = null;
    try {
      t.dump({ call_type: callTypeRef.current });
    } catch (_) { /* swallow — diagnostic only */ }
  }, []);
  const audioPlayerRef = useRef(null);
  const vadStreamRef = useRef(null);
  const vadAudioCtxRef = useRef(null);
  const vadAnimFrameRef = useRef(null);
  const vadStoppedRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const speechStartRef = useRef(null);
  const pendingBlobRef = useRef(null);
  const lenaSpeakingRef = useRef(false);
  const ringToneRef = useRef(null);
  const wsReadyRef = useRef(false);
  const firstAudioReceivedRef = useRef(false);
  const callTypeRef = useRef('normal');
  // #200 PR-A2 — set when handling an incoming companion call so the
  // server can tie the conversation back to the originating
  // pending_call (used by finalise_call_lifecycle to mark the reminder
  // delivered on engaged outcomes, or reset pending_call to 'missed'
  // on delivered_unconfirmed so the retry scheduler fires). Null for
  // user-initiated calls.
  const pendingCallIdRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const hangUpPendingRef = useRef(false);
  const hangUpTimerRef = useRef(null);
  const audioServerDoneRef = useRef(false);
  // #622 — current audio format for incoming binary chunks. Defaults to
  // 'mp3' (the historical Aura REST + Azure + ElevenLabs path that
  // AudioPlayer.addChunk() decodes via decodeAudioData). Server flips
  // this to 'pcm-linear16' via an `audio_format` JSON frame RIGHT
  // BEFORE the first PCM chunk when Aura WS streaming is active.
  // Reset to 'mp3' on every `status: speaking` so each turn starts
  // fresh — covers the case where one turn was Aura-streaming and the
  // next falls back to per-sentence REST (or vice versa).
  const audioFormatRef = useRef('mp3');
  // #622 — sample rate the server declared in audio_format. Defaults
  // to 16000 (Aura's native rate for most voice models). Hardcoding
  // 24000 caused 1.5x chipmunked audio on staging 2026-05-10 because
  // Aura ignored the URL sample_rate hint and sent 16kHz regardless.
  const audioSampleRateRef = useRef(16000);
  // #297 — set when the server delivers a {type: "quota_exceeded"}
  // event. Tells the WS onclose handler to wait for AudioPlayer drain
  // before transitioning to idle, so the UI doesn't flip to "ended"
  // while the quota TTS is still playing on the user's end.
  const quotaExceededRef = useRef(false);
  const audioChunksReceivedRef = useRef(0);
  const emptyTurnCountRef = useRef(0);
  const gotReplyRef = useRef(false);
  const hadRealAudioRef = useRef(false);
  const reassuranceSentRef = useRef(false);
  const vadDetectedSpeechRef = useRef(false);
  // #382 — per-turn SNR measurement. The VAD loop populates these
  // refs; stopRecordingAndSend reads them to compute turn_snr =
  // speechRmsP50 / noiseFloor and ship it in the audio_end payload.
  // Server side aggregates into the user's EWMA via
  // services.speaker_profile.update_speaker_snr.
  const vadNoiseFloorRef = useRef(null);   // p90 RMS during the calibration window
  const vadSpeechSamplesRef = useRef([]);  // RMS samples taken while VAD was in 'speaking' state
  // #381 — last calibrated VAD threshold. Lifted from a function-scope
  // local in startVADLoop so stopRecordingAndSend can ship it in the
  // audio_end payload (helps server logs explain "why was this empty?"
  // — was the threshold too high?). Null until the calibration window
  // closes for the first time.
  const vadCalibratedThresholdRef = useRef(null);
  // #381 — flips true if MediaRecorder.onerror fires during the turn.
  // Drives the `mic_error` reason in the audio_end payload so the
  // server can take the re-prompt path instead of the silence ladder.
  const micErrorRef = useRef(false);
  // Serialised mic-chunk send pipeline. Created lazily on first recording
  // (see startListening). Extracted into web/src/utils/sendChain.js so the
  // ordering invariant is unit-tested in Jest (#131) — without that, the
  // race between async chunk sends and the synchronous audio_end was
  // re-introduced twice during the recent perf series.
  const sendChainRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { callStateRef.current = callState; }, [callState]);
  // #713 — keep scrRef in sync so WS handlers always read the latest
  // translation snapshot. `scr` is a new object reference each render
  // (built fresh inside _useScreenStrings) so this effect runs every
  // render; cost is negligible (one assignment) and the alternative
  // would be threading langCtx.lang through, doubling the indirection.
  useEffect(() => { scrRef.current = scr; }, [scr]);

  // ── Bedside-aware idle dim (#138) ────────────────────────────────────────
  // Day mode (07:00-22:00): 60s timeout, 72% black, single tap wakes.
  // Night mode (22:00-07:00): 25s timeout, 97% black, soft clock visible,
  // requires long-press or double-tap to wake (so accidentally brushing
  // the tablet doesn't blast a sleeping user with light).
  // Post-call re-dim is faster at night (10s vs 60s).
  const { dimmed, config: dimConfig, wake: wakeIdleDim } = useIdleDim({ status });

  // Pause music while Berta is speaking; resume when she stops (#90 phase 1).
  // Background music + TTS playing simultaneously sounds like a mess on the
  // tablet's single output. Pause-during-speak is simpler than ducking and
  // works fine for a 1-2 sentence reply.
  useEffect(() => {
    if (!musicPlayerRef.current) return;
    if (status === 'speaking') {
      musicPlayerRef.current.pause();
    } else {
      // 'listening' / 'thinking' / 'transcribing' / 'idle' all → music can resume.
      // The player itself no-ops resume() if there's nothing to resume.
      musicPlayerRef.current.resume();
    }
  }, [status]);

  // #1075 — synchronous "is a track playing right now?" check. Reads the
  // YouTube player's LIVE state (set synchronously inside MusicPlayer.play(),
  // MusicPlayer.js), which is true the instant play() returns — unlike the
  // `nowPlaying` React state, which only flips after YouTube's async
  // onStateChange re-renders. The mic guards below depend on this so they
  // never reopen the mic in the SAME synchronous tick a track was started
  // from AudioPlayer.onDone; otherwise the music bleeds into the open mic and
  // the server speaks a spurious "didn't catch that" reprompt (Bas's
  // 2026-06-15 staging repro, conversation 9ea09b07).
  const musicIsLive = () =>
    !!(musicPlayerRef.current
       && musicPlayerRef.current.getState
       && musicPlayerRef.current.getState().playing);

  // When media (video / music) is dismissed, re-trigger startListening so the
  // conversation resumes. Without this, after closing a video the user would
  // have to speak first to wake the mic — disorienting on a hands-free
  // tablet. The startListening function itself has all the guards (status /
  // callState / lena-speaking) so calling it here is safe.
  useEffect(() => {
    const mediaActive = !!(videoOverlay || nowPlaying);
    if (mediaActive) return;
    if (callStateRef.current !== 'connected') return;
    const s = statusRef.current;
    if (s === 'transcribing' || s === 'thinking' || s === 'speaking') return;
    if (startListeningRef.current) {
      console.log('[MIC DEBUG] media dismissed — resuming mic');
      startListeningRef.current();
    }
  }, [videoOverlay, nowPlaying]);

  // #825 — defensive mic-restart fallback for the four full-screen
  // overlays (domino, solitaire, photo carousel, video clip).
  //
  // Primary fix lives on the server: `_after_overlay_close(websocket)`
  // emits `{type: "listening"}` on every overlay-dismiss path. This
  // effect adds belt-and-braces coverage so a future fifth overlay that
  // forgets the server-side hook doesn't silently re-introduce the bug
  // where the mic stayed soft-muted post-overlay (Bas's 2026-05-19
  // domino_quit repro, conversation 34403b1f).
  //
  // When ALL four overlays are null and we're in an active call, wait
  // 1 s and then re-evaluate via `shouldFireOverlayCloseMicFallback`.
  // The guards check status / mic-recording state so we don't double-
  // fire when the server's `listening` arrived in the interim (or when
  // the normal AudioPlayer.onDone path has already restarted the mic).
  // A `console.warn` surfaces the fallback firing so a future regression
  // is visible without needing to repro the silent dead-air symptom.
  useEffect(() => {
    if (dominoState || solitaireState || photoCarousel || videoOverlay) {
      return;
    }
    if (callStateRef.current !== 'connected') return;

    const timer = setTimeout(() => {
      const micRecording = !!(
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      );
      const shouldFire = shouldFireOverlayCloseMicFallback({
        dominoState,
        photoCarousel,
        videoOverlay,
        callState: callStateRef.current,
        status: statusRef.current,
        micRecording,
        hasStartListening: !!startListeningRef.current,
      });
      if (!shouldFire) return;
      console.warn(
        '[MIC #825] overlay-close fallback fired — server did not ' +
        'restart the mic within 1 s. Check _after_overlay_close ' +
        'coverage on the server for the most-recently-closed overlay.'
      );
      startListeningRef.current();
    }, 1000);

    return () => clearTimeout(timer);
  }, [dominoState, solitaireState, photoCarousel, videoOverlay]);

  // #480 — tell the server when the video overlay opens/closes so it can
  // extend the idle-timeout (30 min cap instead of the normal 120s mid-
  // conversation). Without this, any clip > 2 min trips the timeout and
  // the WS closes mid-playback — the user finds a dead call when they
  // press Done. Symmetrical to MusicPlayer.onStateChange's
  // music_started/music_stopped events further down in the JSX.
  // The payload shape lives in `utils/videoLifecycleSignal` so it can be
  // unit-tested without mounting all of TabletApp.
  const videoOverlayActive = !!videoOverlay;
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      const title = (videoOverlay && videoOverlay.title) || null;
      ws.send(JSON.stringify(videoLifecycleSignal(videoOverlayActive, title)));
    } catch (_) {}
    // We deliberately key on the boolean `videoOverlayActive` rather than
    // the videoOverlay object itself: the title is informational only, and
    // using the object would re-fire video_started on any re-render that
    // creates a new object reference (the same class of bug as #475).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoOverlayActive]);

  // #137 — Auto-scroll the reply panel as new sentences arrive (TTS
  // chunking grows the `reply` string). Pauses if the user manually
  // scrolled up to re-read (replyStickToBottomRef). Standard chat-app
  // pattern.
  useEffect(() => {
    const el = replyPanelRef.current;
    if (!el) return;
    if (!replyStickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
    // #1032 — also re-pin when the live transcript grows (the same panel
    // now hosts the user's words on their turn) so the newest words stay
    // in view.
  }, [reply, transcript]);

  // #1032 — when Berta stops speaking, reveal any still-queued reply
  // sentences at once. The pacing runs at / just behind her voice (never
  // ahead — replyQueue.js), so on a long reply the final sentence or two
  // can still be queued when she finishes; flushing here lands the full
  // reply for the re-read pause instead of trailing it in after silence.
  useEffect(() => {
    if (status !== 'speaking') flushReply();
    // flushReply identity changes per render; we only want this on status
    // transitions, and flush is idempotent when nothing is queued.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // #1032 follow-up (Bas 2026-06-08) — disable the browser's pull-to-refresh
  // gesture during a connected call. The user scrolls the reply panel with a
  // finger to follow along; an over-scroll past the top edge was triggering
  // a page reload, which tears down the WebSocket and drops the call.
  // `overscroll-behavior: none` on the document root suppresses pull-to-
  // refresh (and scroll-chaining) for the duration of the call; it's restored
  // on call end / unmount so other routes (e.g. the Portal) are unaffected.
  useEffect(() => {
    if (callState !== 'connected') return undefined;
    const root = document.documentElement;
    const prev = root.style.overscrollBehavior;
    root.style.overscrollBehavior = 'none';
    return () => { root.style.overscrollBehavior = prev; };
  }, [callState]);

  // Keepalive ping every 20s
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  // Cleanup all resources on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => cleanup(), []);

  // ── Wake Lock: prevent screen from locking ──────────────────────────────
  async function requestWakeLock() {
    if (!('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      wakeLockRef.current.addEventListener('release', () => {
        requestWakeLock();
      });
    } catch (err) {
      console.log('Wake lock not available:', err.message);
    }
  }

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Pre-warm AudioContext on first user gesture + acquire wake lock
  useEffect(() => {
    function warmAudio() {
      if (audioPlayerRef.current) audioPlayerRef.current.warmUp();
      requestWakeLock();
      document.removeEventListener('click', warmAudio);
      document.removeEventListener('touchstart', warmAudio);
    }
    document.addEventListener('click', warmAudio);
    document.addEventListener('touchstart', warmAudio);
    return () => {
      document.removeEventListener('click', warmAudio);
      document.removeEventListener('touchstart', warmAudio);
    };
  }, []);

  const startListeningRef = useRef(null);
  const pendingMicStartRef = useRef(false);
  // #716 — re-entrancy guard for startListening. See startListening
  // header for the full rationale (concurrent + sequential rapid-fire
  // both have to be blocked). The flag is supplementary to the
  // mediaRecorder.state check — it covers the brief setup window
  // before mediaRecorder.start() returns.
  const startListeningInFlightRef = useRef(false);
  // #716 follow-up — VAD_MAX_RECORD_MS safety timer handle. Pre-fix
  // the 60s setTimeout was scheduled but its handle was discarded, so
  // it couldn't be cleared. A timer set during one recording session
  // (e.g. a wait-mode turn) could fire 60s later during a completely
  // different recording session, calling stopRecordingAndSend on the
  // wrong session. Surfaced live on staging 2026-05-15 as the
  // `VAD_MAX_RECORD_MS hit (60s hard cap)` line firing 84 ms after a
  // fresh VAD calibration — the leaked timer was from a wait-mode
  // turn 60 s earlier. Now we track the handle and clear it in every
  // teardown path (stopRecordingAndSend, killMic, softMuteMic, and
  // at the top of every new startListening).
  const vadMaxTimerRef = useRef(null);

  // ── Stop VAD analysis loop ──────────────────────────────────────────────
  function stopVADLoop() {
    vadStoppedRef.current = true;
    if (vadAnimFrameRef.current) cancelAnimationFrame(vadAnimFrameRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    vadAnimFrameRef.current = null;
    silenceTimerRef.current = null;
  }

  // ── Soft-mute mic (#402 — BT headset profile preservation) ─────────────
  //
  // When Berta is about to speak, we need to silence the user's mic so
  // her TTS doesn't loop back through the mic into Deepgram. The
  // original implementation called killMic() which stops the stream
  // tracks entirely — and on Bluetooth headsets, that releases the
  // HFP/SCO profile, causing the OS to switch back to A2DP and produce
  // an audible "click" + volume/quality jump every turn.
  //
  // softMuteMic instead:
  //   - Stops the MediaRecorder (we don't need to capture during TTS)
  //   - Sets all tracks to `enabled = false` (mic is muted but the
  //     stream is alive)
  //   - Keeps vadStreamRef alive so the next startListening can reuse it
  //   - Closes VAD AudioContext (will recreate fresh on resume)
  //   - Sends audio_end to the server (same as killMic)
  //
  // Because the MediaStream stays alive, the OS keeps the BT headset
  // in HFP for the entire call — no profile switch, no click.
  //
  // Trade-off: BT audio is HFP-quality (lower bitrate) for the whole
  // call vs only during user turns. Acceptable per #402 design lock —
  // consistent quality > oscillating quality with clicks.
  //
  // Feature flag: localStorage 'mic_keep_alive' (default true). If
  // echo regression appears, set to 'false' in DevTools to revert
  // to the old killMic-everything behavior.
  function shouldKeepMicAlive() {
    if (typeof window === 'undefined') return true;
    try {
      const v = window.localStorage.getItem('mic_keep_alive');
      return v === null || v === 'true';
    } catch {
      return true;
    }
  }

  function softMuteMic(callerLabel = 'softMuteMic') {
    if (!shouldKeepMicAlive()) {
      // Feature flag off — fall back to the old behavior
      console.log('[MIC DEBUG #402] mic_keep_alive=false → falling back to killMic');
      killMic(`softMuteMic_fallback_${callerLabel}`);
      return;
    }
    stopVADLoop();
    pendingMicStartRef.current = false;
    // #716 follow-up — clear the VAD_MAX safety timer. softMute happens
    // when Berta starts speaking mid-listen (between-turn mute); the
    // 60s timer for the current recording session is no longer needed
    // and must not leak into the NEXT session.
    if (vadMaxTimerRef.current) {
      clearTimeout(vadMaxTimerRef.current);
      vadMaxTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    mediaRecorderRef.current = null;
    if (vadStreamRef.current) {
      // Disable but DON'T stop the tracks — keeps the BT profile in HFP.
      vadStreamRef.current.getTracks().forEach(t => { t.enabled = false; });
      console.log('[MIC DEBUG #402] softMute: tracks disabled, stream kept alive (BT profile preserved)');
    }
    if (vadAudioCtxRef.current) {
      try { vadAudioCtxRef.current.close(); } catch (e) {}
      vadAudioCtxRef.current = null;
    }
    if (sessionActiveRef.current) {
      // #716 follow-up — DON'T send a `ws_dropped` audio_end here when
      // the WS is healthy. The `wsDropped` flag tells the server
      // "treat this as a mechanical failure → fire a re-prompt
      // (sorry, didn't catch that)" — but softMute fires when BERTA
      // is starting to speak, not when the WS actually dropped. The
      // mediaRecorder's natural onstop handler (set in
      // stopRecordingAndSend) fires async and sends an audio_end
      // with the REAL reason (vad_speech / vad_silence) + the real
      // audio bytes. Pre-fix, the ws_dropped sent here arrived FIRST
      // (with bytes=0), the server classified it as mechanical, fired
      // a re-prompt that arrived during the user's NEXT speech
      // attempt, softMute fired again, repeat — the post-mic_error
      // cascade observed on staging 2026-05-15. We still log a
      // diagnostic line so the path is visible in the #718 console
      // capture.
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        console.log(`[MIC DEBUG #716] softMute on healthy WS — letting onstop send the canonical audio_end (caller=${callerLabel})`);
      } else {
        // Real WS drop. No audio_end can land; just mark the session
        // closed locally so subsequent sends don't try to reuse it.
        console.warn(`[#712 MIC] softMute on closed WS caller=${callerLabel}`);
      }
      sessionActiveRef.current = false;
    }
    audioServerDoneRef.current = false;
    audioChunksReceivedRef.current = 0;
    setIsRecording(false);
  }

  // ── Kill mic ────────────────────────────────────────────────────────────
  // Hard release — stops MediaRecorder AND tracks. Used for cleanup
  // paths only (call ended, hangup, idle timeout, WS dropped). For
  // between-turn pauses (Berta speaking), use softMuteMic instead.
  function killMic(callerLabel = 'killMic') {
    stopVADLoop();
    pendingMicStartRef.current = false;
    // #716 follow-up — clear the VAD_MAX safety timer on every hard
    // teardown so it can't fire on a subsequent recording session.
    if (vadMaxTimerRef.current) {
      clearTimeout(vadMaxTimerRef.current);
      vadMaxTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    if (vadStreamRef.current) {
      vadStreamRef.current.getTracks().forEach(t => t.stop());
      vadStreamRef.current = null;
    }
    if (vadAudioCtxRef.current) {
      try { vadAudioCtxRef.current.close(); } catch(e) {}
      vadAudioCtxRef.current = null;
    }
    // Close the streaming protocol so the server doesn't keep a dangling session
    if (sessionActiveRef.current) {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        // #381 — same as softMute: this is teardown, not a natural turn end.
        // #712 instrumentation — log + echo the caller label so server
        // logs can attribute mid-recording teardowns to specific
        // tablet-side code paths.
        console.warn(`[#712 MIC] ws_dropped sent from killMic caller=${callerLabel}`);
        ws.send(JSON.stringify(buildAudioEndPayload({
          wsDropped: true,
          wsDroppedSource: `killMic:${callerLabel}`,
        })));
      }
      sessionActiveRef.current = false;
    }
    audioServerDoneRef.current = false;
    audioChunksReceivedRef.current = 0;
    setIsRecording(false);
  }

  // ── Comprehensive resource cleanup ──────────────────────────────────────
  function cleanup() {
    // #700 follow-up — clear wait-mode state on every disconnect
    // path, not just the explicit-hangup path. Belt-and-braces for
    // onclose / WS error / reconnect flows that bypass doHangUp.
    // Idempotent — if waitMode is already false this is a no-op.
    waitModeRef.current = false;
    // setWaitMode dispatches a re-render; the cleanup runs in the
    // unmount sequence too, where setting state is safe (React
    // silently no-ops if the component is already unmounted).
    setWaitMode(false);
    // 1. Stop MediaRecorder and mic tracks
    killMic();

    // 2. Stop ring tone
    if (ringToneRef.current) {
      ringToneRef.current.stop();
      ringToneRef.current = null;
    }

    // 3. Stop audio playback (Berta's TTS) and music (#409 — bug:
    // music kept playing after hangup because cleanup didn't stop it).
    if (audioPlayerRef.current) audioPlayerRef.current.stop();
    if (musicPlayerRef.current) musicPlayerRef.current.stop();
    // #390 PR C-3 — clear any pending domino state queued for
    // post-TTS flush, so it doesn't pop the overlay open after a
    // hangup (the WS is dead by then anyway, but defensive).
    pendingDominoRef.current = null;
    // #966 follow-up — pendingSolitaireRef was never cleared here (the
    // #966 deferred-open landed after #390 wrote this cleanup), so a
    // solitaire_start that was deferred but not flushed before hang-up
    // leaked into the NEXT call and popped the board open unasked. Clear
    // it (and any open board) too.
    pendingSolitaireRef.current = null;
    setSolitaireState(null);
    // #1029 — same defensive clear for the game-picker carousel.
    pendingGamesMenuRef.current = null;
    setGamesMenuState(null);
    // #477 — same defensive clear for pending music so a queued
    // play_music doesn't fire after the user has hung up.
    pendingMusicRef.current = null;

    // 4. Close WebSocket
    if (wsRef.current) {
      wsRef.current.onclose = null; // prevent recursive cleanup
      wsRef.current.close();
      wsRef.current = null;
    }

    // 5. Clear hang-up confirmation timer
    if (hangUpTimerRef.current) { clearTimeout(hangUpTimerRef.current); hangUpTimerRef.current = null; }
    hangUpPendingRef.current = false;
    // #1058 — clear the post-hangup re-dial cooldown timer so a queued
    // setCallJustEnded(false) can't fire after the component unmounts.
    // NB: doHangUp runs cleanup() BEFORE setCallState('idle'), so the
    // cooldown the callState effect arms right after is unaffected.
    if (callJustEndedTimerRef.current) { clearTimeout(callJustEndedTimerRef.current); callJustEndedTimerRef.current = null; }

    // 6. Reset flags
    wsReadyRef.current = false;
    sessionActiveRef.current = false;
    lenaSpeakingRef.current = false;
    pendingMicStartRef.current = false;
    firstAudioReceivedRef.current = false;
    callStartTimeRef.current = null;
    audioServerDoneRef.current = false;
    audioChunksReceivedRef.current = 0;

    // #606 (2026-05-11) — tear down the call-level AudioContext and
    // its watchdog. Created in handleCall under the user gesture;
    // closed here on hangup so a new call gets a fresh context (with
    // a new user gesture window). Belt-and-braces: clear the watchdog
    // FIRST so it can't try to resume a closing context.
    if (pcmWatchdogRef.current) {
      clearInterval(pcmWatchdogRef.current);
      pcmWatchdogRef.current = null;
    }
    if (pcmCtxRef.current) {
      try { pcmCtxRef.current.close().catch(() => {}); } catch {}
      pcmCtxRef.current = null;
    }
    pcmDegradedRef.current = false;
    pcmResumeFailCountRef.current = 0;

    // #897 — defence-in-depth: catch the WS-onclose / abnormal-
    // disconnect path that bypasses doHangUp. dumpWarmupTimings is
    // idempotent (clears its ref after first emission) so calling it
    // here after a successful doHangUp-side dump is a no-op.
    dumpWarmupTimings();
  }

  // ── Transition from calling → connected ─────────────────────────────────
  function transitionToConnected() {
    if (callStateRef.current !== 'calling') return;
    const doTransition = () => {
      if (callStateRef.current !== 'calling') return;
      if (ringToneRef.current) {
        ringToneRef.current.stop();
        ringToneRef.current = null;
      }
      setCallState('connected');
      callStateRef.current = 'connected';
    };
    // For user-initiated calls we hold the `calling…` state for at
    // least MIN_RING_MS so Berta doesn't pick up unnaturally fast.
    // For companion-initiated calls (#196 E) the user just tapped
    // Answer on an incoming call — they expect Berta NOW, not after
    // three seconds of dead silence.
    if (callTypeRef.current !== 'normal') {
      doTransition();
      return;
    }
    const elapsed = callStartTimeRef.current ? Date.now() - callStartTimeRef.current : MIN_RING_MS;
    const remaining = MIN_RING_MS - elapsed;
    if (remaining > 0) {
      setTimeout(doTransition, remaining);
    } else {
      doTransition();
    }
  }

  // ── Open WebSocket ──────────────────────────────────────────────────────
  function ensureWS() {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      // #897 — mark voice-WS open. Together with getUserMedia_resolved
      // and pcm_audio_context_ready this gives the offline analyst a
      // three-way comparison of which warm-up cost dominated this
      // particular call.
      if (warmupTimingsRef.current) {
        warmupTimingsRef.current.mark('ws_open');
      }
    };

    ws.onmessage = async (event) => {
      if (event.data instanceof ArrayBuffer) {
        // First audio chunk → transition to connected
        if (!firstAudioReceivedRef.current && callStateRef.current === 'calling') {
          firstAudioReceivedRef.current = true;
          // #493 reverted 2026-05-08: the synchronous ringtone-stop
          // block was suspected of breaking call setup (Bas reported
          // "nobody is picking up" — WS opens, no `start` ever sent).
          // Reverted to investigate. transitionToConnected handles the
          // ringtone stop on its delayed visual path (pre-#493 behaviour).
          transitionToConnected();
        }

        if (!lenaSpeakingRef.current) {
          lenaSpeakingRef.current = true;
          audioChunksReceivedRef.current = 0;
          audioServerDoneRef.current = false;
          // #402 — soft-mute (keep stream alive) instead of hard kill,
          // so the BT headset doesn't switch profile every turn.
          softMuteMic();
        }
        audioChunksReceivedRef.current++;
        if (audioPlayerRef.current) {
          // #622 — dispatch based on the format the server signalled.
          // 'mp3' (default) goes through addChunk (decoded via Web Audio
          // decodeAudioData). 'pcm-linear16' bypasses the decode queue
          // and treats bytes as raw 16-bit LE PCM at the rate the
          // server declared in audio_format.sample_rate (defaults to
          // 16000 — Aura's actual native rate, regardless of the
          // sample_rate URL hint we send to Deepgram).
          //
          // #622 diagnostic (temporary): log the first chunk of every
          // dispatch decision so we can verify the audio_format JSON
          // was processed BEFORE the binary chunk. If the format flips
          // mid-turn or the first chunk arrives before the format
          // signal, this log shows it.
          if (audioChunksReceivedRef.current === 1) {
            const head = new Uint8Array(event.data, 0, Math.min(16, event.data.byteLength));
            const hex = Array.from(head).map(b => b.toString(16).padStart(2, '0')).join(' ');
            console.log(
              `[#622 dispatch] first chunk this turn: format=${audioFormatRef.current} ` +
              // #997 — sampleRate is only meaningful on the PCM path; mp3
              // carries its own rate in the container.
              (audioFormatRef.current === 'pcm-linear16'
                ? `sampleRate=${audioSampleRateRef.current} `
                : `sampleRate=container-derived `) +
              `size=${event.data.byteLength} head=[${hex}]`
            );
          }
          if (audioFormatRef.current === 'pcm-linear16') {
            audioPlayerRef.current.addPcmChunk(event.data, audioSampleRateRef.current);
          } else {
            audioPlayerRef.current.addChunk(event.data);
          }
        }
        return;
      }
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }

      switch (msg.type) {
        case 'server_hello':
          // #642 — server announces its BUILD_VERSION at handshake.
          // Compare against our loaded build; arm silent reload if
          // mismatched. The hook handles dev/test bypass + bfcache
          // restore + post-call-end reload + idle ceiling + daily
          // 04:00 defence-in-depth internally.
          autoReloadOnServerHello(msg.build_version);
          break;
        case 'ready':
          wsReadyRef.current = true;
          // Hold the `start` send for MIN_RING_MS for user-initiated
          // calls so Berta doesn't greet instantly — feels unnatural,
          // like a hot mic. For companion-initiated calls (#196 E) the
          // user has already heard the incoming-call ring AND tapped
          // Answer; making them sit through 3 more seconds of dead
          // silence reads as "she's broken". Send `start` immediately.
          (async () => {
            const isUserInitiated = callTypeRef.current === 'normal';
            if (isUserInitiated && callStartTimeRef.current && callStateRef.current === 'calling') {
              const elapsed = Date.now() - callStartTimeRef.current;
              if (elapsed < MIN_RING_MS) {
                await new Promise(r => setTimeout(r, MIN_RING_MS - elapsed));
              }
            }
            if (ws.readyState !== WebSocket.OPEN) return;
            ws.send(JSON.stringify({
              type: 'start',
              session_id: Date.now().toString(),
              user_id: userIdRef.current,
              call_type: callTypeRef.current,
              // #200 PR-A2 — only present for incoming companion calls;
              // server uses it to apply outcome-driven side-effects
              // (mark reminder delivered, retry on no-response).
              pending_call_id: pendingCallIdRef.current || null,
            }));
            // #703 — phone-home: collect + send device snapshot so the
            // admin Call Diagnostics view has tablet context (browser /
            // OS / network / mic permission / audio devices / app
            // build) for remote Alpha debugging. Fire-and-forget; any
            // failure is swallowed by the helpers and cannot impact
            // the call.
            try {
              const snap = await _collectClientSnapshot(LOADED_BUILD);
              _sendClientDiagnostics(ws, snap);
            } catch (_) {
              // Snapshot is best-effort; never break the call.
            }
            // Do NOT start mic here — mic starts only after greeting audio drains
            // (handled in the 'listening' message handler)
          })();
          break;
        case 'audio_format':
          // #622 — server tells us how to decode the next binary
          // chunks for this turn. 'mp3' (default) → addChunk, decoded
          // via Web Audio. 'pcm-linear16' → addPcmChunk, treated as
          // raw 16-bit LE PCM at the rate the server declares
          // (msg.sample_rate, default 16000). Sent right before the
          // first chunk; later chunks in the same turn inherit it.
          if (msg.format === 'pcm-linear16' || msg.format === 'mp3') {
            audioFormatRef.current = msg.format;
            if (typeof msg.sample_rate === 'number' && msg.sample_rate > 0) {
              audioSampleRateRef.current = msg.sample_rate;
            }
            // #997 — only PCM uses audioSampleRateRef; for mp3 the rate is
            // read from the container by decodeAudioData, so don't print the
            // (unused) ref value — it misled a #997 investigation into thinking
            // the greeting was 16 kHz when the mp3 was actually 24 kHz.
            console.log(
              msg.format === 'pcm-linear16'
                ? `[AUDIO] format set to pcm-linear16 @ ${audioSampleRateRef.current}Hz for this turn`
                : `[AUDIO] format set to ${msg.format} (sample rate from container) for this turn`
            );
          } else if (msg.format === 'pcm-linear16-24000') {
            // Backward compat with the previous-bug naming where rate
            // was baked into the format string. Treat as 24000Hz PCM.
            audioFormatRef.current = 'pcm-linear16';
            audioSampleRateRef.current = 24000;
            console.log(`[AUDIO] format set to pcm-linear16 @ 24000Hz (legacy format string)`);
          } else {
            console.warn(`[AUDIO] unknown format ${msg.format} — staying on ${audioFormatRef.current}`);
          }
          break;

        case 'partial_transcript':
          // #897 — record the FIRST partial transcript of the call as
          // a warm-up timing anchor. The collector's `mark` keeps
          // first-wins semantics, so later partials don't overwrite.
          // This is the user-facing "STT is awake" reference point.
          if (warmupTimingsRef.current) {
            warmupTimingsRef.current.mark('first_stt_partial');
          }
          setTranscript(msg.text);
          setFocalSpeaker('user');  // #1032 — user's turn owns the panel
          break;
        case 'utterance_end':
          // Server-side STT (Deepgram) detected end-of-utterance. Whether
          // we act on it depends on vad_strategy (#132):
          //   client_vad           → ignore (browser VAD silence timer drives stop)
          //   deepgram_endpointing → stop now; browser silence timer is suppressed
          //   hybrid               → stop now; whichever signal arrives first wins
          {
            const strategy = configRef.current.vad_strategy || 'client_vad';
            console.log('[MIC DEBUG] utterance_end received, strategy:', strategy);
            if (strategy === 'deepgram_endpointing' || strategy === 'hybrid') {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                stopRecordingAndSend();
              }
            }
          }
          break;
        case 'interrupted':
          setStatus('idle'); statusRef.current = 'idle';
          sessionActiveRef.current = false;
          if (callStateRef.current === 'connected' && startListeningRef.current) {
            startListeningRef.current();
          }
          break;
        case 'quota_exceeded':
          // #297 — the server is about to close the WS after the
          // quota TTS finishes streaming. Mark the call as
          // quota-declined so the onclose handler waits for the
          // AudioPlayer to finish playing before transitioning to
          // idle (instead of stopping playback + flipping state
          // immediately, which is what made the UI look "ended"
          // while Berta was still speaking).
          // Keep the in-call state visible during the trailing audio.
          // Reply text is set so the user (and tester) can see what
          // was just spoken.
          quotaExceededRef.current = true;
          if (msg.reset_date) {
            console.info(`Quota exceeded — service resumes ${msg.reset_date}`);
          }
          // #897 — dump warm-up timings into the ring buffer BEFORE
          // the flush so this call's timings ride the same batch.
          dumpWarmupTimings();
          // #718 — flush console logs before the trailing-TTS WS
          // close (server will close once the audio drains). WS is
          // still OPEN here so the send goes through.
          try { _flushConsoleCapture(wsRef.current); } catch (_) { /* swallow */ }
          setStatus('speaking'); statusRef.current = 'speaking';
          setFocalSpeaker('companion');  // #1032 — quota message is Berta's
          if (audioPlayerRef.current) {
            // Tell the AudioPlayer "no more chunks coming" so its
            // onDone handler can fire once the queue drains.
            audioPlayerRef.current.signalServerDone();
          }
          break;
        case 'status':
          if (msg.message === 'transcribing') { setStatus('transcribing'); statusRef.current = 'transcribing'; }
          else if (msg.message === 'thinking') { setStatus('thinking'); statusRef.current = 'thinking'; }
          else if (msg.message === 'speaking') {
            setStatus('speaking'); statusRef.current = 'speaking';
            // #1032 — Berta is the focal speaker now. Clear the previous
            // reply IMMEDIATELY (Bas 2026-06-08: at the user→Berta switch the
            // prior reply lingered until her first new sentence streamed in;
            // he wants an empty box in that gap, not the stale text). The
            // re-read of her last reply happens during the listening pause
            // BEFORE she speaks, not here, so clearing on-speak doesn't lose
            // it. setReply('') also flushes any queued sentences.
            setFocalSpeaker('companion');
            setReply('');
            // #402 — soft-mute (keep stream alive) so BT headset
            // doesn't switch profile.
            if (!lenaSpeakingRef.current) { lenaSpeakingRef.current = true; softMuteMic(); }
            // #622 — reset to default audio format at start of every
            // turn. Aura WS streaming will explicitly signal pcm-linear16
            // via an 'audio_format' frame BEFORE its first binary
            // chunk; if no signal comes, this turn is the per-sentence
            // MP3 fallback path.
            audioFormatRef.current = 'mp3';
          }
          else if (msg.message === 'waiting') {
            // #687 — wait mode: Berta is silent until the user says
            // her name again. The mic stays open and audio still
            // streams, but the CallStrip face flips to the "waiting"
            // pose so the user can see her state at a glance. We do
            // NOT call startListening / startVADLoop / etc. — those
            // are bound to the listening status and we don't want
            // the silence-ladder or mic-warning machinery firing
            // during a side conversation.
            setStatus('waiting'); statusRef.current = 'waiting';
            // #692 — clear the speaking flag the preceding
            // case 'speaking' branch set. Without this, AudioPlayer's
            // onDone path (handleAudioPlayerDone) would see
            // lenaSpeakingRef=true after the ack drains and stay in
            // the "Berta is still speaking" state, which freezes the
            // mic in soft-mute and blocks the wake-name from ever
            // reaching the server.
            lenaSpeakingRef.current = false;
          }
          else if (msg.message === 'no_speech_detected') {
            // Legacy fallback — backend now sends 'listening' instead
            sessionActiveRef.current = false;
            lenaSpeakingRef.current = false;
            stopVADLoop();
            if (callStateRef.current === 'connected' && startListeningRef.current) {
              setStatus('listening'); statusRef.current = 'listening';
              startListeningRef.current();
            } else {
              setStatus('idle'); statusRef.current = 'idle';
            }
          }
          break;
        case 'wait_mode_state':
          // #700 follow-up — authoritative wait-mode lifecycle event.
          // The server pushes `{active:true}` on entry (voice gate OR
          // button) and `{active:false}` on every exit path (wake by
          // name, button resume, 10-min cap, 5-min silent cap). Drives
          // the pause/play icon swap WITHOUT any dependency on the
          // mini-face `status` (which churns through transcribing /
          // thinking / speaking / listening / waiting and would make
          // the button flicker between non-wake utterances if we
          // derived the icon state from it).
          if (typeof msg.active === 'boolean') {
            setWaitMode(msg.active);
            waitModeRef.current = msg.active;
          }
          break;
        case 'transcript':
          if (msg.text) { emptyTurnCountRef.current = 0; setMicWarning(null); }
          setTranscript(msg.text);
          // #405 — capture the completed utterance into the freeze
          // slot so the YouSaidCard keeps showing it through
          // transcribing/thinking/speaking. Only freeze when there's
          // actual content (don't clear an existing freeze with an
          // empty late-arriving final from a hang-up race).
          if (msg.text && msg.text.trim()) {
            setFrozenUtterance(msg.text);
            setFocalSpeaker('user');  // #1032 — completed utterance is the user's turn
            // #381 — successful transcript means whatever was broken
            // cleared up. Drop the connection-hiccup indicator.
            setConnectionHiccup(null);
          }
          break;
        case 'user_config':
          // #441 Phase 1 — server has loaded the elderly user and
          // pushed their resolved language. Switch the tablet UI to
          // it (CallStrip / YouSaidCard read live from LanguageContext).
          // Defensive: ignore if the value isn't one of the SUPPORTED
          // codes — falls through to whatever LanguageProvider
          // detected from the browser.
          if (msg.language && langCtx && typeof langCtx.setLang === 'function') {
            try {
              langCtx.setLang(msg.language);
            } catch (e) {
              console.warn('[i18n] setLang failed (non-fatal):', e);
            }
          }
          break;
        case 'connection_hiccup':
          // #381 — server's empty-transcript branching classified this
          // turn as a mechanical failure (or vad-speech-no-transcript).
          // Show a small amber indicator in the CallStrip so the elderly
          // user (and family/admin watching the recording) can see "she
          // didn't ignore me, something glitched". Auto-clears on next
          // successful transcript above. Cap is 2 attempts; after that
          // the server falls back to the silence ladder (no more
          // hiccup events fire — see #381 design Q3 → A).
          setConnectionHiccup({
            kind: msg.reason_kind || 'mechanical',
            attempt: msg.attempt || 1,
          });
          break;
        case 'show_photos':
          setPhotoCarousel({ photos: msg.photos });
          break;
        case 'show_photo': // backward compat
          setPhotoCarousel({ photos: [{ url: msg.url, caption: msg.caption }] });
          break;
        case 'show_image_gallery':
          // Web image search results (#89). Reuses PhotoCarousel —
          // the data shape {url, caption} is the same as family photos.
          setPhotoCarousel({ photos: msg.images });
          break;
        case 'proposal_dropped':
          // #340 — server-side safety filter blocked a media tag
          // Berta just emitted. The user heard her promise something
          // that won't appear on the tablet. The server has already
          // armed a one-shot recovery instruction in the LLM context
          // for the next turn, so Berta will acknowledge verbally.
          // Client behaviour for v1: log only. No popup / toast —
          // verbal recovery from Berta is the primary UX.
          console.warn('[SAFETY] Proposal dropped by content filter:',
            msg.proposal_type, msg.query, '— reason:', msg.reason);
          break;
        case 'show_video':
          // YouTube video clip (#91). Queued — applied to videoOverlay
          // only once Berta's TTS for this turn has finished playing,
          // so the user hears her finish speaking before the video
          // starts. The flush happens in AudioPlayer.onDone below.
          // #625 — fallback_ids carries up to 4 alternate clips that
          // also passed the oEmbed probe; VideoOverlay walks them on
          // YT player onError or never-reached-PLAYING. Capture the
          // query so we can speak a contextual apology if the whole
          // list is exhausted.
          pendingVideoRef.current = {
            youtubeId: msg.youtube_id,
            title: msg.title,
            fallbackIds: Array.isArray(msg.fallback_ids) ? msg.fallback_ids : [],
            query: msg.query || '',
          };
          break;
        case 'play_music':
          // Background YouTube audio (#90). Tablet renders an invisible
          // iframe; user just hears the music. Auto-pauses while Berta speaks.
          //
          // #477 — same deferral pattern as `show_video` / `domino_start`.
          // The server dispatches play_music INLINE while the LLM is
          // streaming the sentence with the [PLAY_MUSIC] tag, so the WS
          // message arrives BEFORE the TTS audio for that sentence has
          // started. Without this gate the player would pop up while
          // Berta is mid-question ("what would you like to hear?") —
          // exactly Bas's report 2026-05-08. If Berta is currently
          // speaking (AudioPlayer has audio queued / playing), queue
          // the play call and let onDone flush it once she finishes.
          // Otherwise (idle / between turns) play immediately as
          // before.
          if (audioPlayerRef.current && audioPlayerRef.current.isPlaying()) {
            pendingMusicRef.current = { youtube_id: msg.youtube_id, title: msg.title };
          } else if (musicPlayerRef.current) {
            musicPlayerRef.current.play(msg.youtube_id, msg.title);
          }
          break;
        case 'stop_music':
          if (musicPlayerRef.current) musicPlayerRef.current.stop();
          break;
        case 'clear_media':
          // Dismiss visual media (photos / video). Music has its own
          // stop_music event — semantically separate so the user can
          // ask "put the pictures away" without killing the music.
          setPhotoCarousel(null);
          setVideoOverlay(null);
          // #1029 — also close the game-picker carousel. Berta emits
          // [CLEAR_MEDIA] when the user says "never mind" / "put that
          // away", giving a voice path to dismiss the picker.
          setGamesMenuState(null);
          pendingGamesMenuRef.current = null;
          break;
        case 'domino_start':
          // #390 PR C-3 — defer overlay opening until Berta finishes her
          // spoken acceptance. Without this, the board appears while
          // she's still mid-sentence saying her warm "yes, let's play"
          // (Bas's PR #393 feedback). #974 — that acceptance line is a
          // normal chat-channel reply (the game itself has no voice);
          // deferring until it finishes is still correct. Same deferral
          // pattern as pendingVideoRef. The flush happens in
          // AudioPlayer.onDone.
          //
          // We DO stop the music immediately (not deferred) — Berta's
          // spoken acceptance shouldn't compete with background music
          // while she's introducing the game.
          if (musicPlayerRef.current) musicPlayerRef.current.stop();
          // Also: if Berta isn't currently speaking (no AudioPlayer
          // playback in progress), there's nothing to wait for —
          // open the overlay immediately. Otherwise queue.
          if (audioPlayerRef.current && audioPlayerRef.current.isPlaying()) {
            pendingDominoRef.current = msg.state;
          } else {
            setDominoState(msg.state);
          }
          break;
        case 'domino_state':
          // Mid-game state pushes are immediate — these are responses
          // to user actions / Berta's move. No overlay deferral needed;
          // the overlay is already open. #974 — the game no longer has
          // ANY audio channel of its own (Berta's move/pass cues used to
          // play in parallel here; that second channel was deleted as
          // the structural root of the in-game hang/hash bug class). The
          // DominoGame component now adds a short client-side "thinking"
          // pause before rendering Berta's move and shows a visual
          // "passed" badge for her knock, so no audio frame is expected
          // by the tablet for a domino move (the #965 WS-consumer-side
          // check: removing server game-TTS leaves nothing here awaiting
          // a status:speaking / audio_format it no longer gets).
          setDominoState(msg.state);
          break;
        case 'domino_end':
          // Game ended — keep the overlay visible long enough to show
          // the GameEndCard ("What a lovely game!"), then user taps
          // Done to dismiss. The server's _handle_domino_message has
          // already cleared its side; we just need to clear ours when
          // the user closes the overlay (handled in onQuit below).
          // For now we mark the game as ended on the local state so
          // the GameEndCard appears.
          setDominoState((prev) => prev ? { ...prev, winner: msg.winner || 'tie' } : null);
          console.log('[domino] game ended', msg.reason, msg.winner);
          break;
        case 'domino_error':
          // Illegal move or server-side game-state error. Surface as a
          // brief toast-style message via the existing reply panel —
          // doesn't break the game flow.
          console.warn('[domino] error from server:', msg.message);
          // #713 — scrRef.current (not scr) so the user sees the
          // fallback in the elderly persona's language, not whatever
          // language the LanguageProvider booted into.
          setReply(msg.message || scrRef.current.dominoMoveError);
          setTimeout(() => setReply(''), 4000);
          break;
        case 'solitaire_start':
          // #966 — defer overlay open until Berta finishes her spoken
          // acceptance (same pattern as domino_start). Stop background
          // music immediately; the game itself has no audio of its own.
          if (musicPlayerRef.current) musicPlayerRef.current.stop();
          if (audioPlayerRef.current && audioPlayerRef.current.isPlaying()) {
            pendingSolitaireRef.current = msg.state;
          } else {
            setSolitaireState(msg.state);
          }
          break;
        case 'solitaire_state':
          // Mid-game pushes are immediate — the overlay is already open and
          // the game is visual-only, so no audio frame is expected here
          // (the #966 single-channel invariant; mirrors domino_state).
          setSolitaireState(msg.state);
          break;
        case 'solitaire_end':
          // The winning / stuck flags ride in the preceding solitaire_state,
          // so the win-cascade / "fresh deal?" card is already showing. The
          // server has cleared its side; we clear ours when the user taps
          // Done (onQuit). Nothing to render here beyond a log.
          console.log('[solitaire] game ended', msg.reason);
          break;
        case 'solitaire_error':
          // Illegal move / server-side error. Moves are validated
          // client-side before issuing (only glowing targets are tappable),
          // so this is an edge case. Do NOT surface the server's English
          // ValueError text to the user (rule 6) — the card simply doesn't
          // move, which is self-evident. Log for diagnosis only.
          console.warn('[solitaire] error from server:', msg.message);
          break;
        case 'games_menu_start':
          // #1029 — open the game-picker carousel. Defer until Berta
          // finishes her spoken line (same deferred-open pattern as the
          // game overlays). No music stop here — the carousel is only a
          // chooser; if the user then picks a game, that game's *_start
          // handler stops the music. Payload: most-played-ordered game ids
          // + a personalised flag (drives the "you enjoy this" badge).
          if (audioPlayerRef.current && audioPlayerRef.current.isPlaying()) {
            pendingGamesMenuRef.current = { games: msg.games, personalised: msg.personalised };
          } else {
            setGamesMenuState({ games: msg.games, personalised: msg.personalised });
          }
          break;
        case 'reply_chunk':
          gotReplyRef.current = true;
          // #1032 — the previous reply was already cleared at status
          // 'speaking' (so the user→Berta switch shows an empty box, not the
          // stale prior reply), so the queue starts fresh here. Mark that the
          // call has produced a reply — this flips the empty panel from the
          // idle placeholder to a blank box for the rest of the call
          // (allowPlaceholder in BertaReplyPanel).
          setCallHasReply(true);
          // #468 — buffered + paced reveal. The hook joins the new sentence
          // to the existing visible reply with a single space and releases
          // queued sentences at ~150 wpm (#1032 — lowered from 175, which
          // ran ahead of her voice).
          enqueueChunk(msg.text);
          break;
        case 'reply':
          gotReplyRef.current = true;
          setFocalSpeaker('companion');  // #1032 — full-reply / toast is Berta's
          setCallHasReply(true);
          setReply(msg.text);
          break;
        case 'listening':
        case 'done': {
          // #726 — only clear sessionActiveRef when the mic is NOT
          // mid-recording. Pre-fix, the unconditional clear here
          // disabled sendChain (which drops chunks when
          // !isSessionActive) on the un-pause-from-wait-mode path
          // where #724/#716 makes startListening early-out because
          // the mic is still active from a prior wait-mode-drop
          // restart. Two distinct downstream symptoms, both surfaced
          // on staging 2026-05-15:
          //   - Button pause + button resume → audio never reaches
          //     server; sendChain silently drops chunks until hangup.
          //   - Voice pause + button resume → when VAD's silence
          //     timer eventually fires, onstop sees sessionActiveRef
          //     false and takes the killMic-replay path, sending a
          //     fresh audio_start that splits the recording into two
          //     server-side turns and fires a spurious re-prompt
          //     ("Sorry, that didn't quite come through").
          // Same guard pattern as #724's stopVADLoop guard below —
          // when the mic is mid-session, leave the active flag alone.
          const _micRecAtDone = mediaRecorderRef.current
            && mediaRecorderRef.current.state !== 'inactive';
          if (!_micRecAtDone) {
            sessionActiveRef.current = false;
          }
          console.log('[MIC DEBUG] listening/done: hadRealAudio:', hadRealAudioRef.current, 'gotReply:', gotReplyRef.current, 'emptyCount:', emptyTurnCountRef.current, 'micRec:', _micRecAtDone);
          // Track consecutive empty turns — only when VAD detected real speech (#95)
          if (!gotReplyRef.current && hadRealAudioRef.current) {
            emptyTurnCountRef.current++;
            console.log('[MIC DEBUG] empty turn incremented to', emptyTurnCountRef.current);
            // Show one-time reassurance message per call (#94)
            // #544 \u2014 was hardcoded English; now resolved through the
            // same `scr` i18n bucket the rest of the screen uses, so
            // a non-English caller sees the reassurance + warning in
            // their own language.
            if (!reassuranceSentRef.current) {
              reassuranceSentRef.current = true;
              // #713 — scrRef.current (not scr) — ws.onmessage closure
              // captures scr at WS-open time; if setLang fires AFTER the
              // WS opens, the captured scr is the WRONG language. See
              // scrRef declaration at the top of the component.
              setReply(scrRef.current.micwarning.takeYourTime);
              setTimeout(() => setReply(''), 4000);
            }
            if (emptyTurnCountRef.current >= 3) {
              setMicWarning(scrRef.current.micwarning.checkMicrophone);
            }
          } else if (gotReplyRef.current) {
            emptyTurnCountRef.current = 0;
            setMicWarning(null);
          }
          console.log('[MIC DEBUG] continuing after turn end, callState:', callStateRef.current);
          gotReplyRef.current = false;
          hadRealAudioRef.current = false;

          // If no audio chunks from server (or companion already stopped speaking
          // and the counter is stale from a previous turn), restart listening
          // directly — AudioPlayer.onDone won't fire when there's nothing to play.
          // The !lenaSpeakingRef.current guard catches stale audioChunksReceivedRef
          // values left over from a prior turn (#124).
          // Critical: if audio is still playing on the client, NEVER take the
          // direct restart path. With buffered+pipelined TTS the server now
          // sends 'listening' ~10s before playback finishes, and prematurely
          // flipping status to 'listening' was making the reply text disappear
          // mid-speech. Always fall through to the wait-for-onDone path; the
          // AudioPlayer.onDone callback handles the real handoff.
          const audioStillPlaying = audioPlayerRef.current && audioPlayerRef.current.isPlaying();
          if (!audioStillPlaying && (audioChunksReceivedRef.current === 0 || !lenaSpeakingRef.current)) {
            console.log('[MIC DEBUG #124] direct restart path: audioChunks:', audioChunksReceivedRef.current, 'lenaSpeaking:', lenaSpeakingRef.current);
            lenaSpeakingRef.current = false;
            audioChunksReceivedRef.current = 0;
            audioServerDoneRef.current = false;
            // #724 — only reset the VAD loop when we're actually opening a
            // fresh mic. If the mic is already recording (e.g. un-pause
            // from button-triggered wait mode, where the mic + VAD were
            // running throughout to listen for the wake-name), the
            // `startListening` call below early-outs via the #716
            // idempotency guard — so this `stopVADLoop()` would kill the
            // live VAD with nothing to restart it, leaving the mic
            // recording with no endpoint detection. `startListening`
            // already calls `stopVADLoop()` internally (line ~2340)
            // before it sets up a fresh VAD, so the duplicate here was
            // only ever needed for the wait-mode-exit case it just
            // broke. Staging repro 2026-05-15: pause-while-speaking +
            // un-pause left the user unable to be heard until hangup.
            const micAlreadyRecording = mediaRecorderRef.current
              && mediaRecorderRef.current.state !== 'inactive';
            if (!micAlreadyRecording) {
              stopVADLoop();
            }
            const isInCall = callStateRef.current === 'connected' || callStateRef.current === 'calling';
            if (isInCall && startListeningRef.current) {
              if (callStateRef.current === 'calling') {
                setCallState('connected'); callStateRef.current = 'connected';
                if (ringToneRef.current) { ringToneRef.current.stop(); ringToneRef.current = null; }
              }
              // Detect dead WS and reconnect before restarting mic (#124)
              const wsNow = wsRef.current;
              if (!wsNow || wsNow.readyState === WebSocket.CLOSED || wsNow.readyState === WebSocket.CLOSING) {
                console.warn('[MIC DEBUG #124] WS dead after empty turns — reconnecting');
                ensureWS();
              }
              // #696 — when in wait mode (status='waiting' set by server's
              // `status: waiting` push that came before this 'done'), DON'T
              // overwrite the user-facing label to 'listening'. The VAD
              // restart below still runs so the wake-name can be captured;
              // we just keep the 🤫 "Waiting" face visible so the user
              // knows Berta is in silent-listening mode.
              if (statusRef.current !== 'waiting') {
                setStatus('listening'); statusRef.current = 'listening';
              }
              console.log('[MIC DEBUG #124] restarting listening (emptyCount:', emptyTurnCountRef.current, ')');
              startListeningRef.current();
            }
            break;
          }

          // Signal AudioPlayer that all audio for this turn has been sent.
          // AudioPlayer.onDone will fire when all chunks finish decoding + playing.
          audioServerDoneRef.current = true;
          if (audioPlayerRef.current) audioPlayerRef.current.signalServerDone();
          // Safety fallback: only fires if AudioPlayer.onDone hasn't fired
          // after 15s AND audio isn't still playing. With buffered TTS a
          // single reply can be 25+ seconds of audio, so we must not force
          // a restart while the buffers are still draining.
          {
            const micFallbackTimer = setTimeout(() => {
              if (!audioServerDoneRef.current) return;
              if (audioPlayerRef.current && audioPlayerRef.current.isPlaying()) {
                console.log('[MIC DEBUG] 15s fallback skipped — audio still playing, deferring to onDone');
                return;
              }
              console.log('[MIC DEBUG #124] 15s fallback fired — forcing restart');
              lenaSpeakingRef.current = false;
              audioServerDoneRef.current = false;
              audioChunksReceivedRef.current = 0;
              stopVADLoop();
              const isInCall = callStateRef.current === 'connected' || callStateRef.current === 'calling';
              if (isInCall && startListeningRef.current) {
                if (callStateRef.current === 'calling') {
                  setCallState('connected'); callStateRef.current = 'connected';
                  if (ringToneRef.current) { ringToneRef.current.stop(); ringToneRef.current = null; }
                }
                setStatus('listening'); statusRef.current = 'listening';
                startListeningRef.current();
              }
            }, 15000);
            if (window._micFallbackTimer) clearTimeout(window._micFallbackTimer);
            window._micFallbackTimer = micFallbackTimer;
          }
          break;
        }
        case 'idle_timeout':
          // Server hung up due to inactivity — transition cleanly to idle
          sessionActiveRef.current = false;
          lenaSpeakingRef.current = false;
          // #897 — dump warm-up timings before the flush so this
          // call's timings ride the same batch.
          dumpWarmupTimings();
          // #718 — flush captured console logs while the WS is still
          // technically open (server hasn't closed yet, just sent
          // idle_timeout message). This and the user-hangup path are
          // the two reliable end-of-call hooks; ws.onclose fires too
          // late to do a clean WS send.
          try { _flushConsoleCapture(wsRef.current); } catch (_) { /* swallow */ }
          stopVADLoop();
          killMic('server_idle_timeout');
          if (audioPlayerRef.current) audioPlayerRef.current.stop();
          // #409 — also stop background music so it doesn't outlive
          // the call when server idle-times us out
          if (musicPlayerRef.current) musicPlayerRef.current.stop();
          // #713 — scrRef.current (latest language), not scr (captured
          // at WS-open). See scrRef declaration.
          setReply(scrRef.current.callEnded);
          setStatus('idle'); statusRef.current = 'idle';
          setCallState('idle'); callStateRef.current = 'idle';
          if (ringToneRef.current) { ringToneRef.current.stop(); ringToneRef.current = null; }
          firstAudioReceivedRef.current = false;
          callStartTimeRef.current = null;
          // Brief "Call ended" display, then clear
          setTimeout(() => { setReply(''); }, 2000);
          break;
        case 'error':
          const isQuota = msg.message?.includes('quota');
          setStatus(isQuota ? 'quota' : 'error');
          statusRef.current = isQuota ? 'quota' : 'error';
          sessionActiveRef.current = false;
          setTimeout(() => {
            setStatus('idle'); statusRef.current = 'idle';
            if (callStateRef.current === 'connected' && startListeningRef.current) {
              startListeningRef.current();
            }
          }, 3000);
          break;
        default: break;
      }
    };

    ws.onclose = (e) => {
      console.warn('WS closed:', e.code, e.reason);
      // #642 — WS close === end of a call. The hook defers any reload
      // by 5s so the user sees the call-ended UI before the screen
      // blinks, and re-checks at the trailing edge that no new call
      // started in the gap. Called as early as possible so the reload
      // is armed before the rest of onclose mutates state.
      autoReloadOnCallEnd();
      wsRef.current = null;
      wsReadyRef.current = false;
      sessionActiveRef.current = false;

      // If WS drops while still calling, clean up and reset to idle
      if (callStateRef.current === 'calling') {
        if (ringToneRef.current) { ringToneRef.current.stop(); ringToneRef.current = null; }
        killMic(`ws_onclose_while_calling_code${e.code}`);
        setCallState('idle'); callStateRef.current = 'idle';
      }

      // Auto-reconnect on 1005 (unexpected close) during active call
      if (e.code === 1005 && callStateRef.current === 'connected') {
        setStatus('reconnecting'); statusRef.current = 'reconnecting';
        killMic('ws_onclose_1005_pre_reconnect');
        // #703 — phone home the reconnect attempt BEFORE we tear down
        // the existing WS, while it might still be open enough to
        // deliver a final message. After reconnect succeeds the new WS
        // does not get a duplicate event (we send only the attempt).
        try {
          _sendClientEvent(wsRef.current, 'reconnect', {
            ws_close_code: e.code,
            ws_close_reason: e.reason || '',
          });
        } catch (_) { /* swallow */ }
        setTimeout(() => {
          // Attempt one reconnect
          try {
            firstAudioReceivedRef.current = false;
            ensureWS();
            // If second close also fires, the default handler below will drop to idle
          } catch (err) {
            console.error('Reconnect failed:', err);
            killMic('ws_reconnect_failed');
            if (audioPlayerRef.current) audioPlayerRef.current.stop();
            // #409 — stop music on reconnect failure (call terminating)
            if (musicPlayerRef.current) musicPlayerRef.current.stop();
            setCallState('idle'); callStateRef.current = 'idle';
            setStatus('idle'); statusRef.current = 'idle';
            // #713 — scrRef.current to read latest language. The
            // reconnect setTimeout fires later than ws.onclose itself
            // but the same closure-capture risk applies.
            setReply(scrRef.current.callEnded);
          }
        }, 1500);
        return; // Don't drop to idle yet
      }

      // #297 — quota-declined close: the server played a TTS message
      // and is now closing the WS. Don't transition to idle here — the
      // AudioPlayer.onDone callback (further down in JSX) will handle
      // the transition when the queue drains. Mic is killed; everything
      // else waits.
      // Why not poll isPlaying(): there's a race window where chunks
      // are queued but decoding hasn't started, isPlaying returns false,
      // and the poll thinks drain is done immediately. onDone is the
      // authoritative drain signal — it fires only after pendingDoneRef
      // is set AND all chunks have actually played out (#297 testing
      // 2026-05-05 evening).
      if (callStateRef.current === 'connected' && quotaExceededRef.current) {
        killMic('ws_onclose_quota_exceeded');
        // Status stays 'speaking' (set in the quota_exceeded message
        // handler) so the in-call UI persists. The onDone handler
        // checks quotaExceededRef and transitions to idle when audio
        // finishes.
        return;
      }

      // If connected and WS closes for any other reason, clean up and drop to idle
      if (callStateRef.current === 'connected') {
        killMic(`ws_onclose_unexpected_code${e.code}`);
        if (audioPlayerRef.current) audioPlayerRef.current.stop();
        // #409 — stop music when WS unexpectedly closes mid-call
        if (musicPlayerRef.current) musicPlayerRef.current.stop();
        lenaSpeakingRef.current = false;
        firstAudioReceivedRef.current = false;
        setCallState('idle'); callStateRef.current = 'idle';
        setStatus('idle'); statusRef.current = 'idle';
        // #713 — scrRef.current for the same reason as in ws.onmessage.
        setReply(scrRef.current.callEnded);
      }
    };

    ws.onerror = () => {
      setStatus('error'); statusRef.current = 'error';
      sessionActiveRef.current = false;
      wsRef.current = null;
      wsReadyRef.current = false;
      killMic();
      setTimeout(() => {
        setStatus('idle'); statusRef.current = 'idle';
        if (callStateRef.current === 'connected' && startListeningRef.current) {
          startListeningRef.current();
        }
      }, 3000);
    };
  }

  // ── Send full audio blob (fallback) ─────────────────────────────────────
  function sendAudioBlob(audioBlob) {
    sessionActiveRef.current = true;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      audioBlob.arrayBuffer().then(ab => ws.send(ab));
    } else {
      pendingBlobRef.current = audioBlob;
      ensureWS();
    }
  }

  // ── Stop recording and signal end ───────────────────────────────────────
  function stopRecordingAndSend() {
    stopVADLoop();
    // #716 follow-up — clear the VAD_MAX safety timer. The recording
    // is ending naturally; the 60s cap is no longer relevant for this
    // session, and a leaked handle could fire during the next one.
    if (vadMaxTimerRef.current) {
      clearTimeout(vadMaxTimerRef.current);
      vadMaxTimerRef.current = null;
    }
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return;

    const speechDuration = speechStartRef.current ? Date.now() - speechStartRef.current : 0;

    mediaRecorderRef.current.onstop = async () => {
      setIsRecording(false);

      const totalBytes = audioChunksRef.current.reduce((sum, c) => sum + c.size, 0);
      console.log('[MIC DEBUG] stopRecordingAndSend: speechDuration:', speechDuration, 'totalBytes:', totalBytes, 'vadDetectedSpeech:', vadDetectedSpeechRef.current);
      if (speechDuration < VAD_MIN_SPEECH_MS || audioChunksRef.current.length === 0 || totalBytes < 2000) {
        // Too short / silence — don't count toward mic warning
        hadRealAudioRef.current = false;
        console.log('[MIC DEBUG] too short/small — hadRealAudio=false');
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          // Drain any in-flight sends so we don't put bytes on the wire
          // after audio_end (Deepgram would 400 on the trailing data).
          await (sendChainRef.current ? sendChainRef.current.drain() : Promise.resolve());
          if (ws.readyState === WebSocket.OPEN) {
            // #381 — even on the early-out path the server gets a
            // structured reason (no_audio_frames / mic_error / vad_silence).
            // Without this the server can't tell silence-from-quiet-room
            // apart from silence-from-broken-mic and falls back to ladder.
            ws.send(JSON.stringify(buildAudioEndPayload({
              audioBytes: totalBytes,
              chunkCount: audioChunksRef.current.length,
              vadDetectedSpeech: vadDetectedSpeechRef.current,
              micError: micErrorRef.current,
              calibratedThreshold: vadCalibratedThresholdRef.current,
            })));
          }
        }
        sessionActiveRef.current = false;
        setStatus('idle'); statusRef.current = 'idle';
        if (callStateRef.current === 'connected' && startListeningRef.current) {
          setTimeout(() => startListeningRef.current(), 200);
        }
        return;
      }

      if (vadStreamRef.current) {
        vadStreamRef.current.getTracks().forEach(t => t.stop());
        vadStreamRef.current = null;
      }

      // Only count as real audio if VAD actually detected speech (#95)
      hadRealAudioRef.current = vadDetectedSpeechRef.current;
      console.log('[MIC DEBUG] hadRealAudio:', hadRealAudioRef.current, 'audioSize:', totalBytes, 'emptyCount:', emptyTurnCountRef.current);

      // #382 — compute per-turn SNR (signal-to-noise ratio) so the
      // server can aggregate it into the user's speaker profile.
      // SNR = median speech RMS / noise floor RMS. Device-independent;
      // captures how loudly the user speaks relative to ambient noise.
      // Reset per-turn buffers regardless of whether we ship.
      let turnSnr = null;
      try {
        const speechSamples = vadSpeechSamplesRef.current || [];
        const noiseFloor = vadNoiseFloorRef.current;
        if (speechSamples.length >= 5 && noiseFloor && noiseFloor > 0) {
          const sorted = [...speechSamples].sort((a, b) => a - b);
          const speechP50 = sorted[Math.floor(sorted.length * 0.5)];
          if (speechP50 > 0) {
            turnSnr = speechP50 / noiseFloor;
            // Clamp to a sane range to defend against pathological inputs
            // (the server-side helper also clamps but belt-and-braces).
            if (turnSnr > 100) turnSnr = 100;
            if (turnSnr < 0) turnSnr = null;
            console.log(`[MIC DEBUG #382] turn SNR: ${turnSnr.toFixed(2)} ` +
                        `(speech p50: ${speechP50.toFixed(4)}, noise floor: ${noiseFloor.toFixed(4)}, ` +
                        `${speechSamples.length} samples)`);
          }
        }
      } catch (e) {
        console.warn('[MIC DEBUG #382] SNR calc failed (non-fatal):', e);
      }
      // Reset for the next turn
      vadSpeechSamplesRef.current = [];

      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        if (!sessionActiveRef.current) {
          // Edge case: killMic cleared the session flag mid-recording.
          // No live sends went out (the chain's .then guards on
          // sessionActiveRef and skipped them). Re-send audio_start and
          // replay all buffered chunks before audio_end.
          // #606 — re-declare the wire format so the server's STT
          // session uses the right Deepgram encoding for replayed bytes.
          sessionActiveRef.current = true;
          // #811 — mirror the main audio_start sample_rate-attach logic
          // (~line 2764) so the replay path also tells the server the
          // PCM native sample rate when format='pcm'.
          const _replayMsg = { type: 'audio_start', format: captureFormatRef.current };
          if (captureFormatRef.current === 'pcm') {
            const _sr = pcmCtxRef.current && pcmCtxRef.current.sampleRate;
            if (_sr && Number.isFinite(_sr) && _sr >= 8000 && _sr <= 96000) {
              _replayMsg.sample_rate = _sr;
            }
          }
          ws.send(JSON.stringify(_replayMsg));
          for (const chunk of audioChunksRef.current) {
            if (ws.readyState !== WebSocket.OPEN) break;
            const ab = await chunk.arrayBuffer();
            if (ws.readyState !== WebSocket.OPEN) break;
            ws.send(ab);
          }
        } else {
          // Normal path: chunks streamed live via ondataavailable's send
          // chain. Wait for the chain to drain so all bytes are on the
          // wire before audio_end.
          await (sendChainRef.current ? sendChainRef.current.drain() : Promise.resolve());
        }
        if (ws.readyState === WebSocket.OPEN) {
          // #381 + #382 — payload now carries the per-turn `reason`
          // (vad_silence / vad_speech / no_audio_frames / mic_error)
          // so the server can branch the empty-transcript handler
          // into 3 paths instead of always firing the silence ladder.
          // SNR (#382) and the calibrated VAD threshold ride along
          // for diagnostics.
          ws.send(JSON.stringify(buildAudioEndPayload({
            audioBytes: totalBytes,
            chunkCount: audioChunksRef.current.length,
            vadDetectedSpeech: vadDetectedSpeechRef.current,
            micError: micErrorRef.current,
            calibratedThreshold: vadCalibratedThresholdRef.current,
            turnSnr,
          })));
        }
        sessionActiveRef.current = false;
        // #700 follow-up — when in wait mode the mini-face must stay
        // on 🤫 "Waiting" between non-wake utterances. Pre-fix this
        // unconditionally flipped to 'transcribing' at every audio-end,
        // briefly overwriting the wait-mode label until the server's
        // quiet-drop branch pushed `status:waiting` back. Same trap
        // class as the #696 `setStatus('listening')` guards.
        if (statusRef.current !== 'waiting') {
          setStatus('transcribing'); statusRef.current = 'transcribing';
        }
      } else {
        sessionActiveRef.current = false;
        // True fallback: WS is down — send as single blob on reconnect
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        sendAudioBlob(audioBlob);
      }
    };

    mediaRecorderRef.current.stop();
  }

  // ── VAD volume loop ─────────────────────────────────────────────────────
  function startVADLoop(stream) {
    // Close any previous VAD AudioContext to prevent leaks
    if (vadAudioCtxRef.current) {
      try { vadAudioCtxRef.current.close(); } catch(e) {}
    }
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    vadAudioCtxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    let isSpeaking = false;
    vadDetectedSpeechRef.current = false;

    // VAD strategy (#132): in deepgram_endpointing mode the browser silence
    // timer is suppressed — we wait for the server's `utterance_end` signal.
    // VAD still runs to populate vadDetectedSpeechRef (used by hadRealAudio
    // accounting), and the VAD_MAX_RECORD_MS safety in startListening still
    // applies as an upper bound.
    const silenceFiresLocally = (configRef.current.vad_strategy || 'client_vad') !== 'deepgram_endpointing';

    if (silenceFiresLocally) {
      silenceTimerRef.current = setTimeout(() => {
        console.log('[MIC DEBUG] silence timer fired (initial — never started speaking)');
        stopRecordingAndSend();
      }, (configRef.current.vad_silence_ms || 1800) * 2);
    }

    vadStoppedRef.current = false;

    // #337 — adaptive threshold. Default to the static fallback so the
    // very first frames work even before calibration completes; replace
    // with the calibrated value once VAD_CALIBRATION_MS samples are in.
    let currentThreshold = VAD_SPEECH_THRESHOLD;
    let calibrating = true;
    // #604 — calibration is now a TWO-PHASE process: first wait for
    // silence (consecutive sub-floor samples) so we don't sample
    // mid-speech as "ambient", then run the 400 ms window. waitingForSilence
    // is the first phase; once it flips false the existing 400 ms RMS
    // sampling phase begins.
    let waitingForSilence = true;
    let silentSampleCount = 0;
    const vadStartedAt = Date.now();
    let calibrationStartedAt = Date.now();
    const calibrationSamples = [];

    function loop() {
      if (vadStoppedRef.current) return;  // prevent phantom loops after stopVADLoop
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);

      // #604 — wait-for-silence phase. Defer calibration sampling until
      // we've seen N consecutive sub-floor samples; otherwise the
      // 400 ms window catches the start of the user's reply and
      // inflates the noise-floor estimate above the speech median
      // (Bas's 2026-05-10 cut-off). If silence never arrives within
      // VAD_WAIT_FOR_SILENCE_MAX_MS, bail to the static
      // VAD_SPEECH_THRESHOLD — softens the threshold from the
      // 0.05-capped-mid-speech artifact to a known-safe 0.02.
      if (waitingForSilence) {
        if (rms < VAD_SILENCE_FLOOR_FOR_CALIBRATION) {
          silentSampleCount += 1;
          if (silentSampleCount >= VAD_SILENCE_SAMPLES_REQUIRED) {
            waitingForSilence = false;
            calibrationStartedAt = Date.now();
            // Discard any samples accumulated during the wait phase —
            // they were the trigger for "we have silence", not the
            // calibration data we want.
            calibrationSamples.length = 0;
          }
        } else {
          silentSampleCount = 0;
        }
        if (waitingForSilence && Date.now() - vadStartedAt >= VAD_WAIT_FOR_SILENCE_MAX_MS) {
          // Silence never arrived — user replied immediately. Use the
          // static fallback threshold and skip RMS-based calibration.
          // Guard on `waitingForSilence` so we don't override a
          // just-reached-silence transition that landed near the
          // 800ms boundary.
          waitingForSilence = false;
          calibrating = false;
          currentThreshold = VAD_SPEECH_THRESHOLD;
          // #382 — must still seed vadNoiseFloorRef for SNR calculation
          // at end-of-turn. With no calibration window, we don't have a
          // measured floor; use the static fallback's half as a sane
          // default (matches the existing pathological-silence guard).
          vadNoiseFloorRef.current = VAD_SPEECH_THRESHOLD * 0.5;
          vadSpeechSamplesRef.current = [];
          vadCalibratedThresholdRef.current = currentThreshold;
          console.log(
            `[MIC DEBUG] VAD calibration skipped — no silence within ` +
            `${VAD_WAIT_FOR_SILENCE_MAX_MS}ms (user likely replied immediately). ` +
            `Using fallback threshold ${currentThreshold.toFixed(4)}`
          );
        }
        vadAnimFrameRef.current = requestAnimationFrame(loop);
        return;
      }

      // #337 — calibration phase: collect samples, suppress speech
      // detection until we have a real noise-floor estimate. The
      // initial silence timer (VAD_MAX_RECORD_MS or vad_silence_ms*2)
      // still runs so a totally-silent calibration window doesn't
      // hang the call open.
      if (calibrating) {
        // #604 phase 3 — only count sub-floor samples toward the noise
        // estimate. The wait-for-silence prelude got us to a starting
        // point where ambient was clean; if speech leaks into the
        // 400 ms calibration window (Bas replies ~150 ms in), those
        // samples obviously aren't ambient. Filtering them out keeps
        // p90 anchored to the clean tail of the calibration. If too
        // few clean samples remain at the end, fall back to the
        // static threshold rather than calibrating from a polluted
        // window.
        if (rms < VAD_SILENCE_FLOOR_FOR_CALIBRATION) {
          calibrationSamples.push(rms);
        }
        if (Date.now() - calibrationStartedAt >= VAD_CALIBRATION_MS) {
          calibrating = false;
          // If speech leaked in for most of the window, we have too
          // few clean samples to trust the p90 estimate. Use the
          // static fallback — same path as the "no silence within
          // 800 ms" branch. 5 samples is the minimum that makes the
          // p90 index calculation meaningful.
          if (calibrationSamples.length < 5) {
            currentThreshold = VAD_SPEECH_THRESHOLD;
            vadNoiseFloorRef.current = VAD_SPEECH_THRESHOLD * 0.5;
            vadSpeechSamplesRef.current = [];
            vadCalibratedThresholdRef.current = currentThreshold;
            console.log(
              `[MIC DEBUG] VAD calibration sparse — only ` +
              `${calibrationSamples.length} clean samples in 400ms ` +
              `(speech leaked into window). Using fallback threshold ` +
              `${currentThreshold.toFixed(4)}`
            );
            vadAnimFrameRef.current = requestAnimationFrame(loop);
            return;
          }
          const sorted = [...calibrationSamples].sort((a, b) => a - b);
          const idxP90 = Math.floor(sorted.length * 0.9);
          const p90 = sorted[idxP90] !== undefined ? sorted[idxP90] : VAD_SPEECH_THRESHOLD;
          // #382 — stash the noise floor for SNR calculation at
          // end-of-turn. Floor at the static fallback so a
          // pathologically silent calibration window doesn't divide
          // by ~0 in the SNR ratio.
          vadNoiseFloorRef.current = Math.max(p90, VAD_SPEECH_THRESHOLD * 0.5);
          // Reset the speech-samples buffer for this turn
          vadSpeechSamplesRef.current = [];
          // #604 — combine multiplicative (p90 × 3.0) and additive
          // (p90 + 0.005) candidates; take the LOWER. In noisy rooms
          // multiplicative wins (no change in behaviour). In
          // clean-ambient (wired headset, soft speakers — the
          // elderly demographic) additive dominates and gives ~3×
          // more sensitivity, fixing Bas's 2026-05-10 "she cut me
          // off" report where p90=0.0078 → multiplicative=0.0234
          // blocked his soft speech entirely.
          const _multiplicative = p90 * VAD_CALIBRATION_MULTIPLIER;
          const _additive = p90 + VAD_THRESHOLD_HEADROOM;
          const _candidate = Math.min(_multiplicative, _additive);
          currentThreshold = Math.max(
            VAD_THRESHOLD_MIN,
            Math.min(VAD_THRESHOLD_MAX, _candidate)
          );
          // #381 — stash for the audio_end payload so the server logs
          // can later answer "why did this turn come back empty?".
          vadCalibratedThresholdRef.current = currentThreshold;
          console.log(
            `[MIC DEBUG] VAD threshold calibrated to ${currentThreshold.toFixed(4)} ` +
            `(noise floor p90: ${p90.toFixed(4)}, samples: ${calibrationSamples.length}, ` +
            `mult=${_multiplicative.toFixed(4)} add=${_additive.toFixed(4)})`
          );
        }
        vadAnimFrameRef.current = requestAnimationFrame(loop);
        return;
      }

      if (rms > currentThreshold) {
        // #382 — collect speech-RMS samples for SNR calculation. Only
        // count frames clearly above threshold (this is what
        // 'currentThreshold' guards) so we don't pollute the median
        // with quiet-noise floor frames.
        vadSpeechSamplesRef.current.push(rms);
        if (!isSpeaking) {
          isSpeaking = true;
          vadDetectedSpeechRef.current = true;
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      } else {
        if (isSpeaking) {
          isSpeaking = false;
          if (silenceFiresLocally && !silenceTimerRef.current) {
            // Hybrid mode: silence timer is the SAFETY NET, not the
            // primary turn-end. Use the longer VAD_HYBRID_SAFETY_MS so
            // Deepgram's utterance_end has time to fire first.
            // Pure client_vad: timer is the primary signal — keep
            // the configured vad_silence_ms.
            const _strategy = configRef.current.vad_strategy || 'client_vad';
            const _silenceMs = _strategy === 'hybrid'
              ? VAD_HYBRID_SAFETY_MS
              : (configRef.current.vad_silence_ms || 1800);
            silenceTimerRef.current = setTimeout(() => {
              console.log(`[MIC DEBUG] silence timer fired (post-speech pause, strategy=${_strategy}, ${_silenceMs}ms)`);
              stopRecordingAndSend();
            }, _silenceMs);
          }
        }
      }
      vadAnimFrameRef.current = requestAnimationFrame(loop);
    }

    vadAnimFrameRef.current = requestAnimationFrame(loop);
  }

  // ── Start listening ─────────────────────────────────────────────────────
  async function startListening() {
    // #716 — idempotency. The v1 fix only blocked CONCURRENT calls (one
    // mid-await when another fires). Live staging analysis 2026-05-15
    // showed the bug still triggered for SEQUENTIAL rapid-fire calls
    // ~100 ms apart (server's `done` + `listening` arriving back-to-
    // back, each firing the #124 direct restart, the first one's body
    // completing within the gap and resetting the flag before the
    // second arrives). The strengthened guard:
    //   1. block while mic is actively recording (the mediaRecorder is
    //      the authoritative "we're already listening" signal) — this
    //      kills sequential rapid-fire;
    //   2. keep the in-flight flag for the brief setup window before
    //      mediaRecorder.start() returns — this kills the truly-
    //      concurrent variant.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('[MIC DEBUG #716] mic already recording — skipping duplicate startListening');
      return;
    }
    if (startListeningInFlightRef.current) {
      console.log('[MIC DEBUG #716] startListening already in flight — skipping duplicate call');
      return;
    }
    // #716 follow-up — defensive: clear any leaked VAD_MAX timer from
    // a previous session whose teardown didn't run normally (rare).
    // The new session's own timer is scheduled below.
    if (vadMaxTimerRef.current) {
      clearTimeout(vadMaxTimerRef.current);
      vadMaxTimerRef.current = null;
    }
    startListeningInFlightRef.current = true;
    try {
    console.log('[MIC DEBUG] startListening called, lenaSpeaking:', lenaSpeakingRef.current, 'callState:', callStateRef.current, 'emptyCount:', emptyTurnCountRef.current);
    if (lenaSpeakingRef.current) return;
    if (callStateRef.current !== 'connected') return;
    const s = statusRef.current;
    if (s === 'transcribing' || s === 'thinking' || s === 'speaking') return;

    // Don't open the mic while a video or music is playing on the tablet.
    // Otherwise:
    //   - The user's mic catches the video's audio (Deepgram garbage).
    //   - Any "silence" while watching triggers the silence-trigger ladder
    //     ("Take your time…") on the server — exactly what Bas reported
    //     during a Chaplin clip (he wasn't talking, he was watching).
    // ALSO check pendingVideoRef — between the moment the server sends
    // show_video and the moment AudioPlayer.onDone flushes it, the
    // listening event would otherwise open the mic and trigger the
    // ladder during the brief gap (Bas's "Take your time when video
    // starts" bug from 2026-05-03).
    // #1075 — the SAME race exists for music (given the same #477 deferral
    // pattern): by the time AudioPlayer.onDone restarts the mic it has
    // already played the track and nulled pendingMusicRef, while `nowPlaying`
    // React state hasn't re-rendered yet. Without the synchronous
    // musicIsLive() read (and pendingMusicRef for the not-yet-played gap) the
    // mic reopened into the just-started song and the server spoke "dat kwam
    // er niet goed door".
    if (videoOverlay || nowPlaying || musicIsLive() ||
        pendingVideoRef.current || pendingMusicRef.current) {
      // Note: photoCarousel does NOT block the mic — photos are silent
      // and the user often wants to talk about what they see. Server-side
      // silence-tolerance during photo viewing handles the "user is just
      // looking quietly" case (see photo_opened/photo_closed signals).
      console.log('[MIC DEBUG] startListening deferred — media is playing or pending');
      return;
    }

    // Don't open the mic while Berta's buffered audio is still playing.
    // The server now sends 'listening' as soon as the LAST sentence's WAV
    // is dispatched (Azure REST + pipelining), which happens ~10s before
    // the tablet's AudioPlayer finishes playing the buffered queue. If we
    // started the mic here, it would catch Berta's own voice through the
    // user's headset and ship garbage to STT. The AudioPlayer onDone
    // callback retries startListening once playback actually finishes.
    if (audioPlayerRef.current && audioPlayerRef.current.isPlaying()) {
      console.log('[MIC DEBUG] startListening deferred — audio still playing');
      return;
    }

    // Clear previous turn's transcript so each turn starts fresh (#75).
    // Do NOT clear photo carousel / video overlay / music here — the user
    // should dismiss media themselves (X button or asking Berta to close
    // it). Auto-closing on every new turn was too aggressive and made
    // photo galleries flash and disappear within seconds.
    setTranscript('');
    // #1032 — do NOT clear `reply` here anymore. Mic-open starts the user's
    // turn; wiping Berta's words now killed the re-read (#428) the instant
    // they began thinking. Her reply now persists until her NEXT reply's
    // first chunk resets it (see the reply_chunk handler). With the
    // transcript cleared above, the re-read pause (empty transcript) shows
    // her last reply rather than the user's stale words.

    // Resume AudioContext if suspended (e.g. after OS power-save or tab backgrounding)
    if (audioPlayerRef.current) audioPlayerRef.current.warmUp();

    stopVADLoop();

    try {
      // #402 — if we have a soft-muted stream alive from the previous
      // turn, reuse it instead of acquiring a fresh one. This is the
      // BT-headset-friendly path: the OS keeps the headset in HFP the
      // whole call, no profile switch, no audible click.
      let stream;
      const keepAlive = shouldKeepMicAlive();
      const existingStream = vadStreamRef.current;
      const existingTracksLive = existingStream &&
        existingStream.getTracks().every(t => t.readyState === 'live');
      if (keepAlive && existingTracksLive) {
        console.log('[MIC DEBUG #402] reusing soft-muted stream (BT profile preserved)');
        existingStream.getTracks().forEach(t => { t.enabled = true; });
        stream = existingStream;
      } else {
        // No live stream OR feature flag off — fresh getUserMedia.
        // Stop any leftover tracks first to be safe.
        if (existingStream) {
          existingStream.getTracks().forEach(t => t.stop());
          vadStreamRef.current = null;
        }
        stream = await navigator.mediaDevices.getUserMedia({
          // #601 + #811 — `autoGainControl` and `noiseSuppression`
          // FLIPPED ON for the hands-free tablet case. Bas's 2026-05-18
          // staging repro produced raw AudioWorklet PCM at RMS ~200-800
          // (vs ~3000-6000 for normal speech) because the user sits 1-2
          // meters from the tablet mic and the raw signal arrives much
          // quieter than the headset-close-talk case #601 was tuned for.
          // Without AGC there's no codec-side compensation either
          // (the PCM path bypasses Opus's internal loudness norm).
          // Deepgram then transcribed mostly empty results.
          //
          // The #601 trade-off (AGC corrupting #382 SNR + NS adding
          // "tin" colour on clean headset input) is real for the
          // headset case — that lives behind a per-user
          // `audio_capture_profile = 'headset'` toggle (planned
          // follow-up: Portal + tablet Settings UI). Default profile
          // 'tablet_speaker' uses these AGC-on settings; that's the
          // dominant case for elderly users and the only fix that
          // makes hands-free actually work.
          //
          // `echoCancellation` stays on — speakerphone playback into
          // the same mic needs cancellation in either profile.
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        console.log('[MIC DEBUG #402] fresh getUserMedia stream acquired (AGC/NS off — #601)');
        vadStreamRef.current = stream;
      }

      // #606 (2026-05-11 lifecycle rework) — AudioWorklet PCM capture
      // is on by default but only when the CALL-LEVEL AudioContext
      // bootstrapped by handleCall() is healthy. If the watchdog has
      // flipped `pcmDegradedRef` true (suspended-context resume
      // attempts exhausted) OR if the context was never successfully
      // created at call start, this turn falls back to MediaRecorder.
      //
      // Per-tablet escape hatch:
      //   localStorage.disable_pcm_capture = 'true'
      // is honoured in handleCall (skips context creation entirely
      // so pcmCtxRef stays null + pcmDegradedRef goes true).
      const pcmEnabled = !pcmDegradedRef.current && pcmCtxRef.current != null;
      audioChunksRef.current = [];
      // #381 — capture mic-side faults so audio_end can carry the
      // mic_error reason and the server takes the re-prompt path
      // (instead of silently firing the silence ladder).
      micErrorRef.current = false;

      // Fresh send chain for this recording session.
      sendChainRef.current = createSendChain({
        getWs: () => wsRef.current,
        isSessionActive: () => sessionActiveRef.current,
      });

      let captureFormat = 'webm';
      let mediaRecorder = null;

      if (pcmEnabled) {
        try {
          // #606 (2026-05-11) — pass the call-level AudioContext so this
          // turn builds its source + worklet graph ON TOP of the shared
          // context. stop() at end of turn only tears down the per-turn
          // nodes; the context stays alive for the next turn. Watchdog
          // (in handleCall) monitors the context's health across turns
          // and flips pcmDegradedRef if it can't keep the context
          // running, so subsequent turns naturally fall back here.
          const pcmCapture = await createPcmCapture(stream, {
            audioContext: pcmCtxRef.current,
            onChunk: (blob) => {
              audioChunksRef.current.push(blob);
              if (sendChainRef.current) {
                sendChainRef.current.enqueue(blob);
              }
              // #897 — mark the very first mic chunk we ship to the
              // server (PCM path). The collector's first-wins semantics
              // keep this scoped to turn 1 of the call.
              if (warmupTimingsRef.current) {
                warmupTimingsRef.current.mark('first_audio_chunk');
              }
            },
            onError: (err) => {
              micErrorRef.current = true;
              console.warn('[MIC DEBUG #606] PCM capture error:', err);
              // If the worklet itself errors out, flip degraded so
              // the next turn skips PCM. The error is more likely a
              // permanent worklet failure than a transient blip.
              pcmDegradedRef.current = true;
            },
          });
          if (pcmCapture) {
            mediaRecorder = pcmCapture;
            captureFormat = 'pcm';
            console.log('[MIC DEBUG #606] using AudioWorklet PCM capture (call-level context)');
          } else {
            // createPcmCapture returned null — e.g. the borrowed context
            // failed its last-second health check. Flip degraded so we
            // don't keep retrying every turn.
            pcmDegradedRef.current = true;
            console.warn('[MIC DEBUG #606] createPcmCapture returned null — PCM degraded for rest of call');
          }
        } catch (err) {
          console.warn('[MIC DEBUG #606] PCM capture init failed; falling back to MediaRecorder:', err);
          pcmDegradedRef.current = true;
        }
      }

      if (!mediaRecorder) {
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';

        mediaRecorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);

        mediaRecorder.onerror = (ev) => {
          micErrorRef.current = true;
          console.warn('[MIC DEBUG #381] MediaRecorder.onerror fired:', ev?.error || ev);
        };

        let isFirstChunk = true;
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            // First chunk contains the WebM codec init segment (header) — store it
            if (isFirstChunk) {
              isFirstChunk = false;
              webmHeaderRef.current = e.data;
              if (e.data.size < 100) {
                console.warn(`WebM header chunk too small (${e.data.size} bytes), may be missing init segment`);
              }
            }
            audioChunksRef.current.push(e.data);
            // Stream live via the serialised chain. The chain handles the
            // ws-state / session-active / await-race guards internally.
            if (sendChainRef.current) {
              sendChainRef.current.enqueue(e.data);
            }
            // #897 — mark first chunk shipped (MediaRecorder path).
            // Symmetric with the PCM-path mark above so the warm-up
            // dump has the same shape on both branches.
            if (warmupTimingsRef.current) {
              warmupTimingsRef.current.mark('first_audio_chunk');
            }
          }
        };

        mediaRecorder.onerror = (e) => {
          console.error('MediaRecorder error:', e);
          setMicWarning(scr.micwarning.generic);
        };
      }

      mediaRecorderRef.current = mediaRecorder;
      captureFormatRef.current = captureFormat;
      // #606 — surface the actually-used audio mode in the UI footer
      // so Bas can verify which path is live without DevTools.
      setActiveAudioMode(captureFormat);

      // Send audio_start BEFORE starting MediaRecorder to guarantee server
      // receives the session open signal before any binary chunks arrive.
      // #606 — declare the wire format so the server picks the right
      // Deepgram encoding params (raw PCM vs WebM autodetect).
      // #811 — when format=pcm-linear16, also include the AudioContext's
      // native sample rate so the server tells Deepgram the right
      // `sample_rate=` URL param. Pre-#811 the client decimated to
      // 16 kHz before sending (with broken aliasing on tablets that
      // refused the 16 kHz AudioContext construction); now the worklet
      // sends native-rate PCM unchanged and Deepgram resamples its end.
      // For format=webm the field is omitted (MediaRecorder + Opus
      // contains its own rate in the container, Deepgram autodetects).
      const wsNow = wsRef.current;
      if (wsNow && wsNow.readyState === WebSocket.OPEN) {
        sessionActiveRef.current = true;
        const _audioStartMsg = { type: 'audio_start', format: captureFormat };
        // #811 — captureFormat values are 'pcm' (AudioWorklet) or 'webm'
        // (MediaRecorder). Only attach sample_rate on the PCM path —
        // WebM/Opus carries its rate inside the container, Deepgram
        // autodetects.
        if (captureFormat === 'pcm') {
          const _sr = pcmCtxRef.current && pcmCtxRef.current.sampleRate;
          if (_sr && Number.isFinite(_sr) && _sr >= 8000 && _sr <= 96000) {
            _audioStartMsg.sample_rate = _sr;
          }
        }
        wsNow.send(JSON.stringify(_audioStartMsg));
      } else {
        ensureWS();
      }

      // PCM capture is already running by the time createPcmCapture()
      // returned; MediaRecorder needs an explicit start(1000ms-chunks).
      if (captureFormat === 'webm') {
        mediaRecorder.start(1000);
      }
      setIsRecording(true);
      setStatus('listening'); statusRef.current = 'listening';
      speechStartRef.current = Date.now();

      // Hands-free only — VAD always runs (#1090)
      startVADLoop(stream);
      // #716 follow-up — store the handle so teardown paths can
      // clearTimeout it. Without this, a 60s timer scheduled here
      // would fire ~60s later regardless of whether the recording
      // session it was protecting is still active, killing an
      // unrelated downstream session.
      if (vadMaxTimerRef.current) clearTimeout(vadMaxTimerRef.current);
      vadMaxTimerRef.current = setTimeout(() => {
        vadMaxTimerRef.current = null;
        if (statusRef.current === 'listening') {
          console.log('[MIC DEBUG] VAD_MAX_RECORD_MS hit (60s hard cap)');
          stopRecordingAndSend();
        }
      }, VAD_MAX_RECORD_MS);
    } catch (err) {
      console.error('Mic error:', err);
      setStatus('error'); statusRef.current = 'error';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicWarning(scr.micwarning.accessDenied);
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setMicWarning(scr.micwarning.notFound);
      } else {
        setMicWarning(scr.micwarning.generic);
      }
      setTimeout(() => { setStatus('idle'); statusRef.current = 'idle'; setTranscript(''); }, 4000);
    }
    } finally {
      // #716 — match the try{ at the top of startListening. Fires on
      // every exit (early return, success, throw) so a subsequent
      // call is welcome immediately.
      startListeningInFlightRef.current = false;
    }
  }

  startListeningRef.current = startListening;

  // ── Unified AudioPlayer onDone callback (#623) ─────────────────────────
  // Lives at the top of the component so the same `<AudioPlayer>` element
  // can be rendered at the SAME position (first child of
  // `<div style={styles.container}>`) in both the CALLING and CONNECTED
  // branches. React reconciles same-type-same-position elements as the
  // same instance, so the AudioPlayer (and its AudioContext) stays
  // mounted across the calling→connected transition that fires on the
  // first greeting chunk.
  //
  // Pre-#623 the AudioPlayer was rendered in two separate branch returns
  // with different positions and slightly different onDone bodies. The
  // transition unmounted the calling-branch instance and mounted a fresh
  // connected-branch one. `AudioContext.close()` is async — for the
  // ~50-100ms it takes to actually silence the old context's hardware
  // stream, BOTH contexts were alive, each playing greeting bytes with a
  // small offset. The result was the audible voice-doubling Bas
  // reported: "two instances speaking for a short second with a small
  // delay between them". Body below is the union of both old onDone
  // handlers (the connected version was a superset; the calling version
  // didn't need the pending-video/domino/music flush blocks because those
  // are only ever set on user-reply turns).
  const handleAudioPlayerDone = () => {
    // #297 — quota-declined drain. If the WS already closed with
    // quota_exceeded, this onDone is the signal that the resting
    // message has finished playing — NOW transition to idle.
    if (quotaExceededRef.current) {
      quotaExceededRef.current = false;
      lenaSpeakingRef.current = false;
      firstAudioReceivedRef.current = false;
      audioServerDoneRef.current = false;
      audioChunksReceivedRef.current = 0;
      setCallState('idle'); callStateRef.current = 'idle';
      setStatus('idle'); statusRef.current = 'idle';
      setReply('');
      return;
    }
    if (!audioServerDoneRef.current) return;
    lenaSpeakingRef.current = false;
    audioServerDoneRef.current = false;
    audioChunksReceivedRef.current = 0; // #124: reset so next turn's listening/done handler takes correct path
    // #1075 — capture, BEFORE the flushes below consume the pending refs,
    // whether music/video is about to start (or is already playing). The
    // mic-restart at the end of this handler must NOT reopen the mic into a
    // playing track / opening clip: the media bleeds into the open mic,
    // Deepgram returns empty, and the server speaks a spurious "didn't catch
    // that" reprompt. Synchronous reads only — `nowPlaying` / `videoOverlay`
    // React state is still stale in this same tick (setNowPlaying /
    // setVideoOverlay were only just queued).
    const _mediaWillBlockMic =
      !!pendingVideoRef.current || !!pendingMusicRef.current || musicIsLive();
    // Flush any pending video now that Berta has finished speaking
    // (#91 timing fix from 2026-05-03 testing). The video starts
    // AFTER her last word, not in the middle of her sentence.
    if (pendingVideoRef.current) {
      setVideoOverlay(pendingVideoRef.current);
      pendingVideoRef.current = null;
    }
    // #390 PR C-3 — flush any pending domino-game overlay
    // ditto: open the board AFTER Berta finishes her spoken
    // acceptance ("let me deal the tiles"), not during it.
    if (pendingDominoRef.current) {
      setDominoState(pendingDominoRef.current);
      pendingDominoRef.current = null;
    }
    // #966 — same flush for the Solitaire overlay.
    if (pendingSolitaireRef.current) {
      setSolitaireState(pendingSolitaireRef.current);
      pendingSolitaireRef.current = null;
    }
    // #1029 — same flush for the game-picker carousel. Open it AFTER
    // Berta finishes her "here's what we can play" line, not during it.
    if (pendingGamesMenuRef.current) {
      setGamesMenuState(pendingGamesMenuRef.current);
      pendingGamesMenuRef.current = null;
    }
    // #477 — flush any pending play_music. Same timing rule as
    // video/domino: the player only opens / starts after Berta
    // has finished speaking the sentence that introduced it
    // ("here's something I think you'll like"), not during it.
    if (pendingMusicRef.current && musicPlayerRef.current) {
      const m = pendingMusicRef.current;
      pendingMusicRef.current = null;
      musicPlayerRef.current.play(m.youtube_id, m.title);
    }
    const isInCall = callStateRef.current === 'connected' || callStateRef.current === 'calling';
    if (isInCall &&
        startListeningRef.current) {
      if (callStateRef.current === 'calling') {
        setCallState('connected'); callStateRef.current = 'connected';
        if (ringToneRef.current) { ringToneRef.current.stop(); ringToneRef.current = null; }
      }
      pendingMicStartRef.current = false;
      stopVADLoop();
      // #696 — when in wait mode (status='waiting' was set by the
      // server's `status: waiting` push immediately before this
      // AudioPlayer drain), DON'T overwrite to 'listening'. The
      // VAD restart below still runs so the wake-name can be
      // captured; we just keep the 🤫 "Waiting" face visible so
      // the user knows Berta is in silent-listening mode.
      if (statusRef.current !== 'waiting') {
        setStatus('listening'); statusRef.current = 'listening';
      }
      // #1075 — Berta's reply that introduces music/video has finished and the
      // track/clip is now starting (or already playing). Hold the mic closed:
      // reopening it here is exactly what let the media bleed into the mic and
      // triggered the spurious "didn't catch that" reprompt. Setting status
      // 'listening' above is still correct — it resumes the (now un-ducked)
      // music via the [status] effect; we just don't open the mic. The mic
      // re-opens when the media is dismissed (the [videoOverlay, nowPlaying]
      // effect above).
      if (_mediaWillBlockMic) {
        console.log('[MIC #1075] media starting — holding mic closed until media dismissed');
      } else {
        startListeningRef.current();
      }
    }
  };

  // ── Call button: start the call ─────────────────────────────────────────
  const handleCall = useCallback(() => {
    if (callStateRef.current !== 'idle') return;

    setCallState('calling');
    callStateRef.current = 'calling';
    callStartTimeRef.current = Date.now();
    firstAudioReceivedRef.current = false;
    wsReadyRef.current = false;
    reassuranceSentRef.current = false;
    emptyTurnCountRef.current = 0;
    vadDetectedSpeechRef.current = false;
    // callTypeRef is set by the caller (handleCall tap = 'normal', handleAnswerIncoming = trigger_type)

    // #897 — start the warm-up timings collector. handleCall fires
    // under the user-gesture tap; this is the t0 reference point
    // against which every other warm-up timestamp is reported.
    warmupTimingsRef.current = createWarmupTimings();

    // #897 — Pre-warm `getUserMedia` during the "Berta is connecting…"
    // UI window. Without this, mic acquisition happened lazily inside
    // startListening (turn-1 hot path) and the first ~100-200 ms of
    // captured PCM carried the browser's getUserMedia cold-start
    // transient (DC offset / preamp click / AGC ramp). The audible
    // click was reported by Bas on every recorded turn during the
    // 2026-05-22 retest. Pre-warming here means by the time the
    // user's first utterance reaches the worklet, the audio graph
    // has been live for ~MIN_RING_MS (currently 3 s) — well past
    // the cold-start window.
    //
    // The stream is stashed in `vadStreamRef.current` so
    // `startListening` picks it up via its existing `keepAlive &&
    // existingTracksLive` branch (the same path #402 added for the
    // BT-headset profile-preservation case). If the user hangs up
    // before ever speaking, `cleanup() → killMic` stops the tracks.
    //
    // Constraints honoured:
    //   - Mic permission must already be granted at this point. If
    //     getUserMedia would have to prompt, we deliberately swallow
    //     the rejection and let startListening retry at the natural
    //     moment — never trigger a permission prompt during the
    //     connecting screen (the elderly user would not know what to
    //     do with it). Issue #897's "permission must be granted at
    //     install / pairing" guidance.
    //   - Constraints match startListening's getUserMedia call site
    //     EXACTLY so the soft-mute keep-alive path treats the stream
    //     as reusable (same echoCancellation / noiseSuppression /
    //     autoGainControl tuple). Drift here would split the cache.
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function' &&
        !vadStreamRef.current // honour already-alive stream if reattached
      ) {
        navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }).then((stream) => {
          if (warmupTimingsRef.current) {
            warmupTimingsRef.current.mark('getUserMedia_resolved');
          }
          // If the user already hung up while getUserMedia was
          // resolving (rare — getUserMedia typically resolves in
          // <100ms when permission is granted, but PWA install
          // freshness can stretch it), tear the stream down rather
          // than leak the mic.
          if (callStateRef.current === 'idle') {
            stream.getTracks().forEach(t => t.stop());
            return;
          }
          // Don't clobber a stream startListening already acquired
          // (defensive against race with a manual mic-restart path).
          if (vadStreamRef.current) {
            stream.getTracks().forEach(t => t.stop());
            return;
          }
          vadStreamRef.current = stream;
          console.log('[warmup #897] getUserMedia pre-warmed during connecting screen');
        }).catch((e) => {
          // Most common reason: permission not granted yet, or another
          // tab/app holds the mic. Either way the lazy path in
          // startListening will surface the error to the user as
          // before — pre-warm is best-effort observability + latency
          // optimisation, not a correctness path.
          console.log('[warmup #897] getUserMedia pre-warm skipped:', e?.name || e);
        });
      }
    } catch (e) {
      // Same — never break call setup over the pre-warm attempt.
      console.log('[warmup #897] getUserMedia pre-warm threw:', e?.name || e);
    }

    // #606 (2026-05-11) — bootstrap the call-level AudioContext inside
    // THIS user-gesture handler. Browser autoplay policy requires the
    // context to start under a fresh gesture; creating it here (rather
    // than per-turn inside startListening) means every later turn gets
    // a context that's already running. PCM path stays healthy across
    // arbitrarily many turns.
    //
    // Honours the per-tablet escape hatch: `localStorage.disable_pcm_capture
    // = 'true'` skips PCM init entirely and forces MediaRecorder path.
    pcmDegradedRef.current = false;
    pcmResumeFailCountRef.current = 0;
    const pcmEnabled = window.localStorage.getItem('disable_pcm_capture') !== 'true';
    if (pcmEnabled) {
      createPcmAudioContext().then(ctx => {
        if (ctx) {
          pcmCtxRef.current = ctx;
          // #897 — mark the moment the call-level AudioContext is
          // ready (state=running, worklet module loaded). This is the
          // gate between "AudioContext alive" and "first turn can
          // actually capture PCM" — together with getUserMedia_resolved
          // and ws_open it forms the warm-up triplet we tune against.
          if (warmupTimingsRef.current) {
            warmupTimingsRef.current.mark('pcm_audio_context_ready');
          }
          // Start watchdog — every 1s, check state and attempt
          // resume if drifted. After PCM_WATCHDOG_MAX_FAILS consecutive
          // failed resumes, flip pcmDegradedRef so future turns use
          // MediaRecorder for the rest of the call.
          const PCM_WATCHDOG_INTERVAL_MS = 1000;
          const PCM_WATCHDOG_MAX_FAILS = 3;
          if (pcmWatchdogRef.current) clearInterval(pcmWatchdogRef.current);
          pcmWatchdogRef.current = setInterval(async () => {
            const c = pcmCtxRef.current;
            if (!c) return;
            if (c.state === 'closed') {
              pcmCtxRef.current = null;
              pcmDegradedRef.current = true;
              if (pcmWatchdogRef.current) {
                clearInterval(pcmWatchdogRef.current);
                pcmWatchdogRef.current = null;
              }
              console.warn('[pcm-watchdog] context closed unexpectedly — degraded for rest of call');
              return;
            }
            if (c.state === 'suspended') {
              try {
                await c.resume();
                if (c.state === 'running') {
                  console.info(`[pcm-watchdog] recovered suspended context (fails so far=${pcmResumeFailCountRef.current})`);
                  pcmResumeFailCountRef.current = 0;
                } else {
                  pcmResumeFailCountRef.current += 1;
                }
              } catch (e) {
                pcmResumeFailCountRef.current += 1;
                console.warn(`[pcm-watchdog] resume failed (${pcmResumeFailCountRef.current}/${PCM_WATCHDOG_MAX_FAILS}):`, e);
              }
              if (pcmResumeFailCountRef.current >= PCM_WATCHDOG_MAX_FAILS) {
                pcmDegradedRef.current = true;
                console.warn(
                  `[pcm-watchdog] giving up after ${PCM_WATCHDOG_MAX_FAILS} resume failures ` +
                  `— PCM degraded, MediaRecorder will be used for rest of call`
                );
                if (pcmWatchdogRef.current) {
                  clearInterval(pcmWatchdogRef.current);
                  pcmWatchdogRef.current = null;
                }
              }
            }
          }, PCM_WATCHDOG_INTERVAL_MS);
        } else {
          // createPcmAudioContext returned null — browser doesn't support
          // AudioWorklet, module load failed, or context can't run. Mark
          // degraded; subsequent turns use MediaRecorder transparently.
          pcmDegradedRef.current = true;
          console.warn('[pcm] call-level context init failed — using MediaRecorder for this call');
        }
      }).catch(e => {
        pcmDegradedRef.current = true;
        console.warn('[pcm] call-level context init threw:', e);
      });
    } else {
      // Escape hatch active — PCM disabled per-tablet.
      pcmDegradedRef.current = true;
    }

    // Start ring tone — only for user-initiated outgoing calls.
    // Incoming calls (reminder / wellbeing / inactivity / weekly) reach
    // this point via handleAnswerIncoming → handleCall, with
    // callTypeRef already set to the trigger type. Playing a
    // calling-out tone after the user just answered is wrong; they
    // should hear nothing until Berta speaks. (#193)
    if (callTypeRef.current === 'normal') {
      ringToneRef.current = startRingTone();
    }

    // Connect WS in background — transition happens when first audio arrives
    ensureWS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ── Hang up (double-tap confirmation) ────────────────────────────────────
  const doHangUp = useCallback(() => {
    if (hangUpTimerRef.current) { clearTimeout(hangUpTimerRef.current); hangUpTimerRef.current = null; }
    hangUpPendingRef.current = false;
    setHangUpPending(false);
    // #449 — explicit hangup signal BEFORE cleanup() closes the WS.
    // Lets the server set end_reason='user_hangup' authoritatively
    // instead of falling back to ws_abnormal / unknown_disconnect.
    try {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        // #897 — dump warm-up timings before the flush so this
        // call's timings ride the same batched send.
        dumpWarmupTimings();
        // #718 — flush captured console logs over the WS BEFORE the
        // hangup message + cleanup. After this point the WS is
        // closing; the flush won't go through. The flush is a single
        // batched JSON send so it's atomic — either lands or doesn't.
        try { _flushConsoleCapture(ws); } catch (_) { /* swallow */ }
        ws.send(JSON.stringify({ type: 'hangup' }));
      }
    } catch (_) { /* non-fatal — server still gets WS close */ }
    // #897 — also dump on the WS-not-open path so the timing isn't
    // dropped on the floor when the ring buffer can't be shipped.
    // The local console line is still useful for in-tablet debug
    // even when the batch send fails.
    dumpWarmupTimings();
    cleanup();

    // Reset state
    setCallState('idle');
    callStateRef.current = 'idle';
    setStatus('idle'); statusRef.current = 'idle';
    // #700 follow-up — clear wait-mode state on hangup so the next
    // call starts fresh (button shows pause, not play). Without this,
    // a user who hung up while in wait mode would see the play icon
    // on their next call until the server pushed a fresh
    // wait_mode_state event.
    setWaitMode(false);
    waitModeRef.current = false;
    setTranscript('');
    // #1032 — reset the focal speaker (next call opens on Berta's greeting)
    // and the "has replied" flag (so the pre-greeting placeholder shows again).
    setFocalSpeaker('companion');
    setCallHasReply(false);
    // #405 — clear the YouSaidCard's frozen utterance on hangup so the
    // next call starts blank, not with the previous call's last words.
    setFrozenUtterance('');
    // #381 — clear any lingering connection-hiccup indicator.
    setConnectionHiccup(null);
    setReply('');
    setIsRecording(false);
    setMicWarning(null);
    emptyTurnCountRef.current = 0;
    gotReplyRef.current = false;
    hadRealAudioRef.current = false;
    reassuranceSentRef.current = false;
    vadDetectedSpeechRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHangUp = useCallback(() => {
    doHangUp();
  }, [doHangUp]);

  // #1058 — the ONLY user-initiated call entry point (the idle "Call"
  // button). Wraps handleCall with the post-hangup cooldown guard so an
  // accidental second tap right after a hang-up can't start a phantom
  // 0-turn call. The proactive-call ANSWER path (handleAnswerIncoming)
  // deliberately bypasses this — an incoming reminder/wellbeing call
  // arriving right after a call must still connect.
  const startUserCall = useCallback(() => {
    if (isWithinReDialCooldown(lastCallEndedAtRef.current, Date.now())) {
      // Stray tap inside the cooldown — swallow it. The button is also
      // visually disabled via `callJustEnded`; this is belt-and-braces
      // for any synthetic / residual touch event that bypasses the
      // disabled attribute (the phantom WS opened ~180 ms after hangup).
      return;
    }
    callTypeRef.current = 'normal';
    pendingCallIdRef.current = null;
    handleCall();
  }, [handleCall]);

  // ── Pause / Resume button (#700) ─────────────────────────────────────────
  // Sends `{type:'pause_toggle', active:<bool>}` to the server. The server
  // routes it through receive_task → _enter_wait_mode/_exit_wait_mode and
  // pushes back the corresponding `wait_mode_state`/`status` events that
  // the rest of this component already handles. We don't optimistically
  // flip the local status here — the server is the source of truth (same
  // pattern as music_started/stopped). The button is only meaningful
  // during an active call; we no-op otherwise.
  //
  // #918 (2026-05-24) — additionally mute the MediaStreamTrack via
  // `track.enabled = false` on entry / `true` on exit. Pre-#918 the
  // pause button only flipped a server-side flag — getUserMedia kept
  // capturing, the worklet kept producing frames, frames kept flowing
  // to Deepgram, and Deepgram transcribed ambient noise as the most
  // likely-from-context Dutch word ("nee nee nee nee" → polluted the
  // post-unpause first utterance). The #897 pre-warm pattern made this
  // worse because pre-#897 the mic was acquired lazily on first user
  // turn and pause naturally closed the stream. We MUST NOT undo
  // #897 (the pre-warm fixes a real first-frame click bug). Instead,
  // toggling `track.enabled` is the cheap, correct gate: the
  // MediaStreamTrack stays alive (BT HFP profile preserved per #402,
  // worklet graph stays warm), but the audio output to consumers
  // (worklet input, MediaRecorder) becomes silent zeros. Deepgram
  // doesn't see ambient noise; it sees real silence (and Deepgram's
  // VAD / endpointer handles silence cleanly).
  //
  // Server-side defense-in-depth covers the case where a tablet
  // misses this mute (old bundle, JS exception path): the WS handler
  // in app/api/voice.py drops `bytes` frames while session_wait_mode
  // is True.
  const handlePauseToggle = useCallback(() => {
    if (callStateRef.current !== 'connected') return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    // Derive the desired active state from `waitModeRef` (the
    // authoritative wait-mode flag driven by the server's
    // `wait_mode_state` lifecycle event). Pre-fix this used
    // `statusRef.current !== 'waiting'`, but `status` briefly leaves
    // 'waiting' on every audio-end (line ~1880 sets 'transcribing'),
    // so a tap during that window would mis-classify the intent.
    // Already-waiting → tap means "resume" (active=false). Otherwise
    // → tap means "enter wait mode" (active=true).
    const goActive = !waitModeRef.current;
    // #918 — flip track.enabled BEFORE the WS send so by the time the
    // server's pause_toggle handler engages, the client has already
    // stopped emitting frames. The tracks live on `vadStreamRef`,
    // which is the same handle used by softMuteMic (#402) and the
    // #897 pre-warmed stream. Idempotent: setting enabled to its
    // current value is a no-op per the MediaStreamTrack spec.
    try {
      const stream = vadStreamRef.current;
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach(t => {
          try { t.enabled = !goActive; } catch (_) {}
        });
        console.log(
          `[MIC #918] pause_toggle: tracks.enabled=${!goActive} ` +
          `(goActive=${goActive}, tracks=${stream.getTracks().length})`
        );
      }
    } catch (e) {
      // Never break the server-side flip over the local mute attempt.
      console.warn('[MIC #918] track.enabled toggle failed (non-fatal):', e);
    }
    try {
      ws.send(JSON.stringify({ type: 'pause_toggle', active: goActive }));
    } catch (_) { /* WS not open — server side stays unchanged */ }
  }, []);

  // ── Incoming call handlers ───────────────────────────────────────────────
  const incomingRingRef = useRef(null);

  // Start ring tone when incoming call appears
  useEffect(() => {
    if (incomingCall && !incomingRingRef.current) {
      incomingRingRef.current = startRingTone();
    }
    return () => {
      if (incomingRingRef.current) {
        incomingRingRef.current.stop();
        incomingRingRef.current = null;
      }
    };
  }, [incomingCall]);

  function handleAnswerIncoming() {
    const call = incomingCall;
    if (!call) return;
    // Stop ring
    if (incomingRingRef.current) {
      incomingRingRef.current.stop();
      incomingRingRef.current = null;
    }
    // Mark answered
    fetch(`/api/calls/${call.id}/answer`, {
      method: 'POST',
      headers: { 'X-User-Id': userId },
    }).catch(() => {});
    setIncomingCall(null);
    // Start the call with the incoming call's trigger type +
    // pending_call_id so the server can wire outcome-driven
    // side-effects on the right reminder (#200 PR-A2).
    callTypeRef.current = call.trigger_type || 'normal';
    pendingCallIdRef.current = call.id || null;
    handleCall();
  }

  function handleDismissIncoming() {
    const call = incomingCall;
    if (!call) return;
    if (incomingRingRef.current) {
      incomingRingRef.current.stop();
      incomingRingRef.current = null;
    }
    fetch(`/api/calls/${call.id}/miss`, {
      method: 'POST',
      headers: { 'X-User-Id': userId },
    }).catch(() => {});
    setIncomingCall(null);
  }

  function pickStyle(value) {
    setCompanionStyle(value);
    localStorage.setItem('companion_style', value);
  }

  // ── Mic test handlers ──────────────────────────────────────────────────
  function handleMicTest() {
    if (micTestState === 'idle' || micTestState === 'result') {
      setMicTestResult(null);
      setMicTestState('recording');
      micTestChunksRef.current = [];
      navigator.mediaDevices.getUserMedia({
        // #811 — match the main voice-call constraints (AGC + NS on for
        // hands-free) so the mic-test measures the same audio path the
        // user will actually hear / produce during a real call. See the
        // #601+#811 comment block at the main getUserMedia call site
        // for the rationale.
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      }).then(stream => {
        micTestStreamRef.current = stream;
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
        const recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
        micTestRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) micTestChunksRef.current.push(e.data);
        };
        recorder.start(500);
      }).catch(() => {
        setMicTestState('result');
        setMicTestResult({ text: '', success: false });
        setTimeout(() => setMicTestState('idle'), 3000);
      });
    } else if (micTestState === 'recording') {
      setMicTestState('checking');
      const recorder = micTestRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = async () => {
          if (micTestStreamRef.current) {
            micTestStreamRef.current.getTracks().forEach(t => t.stop());
            micTestStreamRef.current = null;
          }
          const blob = new Blob(micTestChunksRef.current, { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('audio', blob, 'mic_test.webm');
          try {
            const resp = await fetch('/api/transcribe/test', { method: 'POST', body: formData });
            const data = await resp.json();
            setMicTestResult({ text: data.transcript || '', success: data.success });
          } catch {
            setMicTestResult({ text: '', success: false });
          }
          setMicTestState('result');
          setTimeout(() => setMicTestState('idle'), 3000);
        };
        recorder.stop();
      } else {
        setMicTestState('idle');
      }
    }
  }

  // ── Audio self-test (record-and-playback) handlers ──────────────────────
  // Captures through the live PCM path, accumulates the exact Int16 frames
  // the server would receive, and plays them back locally. Self-contained:
  // it never touches the live-call AudioPlayer or WebSocket plumbing.
  function _teardownSelfTestCapture() {
    selfTestActiveRef.current = false;
    if (selfTestTimerRef.current) {
      clearTimeout(selfTestTimerRef.current);
      selfTestTimerRef.current = null;
    }
    const cap = selfTestCaptureRef.current;
    selfTestCaptureRef.current = null;
    if (cap) { try { cap.stop(); } catch (_) {} }
    if (selfTestStreamRef.current) {
      try { selfTestStreamRef.current.getTracks().forEach(tr => tr.stop()); } catch (_) {}
      selfTestStreamRef.current = null;
    }
    if (selfTestCtxRef.current) {
      try { selfTestCtxRef.current.close(); } catch (_) {}
      selfTestCtxRef.current = null;
    }
  }

  function _teardownSelfTestPlayback() {
    const src = selfTestSrcRef.current;
    selfTestSrcRef.current = null;
    if (src) { try { src.onended = null; src.stop(); } catch (_) {} }
    if (selfTestPlayCtxRef.current) {
      try { selfTestPlayCtxRef.current.close(); } catch (_) {}
      selfTestPlayCtxRef.current = null;
    }
  }

  // Full reset — called when the settings overlay closes so a half-finished
  // recording / playback can't leak a live mic or AudioContext.
  function resetSelfTest() {
    _teardownSelfTestCapture();
    _teardownSelfTestPlayback();
    selfTestChunksRef.current = [];
    setSelfTestMs(0);
    setSelfTestState('idle');
  }

  async function stopSelfTestRecording() {
    selfTestActiveRef.current = false;
    if (selfTestTimerRef.current) {
      clearTimeout(selfTestTimerRef.current);
      selfTestTimerRef.current = null;
    }
    const cap = selfTestCaptureRef.current;
    selfTestCaptureRef.current = null;
    if (cap) { try { await cap.stop(); } catch (_) {} }
    if (selfTestStreamRef.current) {
      try { selfTestStreamRef.current.getTracks().forEach(tr => tr.stop()); } catch (_) {}
      selfTestStreamRef.current = null;
    }
    if (selfTestCtxRef.current) {
      try { await selfTestCtxRef.current.close(); } catch (_) {}
      selfTestCtxRef.current = null;
    }
    const totalBytes = selfTestChunksRef.current.reduce((n, b) => n + (b.size || 0), 0);
    if (totalBytes > 0) {
      setSelfTestMs(pcmDurationMs(totalBytes, selfTestRateRef.current));
      setSelfTestState('recorded');
    } else {
      setSelfTestState('error');
    }
  }

  async function startSelfTestRecording() {
    setSelfTestState('recording');
    setSelfTestMs(0);
    selfTestChunksRef.current = [];
    selfTestActiveRef.current = true;
    // Lock in the mode this clip is captured in so the hint stays honest
    // even if the tester flips the toggle before playing back.
    const rawMode = selfTestRaw;
    selfTestRecordedRawRef.current = rawMode;
    setSelfTestRecordedRaw(rawMode);
    // If the overlay closes (resetSelfTest) or another teardown runs while
    // we're awaiting below, the active flag flips false — bail and clean up
    // so a late-resolving getUserMedia can't re-open a hot mic after close.
    const stillActive = () => selfTestActiveRef.current;
    try {
      // Build the PCM context FIRST, while the record-tap's user-gesture
      // activation is still live — createPcmAudioContext() resumes the
      // context and that resume needs the gesture window (same reason the
      // live call constructs it inside handleCall, before any await). If
      // the browser can't build it, the real call would fall back to WebM;
      // surface that rather than silently testing a different path.
      const ctx = await createPcmAudioContext();
      if (!ctx) { _teardownSelfTestCapture(); setSelfTestState('error'); return; }
      if (!stillActive()) { try { ctx.close(); } catch (_) {} return; }
      selfTestCtxRef.current = ctx;
      selfTestRateRef.current = ctx.sampleRate;
      // Processed mode = the same constraints as the live hands-free call +
      // mic-test panel (#601/#811) so the self-test measures the real
      // capture chain. Raw mode drops EC/NS/AGC for the A/B comparison.
      const stream = await navigator.mediaDevices.getUserMedia(
        selfTestMicConstraints(rawMode)
      );
      if (!stillActive()) {
        try { stream.getTracks().forEach(tr => tr.stop()); } catch (_) {}
        return;  // _teardownSelfTestCapture already closed ctx
      }
      selfTestStreamRef.current = stream;
      // Borrow the exact call-level PCM context + worklet the live path uses
      // → the accumulated frames are byte-identical to what is sent to the
      // server.
      const cap = await createPcmCapture(stream, {
        audioContext: ctx,
        onChunk: (blob) => { selfTestChunksRef.current.push(blob); },
      });
      if (!cap) { _teardownSelfTestCapture(); setSelfTestState('error'); return; }
      if (!stillActive()) { try { cap.stop(); } catch (_) {} return; }
      selfTestCaptureRef.current = cap;
      // Safety cap — a forgotten recording auto-stops at 20 s so the buffer
      // (and the open mic) can't grow unbounded.
      selfTestTimerRef.current = setTimeout(() => { stopSelfTestRecording(); }, 20000);
    } catch (e) {
      console.warn('[self-test] recording failed:', e);
      _teardownSelfTestCapture();
      setSelfTestState('error');
    }
  }

  async function playSelfTestRecording() {
    const chunks = selfTestChunksRef.current;
    if (!chunks || chunks.length === 0) return;
    _teardownSelfTestPlayback();   // defensive — never stack two playbacks
    setSelfTestState('playing');
    try {
      const blob = new Blob(chunks, { type: 'application/octet-stream' });
      const ab = await blob.arrayBuffer();
      const f32 = int16ToFloat32(ab);
      if (f32.length === 0) { setSelfTestState('recorded'); return; }
      const Ctor = window.AudioContext || window.webkitAudioContext;
      const playCtx = new Ctor();
      selfTestPlayCtxRef.current = playCtx;
      if (playCtx.state === 'suspended') { try { await playCtx.resume(); } catch (_) {} }
      // The AudioBuffer carries the RECORDED sample rate; the context
      // resamples on playback exactly as any browser audio would, so what
      // you hear is the captured content with no extra loss from us.
      const buf = playCtx.createBuffer(1, f32.length, selfTestRateRef.current);
      buf.getChannelData(0).set(f32);
      const src = playCtx.createBufferSource();
      selfTestSrcRef.current = src;
      src.buffer = buf;
      src.connect(playCtx.destination);
      src.onended = () => {
        _teardownSelfTestPlayback();
        setSelfTestState('recorded');   // allow replay / re-record
      };
      src.start();
    } catch (e) {
      console.warn('[self-test] playback failed:', e);
      _teardownSelfTestPlayback();
      setSelfTestState('recorded');
    }
  }

  function handleSelfTestRecord() {
    if (selfTestState === 'recording') { stopSelfTestRecording(); return; }
    if (selfTestState === 'playing') { _teardownSelfTestPlayback(); }
    startSelfTestRecording();
  }

  function handleSelfTestPlay() {
    if (selfTestState === 'playing') {
      _teardownSelfTestPlayback();
      setSelfTestState('recorded');
      return;
    }
    playSelfTestRecording();
  }

  // ── Activation screens ──────────────────────────────────────────────────
  // #1120 — explicit setup confirm (shown after opening a ?setup= link,
  // before this device is paired). The deliberate tap prevents a casual
  // preview-open from silently turning the previewing device into the
  // user's tablet, and — with idempotent activation — the link survives
  // being opened more than once.
  if (pendingSetupToken && setupInfo && !activating && !activationError) {
    const forName = (setupInfo.user_name || '').trim();
    const comp = setupInfo.companion_name || 'Amigo Mio';
    return (
      <div style={styles.container}>
        <div style={styles.setupInner}>
          <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" style={styles.setupLogo} />
          <h1 style={styles.setupHeading}>{scr.setup.confirmTitle}</h1>
          <p style={styles.setupText}>
            {forName
              ? formatString(scr.setup.confirmBody, { name: forName, companion: comp })
              : scr.setup.confirmBodyNoName}
          </p>
          <button style={styles.setupConfirmBtn} onClick={confirmSetup}>
            {scr.setup.confirmButton}
          </button>
          <button style={styles.setupCancelBtn} onClick={cancelSetup}>
            {scr.setup.confirmCancel}
          </button>
        </div>
        <div style={styles.version}>v{VERSION}</div>
      </div>
    );
  }

  if (activating) {
    return (
      <div style={styles.container}>
        <div style={styles.setupInner}>
          <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" style={styles.setupLogo} />
          <h1 style={styles.setupHeading}>{scr.setup.activatingTitle}</h1>
          <p style={styles.setupText}>{scr.setup.activatingBody}</p>
        </div>
        <div style={styles.version}>
          v{VERSION}
          {activeAudioMode && (
            <> · audio: <span style={{ fontWeight: 600 }}>{activeAudioMode === 'pcm' ? 'PCM' : 'WebM'}</span></>
          )}
        </div>
      </div>
    );
  }

  if (activationError) {
    return (
      <div style={styles.container}>
        <div style={styles.setupInner}>
          <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" style={styles.setupLogo} />
          <h1 style={styles.setupHeading}>{scr.setup.failedTitle}</h1>
          <p style={styles.setupText}>{activationError}</p>
          <p style={styles.setupText}>
            {/* #551 — translation includes {strongOpen}/{strongClose}
                markers so the inline <strong> emphasis survives
                localisation. _renderWithStrong splits the template
                and inserts a real <strong> element. */}
            {_renderWithStrong(scr.setup.failedBody)}
          </p>
          <div style={styles.setupUrl}>
            {/* #711 — derive from current host so the staging tablet
                shows staging.amigomio.app/portal, not the production URL. */}
            {(typeof window !== 'undefined' && window.location && window.location.host)
              ? `${window.location.host}/portal`
              : 'amigomio.app/portal'}
          </div>
        </div>
        <div style={styles.version}>
          v{VERSION}
          {activeAudioMode && (
            <> · audio: <span style={{ fontWeight: 600 }}>{activeAudioMode === 'pcm' ? 'PCM' : 'WebM'}</span></>
          )}
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div style={styles.container}>
        <div style={styles.setupInner}>
          <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" style={styles.setupLogo} />
          <h1 style={styles.setupHeading}>{scr.setup.notSetUpTitle}</h1>
          <p style={styles.setupText}>
            {scr.setup.notSetUpBody}
          </p>
          <div style={styles.setupUrl}>
            {/* #711 — derive from current host so the staging tablet
                shows staging.amigomio.app/portal, not the production URL. */}
            {(typeof window !== 'undefined' && window.location && window.location.host)
              ? `${window.location.host}/portal`
              : 'amigomio.app/portal'}
          </div>
        </div>
        <div style={styles.version}>
          v{VERSION}
          {activeAudioMode && (
            <> · audio: <span style={{ fontWeight: 600 }}>{activeAudioMode === 'pcm' ? 'PCM' : 'WebM'}</span></>
          )}
        </div>
      </div>
    );
  }

  // ── INCOMING CALL screen ─────────────────────────────────────────────────
  if (incomingCall && callState === 'idle') {
    return (
      <div style={styles.container}>
        <div style={styles.callingInner}>
          <CompanionFace status="idle" style={effectiveStyle} companionName={companionName} />

          <p style={styles.incomingLabel}>
            {/* #551 — was hardcoded "{companionName} is calling you". */}
            {formatString(scr.incomingScreen.callingYouTemplate, { name: companionName })}
          </p>

          <button onClick={handleAnswerIncoming} style={styles.answerButton}>
            {scr.incomingScreen.answer}
          </button>

          <button onClick={handleDismissIncoming} style={styles.dismissLink}>
            {scr.incomingScreen.notNow}
          </button>
        </div>
        <div style={styles.version}>
          v{VERSION}
          {activeAudioMode && (
            <> · audio: <span style={{ fontWeight: 600 }}>{activeAudioMode === 'pcm' ? 'PCM' : 'WebM'}</span></>
          )}
        </div>
        <style>{`
          @keyframes incomingPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.4); }
            50% { box-shadow: 0 0 0 20px rgba(16,185,129,0); }
          }
        `}</style>
      </div>
    );
  }

  // ── IDLE screen ─────────────────────────────────────────────────────────
  if (callState === 'idle') {
    return (
      <div style={styles.container}>
        <div style={styles.idleInner}>
          {/* Long-press refresh — covers the post-deploy gap that the
             #642 auto-reload hook can't reach until the SECOND call. */}
          <LongPressRefreshButton ariaLabel={scr.settings.refreshAria} />

          {/* Settings gear */}
          <button
            onClick={() => setShowSettings(true)}
            style={styles.gearButton}
            aria-label={scr.settings.gearAria}
          >⚙️</button>

          {/* Settings overlay */}
          {showSettings && (
            <div style={styles.overlay} onClick={() => { setShowSettings(false); resetSelfTest(); }}>
              <div style={styles.pickerCard} onClick={e => e.stopPropagation()}>
                <div style={styles.pickerTitle}>{scr.settings.title}</div>

                <div style={{ ...styles.pickerSectionLabel, marginTop: 0 }}>{scr.settings.sectionStyle}</div>
                {STYLE_OPTIONS.map(opt => (
                  <label key={opt.value} style={{
                    ...styles.pickerOption,
                    backgroundColor: effectiveStyle === opt.value ? '#fef3e2' : '#fff',
                    borderColor: effectiveStyle === opt.value ? '#f97316' : '#e7e5e4',
                  }}>
                    <input
                      type="radio"
                      name="companion_style"
                      value={opt.value}
                      checked={effectiveStyle === opt.value}
                      onChange={() => pickStyle(opt.value)}
                      style={{ marginRight: '12px', accentColor: '#f97316' }}
                    />
                    {_styleLabel(opt.value, scr)}
                  </label>
                ))}

                {/* #606 — experimental PCM toggle (idle screen).
                    Temporary admin-facing control; remove once we
                    lock in PCM behaviour. Hardcoded English label
                    is fine — only visible to whoever is testing. */}
                <div style={styles.pickerSectionLabel}>🧪 Experimental</div>
                <label style={{
                  ...styles.pickerOption,
                  backgroundColor: pcmDisabled ? '#fff' : '#fef3e2',
                  borderColor: pcmDisabled ? '#e7e5e4' : '#f97316',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <input
                      type="checkbox"
                      checked={!pcmDisabled}
                      onChange={togglePcm}
                      style={{ marginRight: '12px', accentColor: '#f97316', width: '18px', height: '18px' }}
                    />
                    <span>High-fidelity audio capture (PCM)</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#78716c', fontWeight: '400', marginTop: '6px', marginLeft: '30px' }}>
                    Faster end-of-utterance + better Dutch accuracy. May be less stable on long calls. Applies to your next turn.
                  </div>
                </label>

                <div style={styles.pickerSectionLabel}>{scr.settingsMic.sectionLabel}</div>
                <button onClick={handleMicTest} style={{
                  ...styles.pickerOption,
                  justifyContent: 'center',
                  backgroundColor: micTestState === 'recording' ? '#fef2f2' : '#fff',
                  borderColor: micTestState === 'recording' ? '#ef4444' : '#e7e5e4',
                  fontWeight: '600',
                }}>
                  {/* #551 — all four mic-test JSX text nodes were
                      hardcoded English; now sourced from
                      tablet.mictest.* + tablet.settings.sectionMicrophone
                      via the _useScreenStrings helper. */}
                  {micTestState === 'idle' && scr.settingsMic.idleLabel}
                  {micTestState === 'recording' && scr.settingsMic.recordingLabel}
                  {micTestState === 'checking' && scr.settingsMic.checkingLabel}
                  {micTestState === 'result' && micTestResult?.success && (
                    <span style={{ color: '#16a34a' }}>
                      {formatString(scr.settingsMic.weHeardTemplate, { text: micTestResult.text })}
                    </span>
                  )}
                  {micTestState === 'result' && !micTestResult?.success && (
                    <span style={{ color: '#ef4444' }}>{scr.settingsMic.notHeardLabel}</span>
                  )}
                </button>

                {/* Audio self-test (record-and-playback). Unlike the
                    mic-test above (MediaRecorder/Opus + STT round-trip),
                    this records through the live PCM worklet path and
                    plays the captured frames straight back, so PCM-path
                    quality loss (the #811 class) is audible client-side. */}
                <div style={styles.pickerSectionLabel}>{scr.selfTest.section}</div>
                {/* Raw vs. processed A/B (#980 follow-up). Off = exactly
                    what calls send; On = mic with EC/NS/AGC bypassed, to
                    isolate whether the browser voice DSP is what dulls a
                    wired headset. Locked while a capture/playback is live. */}
                <label style={{
                  ...styles.pickerOption,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  opacity: (selfTestState === 'recording' || selfTestState === 'playing') ? 0.5 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <input
                      type="checkbox"
                      checked={selfTestRaw}
                      disabled={selfTestState === 'recording' || selfTestState === 'playing'}
                      onChange={() => setSelfTestRaw(v => !v)}
                      style={{ marginRight: '12px', accentColor: '#f97316', width: '18px', height: '18px' }}
                    />
                    <span>{scr.selfTest.raw}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#78716c', fontWeight: '400', marginTop: '6px', marginLeft: '30px' }}>
                    {scr.selfTest.rawHint}
                  </div>
                </label>
                <button onClick={handleSelfTestRecord} style={{
                  ...styles.pickerOption,
                  justifyContent: 'center',
                  backgroundColor: selfTestState === 'recording' ? '#fef2f2' : '#fff',
                  borderColor: selfTestState === 'recording' ? '#ef4444' : '#e7e5e4',
                  fontWeight: '600',
                }}>
                  {selfTestState === 'recording'
                    ? scr.selfTest.stop
                    : (selfTestState === 'recorded' || selfTestState === 'playing')
                      ? scr.selfTest.recordAgain
                      : scr.selfTest.record}
                </button>
                {(selfTestState === 'recorded' || selfTestState === 'playing') && (
                  <button onClick={handleSelfTestPlay} style={{
                    ...styles.pickerOption,
                    justifyContent: 'center',
                    backgroundColor: selfTestState === 'playing' ? '#eff6ff' : '#fff',
                    borderColor: selfTestState === 'playing' ? '#3b82f6' : '#e7e5e4',
                    fontWeight: '600',
                  }}>
                    {selfTestState === 'playing' ? scr.selfTest.playing : scr.selfTest.play}
                  </button>
                )}
                <div style={{
                  fontSize: '12px',
                  color: selfTestState === 'error' ? '#ef4444' : '#78716c',
                  fontWeight: '400',
                  marginTop: '6px',
                  marginLeft: '4px',
                }}>
                  {selfTestState === 'error'
                    ? scr.selfTest.error
                    : (selfTestState === 'recorded' || selfTestState === 'playing')
                      ? `${selfTestRecordedRaw ? scr.selfTest.tagRaw : scr.selfTest.tagProcessed} · ${(selfTestMs / 1000).toFixed(1)}s`
                      : scr.selfTest.hint}
                </div>
              </div>
            </div>
          )}

          <CompanionFace status="idle" style={effectiveStyle} companionName={companionName} />

          <button
            onClick={startUserCall}
            disabled={callJustEnded}
            style={{
              ...styles.callButton,
              // #1058 — dim + disable for the post-hangup cooldown so an
              // accidental tap right after ending a call can't re-dial.
              ...(callJustEnded ? styles.callButtonCoolingDown : null),
            }}
          >
            {/* #551 — was hardcoded "📞 Call {companionName}". */}
            {formatString(scr.idleCall.buttonTemplate, { name: companionName })}
          </button>
        </div>
        <div style={styles.version}>
          v{VERSION}
          {activeAudioMode && (
            <> · audio: <span style={{ fontWeight: 600 }}>{activeAudioMode === 'pcm' ? 'PCM' : 'WebM'}</span></>
          )}
        </div>
        {/* Bedside-aware idle dim (#138) — also rendered in the active-call
            return below. Without this, the dim never showed on the idle
            screen, which is the primary nightstand use case. */}
        <IdleDimOverlay dimmed={dimmed} config={dimConfig} onWake={wakeIdleDim} />
      </div>
    );
  }

  // ── CALLING screen ──────────────────────────────────────────────────────
  if (callState === 'calling') {
    return (
      <div style={styles.container}>
        {/* #623 — AudioPlayer is rendered as the FIRST direct child of
            <div style={styles.container}> in BOTH the calling and connected
            branches, with identical props. React reconciles same-type
            same-position elements across renders, so the AudioPlayer
            instance (and its AudioContext) stays mounted when callState
            flips calling→connected — no unmount, no async ctx.close()
            race overlapping with the new context's playback. See the
            comment on `handleAudioPlayerDone` above for the full
            rationale. */}
        <AudioPlayer ref={audioPlayerRef} onDone={handleAudioPlayerDone} />
        <div style={styles.callingInner}>
          <CompanionFace status="idle" style={effectiveStyle} companionName={companionName} />

          <p style={styles.callingText}>
            {formatString(scr.calling.template, { name: companionName })}
          </p>

          <button onClick={handleHangUp} style={styles.hangUpButton}>
            {scr.hangup.button}
          </button>
        </div>
        <div style={styles.version}>
          v{VERSION}
          {activeAudioMode && (
            <> · audio: <span style={{ fontWeight: 600 }}>{activeAudioMode === 'pcm' ? 'PCM' : 'WebM'}</span></>
          )}
        </div>
        <style>{`
          @keyframes callingPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
        {/* Bedside-aware idle dim (#138) — won't visibly fire here since
            status='calling' suppresses, but mounted for state continuity. */}
        <IdleDimOverlay dimmed={dimmed} config={dimConfig} onWake={wakeIdleDim} />
      </div>
    );
  }

  // ── CONNECTED screen — #405 split layout ───────────────────────────────
  //
  // Replaces the original #137 three-zone (header/body/footer) layout
  // with the in-game split that #390 PR C introduced:
  //
  //   tablet-landscape → focal area 70% (left) + CallStrip side 30% (right)
  //   other modes      → CallStrip strip on top + focal area below
  //                      phone-portrait gets the 80px pill instead
  //
  // Berta's presence (face, status, last reply, hang-up) lives in the
  // CallStrip — visible on every viewport, never blocked by activities.
  // The focal area carries YouSaidCard (the user's words back to them
  // — Q1+Q2 from the design walkthrough). Photos / music / video / game
  // remain as full-screen overlays for now (will be re-scoped to fit
  // the focal area in a follow-up — likely #409).
  const isPhonePortrait = windowWidth <= PHONE_PORTRAIT_MAX;
  // viewportMode is still used for the `pill` decision (phone-portrait
  // gets the compact 80px strip vs. the taller `strip` variant). The
  // BIG split decision (side-rail vs. top-strip / pill) now reads from
  // the measured call-root box, so a phone rotating to landscape gets
  // the side-rail layout the moment its width exceeds its height plus
  // the FRAME_SPLIT_MIN_WIDTH floor — same threshold ActivityFrame
  // uses, so the connected-call view and any open activity overlay
  // switch together on rotation.
  const isHorizontalSplit =
    callRootBox.width > callRootBox.height &&
    callRootBox.width >= FRAME_SPLIT_MIN_WIDTH;
  const callStripVariant =
    isHorizontalSplit                   ? 'side' :
    viewportMode === 'phone-portrait'   ? 'pill' :
    'strip';
  // #1009 — the dedicated-transcript-row feature now keys off "is the
  // strip NOT a side rail?" rather than the portrait viewport buckets.
  // Same product intent (portrait calls get the dedicated row; side-
  // rail layouts let the strip carry the transcript) — extended so a
  // narrow landscape that doesn't qualify for the split (width <
  // FRAME_SPLIT_MIN_WIDTH) still gets the dedicated row.
  const isPortraitCall = !isHorizontalSplit;
  // The strip/pill drops its inline transcript and the focal-area
  // UserTranscriptRow takes over — portrait only, and not while a photo
  // carousel owns the focal area (then the strip keeps the transcript).
  const portraitDedicatedTranscript = isPortraitCall && !photoCarousel;
  // #1032 — current-speaker slot: the single focal panel shows the user's
  // live transcript ("You") on their turn, else Berta's reply. Applies in
  // every non-pill layout (portrait AND landscape). `focalSpeaker` stays
  // 'companion' through the re-read pause (it only flips to 'user' on a
  // transcript event), so a freshly-cleared transcript correctly falls back
  // to her reply rather than a blank "You".
  const showingUser = focalSpeaker === 'user' && !!(transcript && transcript.trim());
  const callStripDimension =
    callStripVariant === 'side' ? { width: 'min(360px, 32vw)' } :
    callStripVariant === 'pill' ? { height: 80 } :
    { height: 'clamp(112px, 16vh, 148px)' };  // #1016 — trimmed from clamp(140,22vh,200); the A9's short viewport needs the room for Berta + the transcript row
  // #430 — display-only status swap. While music is playing the
  // listening-resume useEffect (above, line ~416) skips the mic
  // restart, so the underlying `status` of 'listening' is a lie:
  // the strip would render the red pulse + "Listening" label and
  // the elderly user thinks Berta hears them when in fact she
  // doesn't. Only flips the visual; the call-state machine is
  // unchanged. `idle` also gets the swap so the visual is
  // consistent if music starts mid-greeting (rare edge case).
  const stripDisplayStatus =
    nowPlaying && (status === 'listening' || status === 'idle')
      ? 'music_playing'
      : status;
  return (
    <div style={styles.container}>
      {/* #623 — AudioPlayer is rendered as the FIRST direct child of
          <div style={styles.container}> in BOTH the calling and connected
          branches with identical props, so React keeps it mounted across
          the calling→connected transition. See the `handleAudioPlayerDone`
          comment above for the full rationale. */}
      <AudioPlayer ref={audioPlayerRef} onDone={handleAudioPlayerDone} />
      {/* #1009 — the connected-call settings overlay was removed along
          with the in-call gear. Settings (conversation mode, companion
          style, PCM toggle, audio self-test) are reachable ONLY from the
          idle screen — never mid-call. This is the whole "no settings
          changes during a call" requirement; the gear was just the
          entry point. The idle-screen overlay (above, in the idle
          return) is the single canonical settings surface. */}

      <div ref={callRootRef} style={styles.callRoot}>
        {/* HEADER — brand + settings cog only. Companion widget moved
            to the CallStrip (#405) so Berta has one canonical place
            on screen, not two. */}
        <header style={styles.callHeader}>
          <div style={styles.callBrand} aria-label="Amigo Mio"> {/* i18n-exempt: brand name */}
            <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" style={styles.callBrandIcon} />
            {!isPhonePortrait && (
              <span style={styles.callBrandWord}>Amigo Mio</span> /* i18n-exempt: brand name */
            )}
          </div>
          {/* #1009 — the settings gear is intentionally NOT rendered in
              the in-call header: the elderly user (and a caregiver) must
              not be able to change settings mid-call. Settings stay
              reachable from the idle screen (which has its own gear). */}
        </header>

        {/* SPLIT BODY — focal area + CallStrip. Direction depends on
            viewport: row on tablet-landscape (strip on right),
            column on others (strip on top). */}
        <main style={{
          ...styles.callSplitBody,
          flexDirection: isHorizontalSplit ? 'row' : 'column',
        }}>
          {/* CallStrip rendered first when stacked vertically (top), or
              second when side-by-side (right). Document order matches
              flex-direction so DOM order makes sense to screen readers. */}
          {!isHorizontalSplit && (
            <div style={callStripDimension}>
              <CallStrip
                status={stripDisplayStatus}
                reply={reply}
                transcript={transcript}
                companionName={companionName}
                variant={callStripVariant}
                onHangup={handleHangUp}
                showHangupLabel={callStripVariant === 'side'}
                connectionHiccup={connectionHiccup}
                /* #428 — focal-area BertaReplyPanel owns the reply now.
                   Strip just shows face + name + status + hangup. Pill
                   variant on phone-portrait still needs the reply
                   inline (see focal-area logic above). */
                showReply={callStripVariant === 'pill'}
                /* #432 — when a photo overlay is inline, the focal-area
                   YouSaidCard hides; surface the user's last completed
                   utterance in the strip so they always see "you said
                   X" while looking at the photo. */
                inlineYouSaid={
                  photoCarousel && callStripVariant === 'side'
                    ? frozenUtterance
                    : ''
                }
                /* #700 — pause/resume button. Visible during a
                   connected call; tapping toggles wait mode via the
                   server (same WS round-trip as the voice gate). */
                onTogglePause={callState === 'connected' ? handlePauseToggle : null}
                isWaiting={waitMode}
                /* #1009/#1032 — the strip shows the live transcript ONLY
                   when a media overlay (photo carousel) owns the focal
                   area. Otherwise the transcript lives in the focal
                   BertaReplyPanel current-speaker slot (#1032 — now in
                   landscape too, not just portrait) or, on the phone pill,
                   the dedicated UserTranscriptRow. */
                showTranscript={!!photoCarousel}
              />
            </div>
          )}

          <div style={styles.focalArea}>
            {/* #1089 — photos are now a standalone ActivityFrame activity
                (rendered below), no longer inline in the conversation focal
                area. The focal area always shows the conversation. */}
            {(
              <>
                {/* #428 — Berta's reply is the focal content. Big readable
                    text, top of the area, persists across listening /
                    transcribing / thinking / speaking until the next reply
                    lands. The phone-portrait pill is too tight for a
                    meaningful reply layout — falls back to the old gated
                    pattern (reply visible only during speaking/idle). */}
                {callStripVariant !== 'pill' ? (
                  <BertaReplyPanel
                    reply={reply}
                    companionName={companionName}
                    /* #1032 — single current-speaker panel: on the user's
                       turn it shows their live transcript under a "You"
                       heading instead of Berta's reply. Applies in EVERY
                       non-pill layout (tablet-portrait AND landscape); the
                       phone pill keeps its own gated reply + transcript row. */
                    userTranscript={transcript}
                    showUser={showingUser}
                    /* #1032 — once she's replied this call, an empty panel
                       (the brief gap when a new reply starts) shows a blank
                       box, not the idle "Hello…" placeholder. */
                    allowPlaceholder={!callHasReply}
                    scrollRef={replyPanelRef}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
                      replyStickToBottomRef.current = nearBottom;
                    }}
                  />
                ) : (
                  reply && (status === 'speaking' || status === 'idle') && (
                    <div
                      ref={replyPanelRef}
                      style={styles.replyPanelSplit}
                      onScroll={(e) => {
                        const el = e.currentTarget;
                        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
                        replyStickToBottomRef.current = nearBottom;
                      }}
                    >
                      {reply}
                    </div>
                  )
                )}

                {/* YouSaidCard removed from the focal area on Bas's
                    request 2026-05-08 — BertaReplyPanel now uses the
                    full focal area in the no-media default call view.
                    The user's "you said" anchor still lives in the
                    right-side strip during a photo carousel via
                    CallStrip's inlineYouSaid prop (#432) — that path
                    is unchanged. The component file + getCardMode
                    helper + tests remain in the codebase for now in
                    case the focal-area card is wanted back later. */}

                {/* #1009 / #1032 — dedicated live-transcription row, now
                    phone-portrait (pill) ONLY. In tablet-portrait (the
                    production A9 since #1016) the unified BertaReplyPanel
                    above hosts the transcript on the user's turn, so a
                    separate row would double it. The pill layout keeps its
                    own gated reply div, so it still needs this row.
                    Landscape keeps the in-strip transcript (CallStrip
                    showTranscript). Renders nothing when empty. */}
                {portraitDedicatedTranscript && callStripVariant === 'pill' && (
                  <UserTranscriptRow transcript={transcript} />
                )}
              </>
            )}

            {micWarning && (
              <div style={styles.micWarning}>{micWarning}</div>
            )}
          </div>

          {isHorizontalSplit && (
            <div style={callStripDimension}>
              <CallStrip
                status={stripDisplayStatus}
                reply={reply}
                transcript={transcript}
                companionName={companionName}
                variant={callStripVariant}
                onHangup={handleHangUp}
                showHangupLabel={callStripVariant === 'side'}
                connectionHiccup={connectionHiccup}
                /* #428 — focal-area BertaReplyPanel owns the reply now.
                   Strip just shows face + name + status + hangup. Pill
                   variant on phone-portrait still needs the reply
                   inline (see focal-area logic above). */
                showReply={callStripVariant === 'pill'}
                /* #432 — when a photo overlay is inline, the focal-area
                   YouSaidCard hides; surface the user's last completed
                   utterance in the strip so they always see "you said
                   X" while looking at the photo. */
                inlineYouSaid={
                  photoCarousel && callStripVariant === 'side'
                    ? frozenUtterance
                    : ''
                }
                /* #700 — pause/resume button. Visible during a
                   connected call; tapping toggles wait mode via the
                   server (same WS round-trip as the voice gate). */
                onTogglePause={callState === 'connected' ? handlePauseToggle : null}
                isWaiting={waitMode}
                /* #1009/#1032 — the strip shows the live transcript ONLY
                   when a media overlay (photo carousel) owns the focal
                   area. Otherwise the transcript lives in the focal
                   BertaReplyPanel current-speaker slot (#1032 — now in
                   landscape too, not just portrait) or, on the phone pill,
                   the dedicated UserTranscriptRow. */
                showTranscript={!!photoCarousel}
              />
            </div>
          )}
        </main>
      </div>

      {/* #1089 — photos as a standalone activity inside the ActivityFrame
          (the single standardized chrome). Replaces both the old inline-in-
          conversation path and the phone-portrait full-screen pill. */}
      {photoCarousel && (
        <ActivityFrame
          status={stripDisplayStatus}
          reply={reply}
          transcript={transcript}
          showingUser={showingUser}
          companionName={companionName}
          onClose={() => setPhotoCarousel(null)}
        >
          <PhotoCarousel
            photos={photoCarousel.photos}
            onOpened={() => {
              // Tell the server we're viewing a photo — it widens the
              // silence-tolerance threshold so "user looking quietly at
              // photo" doesn't trip the silence-ladder ("Take your time…").
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'photo_opened' }));
              }
            }}
            onClosed={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'photo_closed' }));
              }
            }}
            onHelp={() => {
              // Ask the server to have Berta explain the photo controls.
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'photo_help_requested' }));
              }
            }}
          />
        </ActivityFrame>
      )}

      {videoOverlay && (
        <ActivityFrame
          status={stripDisplayStatus}
          reply={reply}
          transcript={transcript}
          showingUser={showingUser}
          companionName={companionName}
          onClose={() => setVideoOverlay(null)}
        >
          <VideoOverlay
            youtubeId={videoOverlay.youtubeId}
            fallbackIds={videoOverlay.fallbackIds || []}
            title={videoOverlay.title}
            onUnavailable={(reason) => {
              // #625 — every candidate (primary + fallbacks) failed to
              // reach PLAYING. Tell the server so Berta apologises
              // verbally, then close the overlay. Server side handles
              // the bracket-and-cut + media-active flag reset.
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({
                  type: 'video_unavailable',
                  query: videoOverlay.query || '',
                  reason: reason || 'all_candidates_failed',
                }));
              }
              setVideoOverlay(null);
            }}
          />
        </ActivityFrame>
      )}

      {/* #390 — Domino game overlay. Full-screen at z-index 100 so it
          sits ABOVE the existing UI (companion face, status pill, etc.).
          Touch-only moves; the mic state is unchanged — it stays open
          during the user's turn (per Q3 lock) so they can chat freely
          while playing, and the existing lenaSpeakingRef gate already
          mutes during Berta's narration to prevent echo.
          PR C polish: now with side/top CallStrip showing Berta's
          status + last reply so the user sees what's happening on
          the call side during the game. Hangup is also exposed via a
          demoted icon in the strip — primary "leave game" path is
          still the Done button. */}
      {dominoState && (
        <ActivityFrame
          status={stripDisplayStatus}
          reply={reply}
          transcript={transcript}
          showingUser={showingUser}
          companionName={companionName}
          onClose={() => {
            // #1089 — the frame's X leaves the game (back to the
            // conversation view). No hangup inside an activity.
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'domino_quit' }));
            }
            setDominoState(null);
          }}
        >
          <DominoGame
            state={dominoState}
            onPlay={(tile, end) => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'domino_play', tile, end }));
              }
            }}
            onKnock={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'domino_knock' }));
              }
            }}
            onQuit={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'domino_quit' }));
              }
              setDominoState(null);
            }}
            companionName={companionName}
          />
        </ActivityFrame>
      )}

      {solitaireState && (
        <ActivityFrame
          status={stripDisplayStatus}
          reply={reply}
          transcript={transcript}
          showingUser={showingUser}
          companionName={companionName}
          onClose={() => {
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'solitaire_quit' }));
            }
            setSolitaireState(null);
          }}
        >
          <SolitaireGame
            state={solitaireState}
            onMove={(move) => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'solitaire_move', move }));
              }
            }}
            onDraw={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'solitaire_draw' }));
              }
            }}
            onUndo={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'solitaire_undo' }));
              }
            }}
            onQuit={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'solitaire_quit' }));
              }
              setSolitaireState(null);
            }}
          />
        </ActivityFrame>
      )}

      {gamesMenuState && (
        <ActivityFrame
          status={stripDisplayStatus}
          reply={reply}
          transcript={transcript}
          showingUser={showingUser}
          companionName={companionName}
          onClose={() => setGamesMenuState(null)}
        >
          <GameCarousel
            games={gamesMenuState.games}
            personalised={gamesMenuState.personalised}
            onPick={(gameId) => {
              // #1029 — tell the server which game; it speaks Berta's warm
              // acknowledgement and then opens that game's overlay (deferred
              // until the ack finishes). Close the picker immediately.
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'game_pick', game: gameId }));
              }
              setGamesMenuState(null);
            }}
          />
        </ActivityFrame>
      )}

      <MusicPlayer ref={musicPlayerRef} onStateChange={(s) => {
        const isPlaying = !!s.youtubeId;
        const wasPlaying = !!nowPlaying;
        setNowPlaying(isPlaying ? { youtubeId: s.youtubeId, title: s.title } : null);
        // #436 — tell the server when music starts / stops so it can
        // pause the idle-timeout (which otherwise counts down against
        // the user during music playback and ends the call mid-song).
        // Symmetrical to the existing photo_opened / photo_closed
        // messages. Skipped when the WS isn't connected.
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (isPlaying && !wasPlaying) {
          ws.send(JSON.stringify({ type: 'music_started', title: s.title || null }));
        } else if (!isPlaying && wasPlaying) {
          ws.send(JSON.stringify({ type: 'music_stopped' }));
        }
      }} />
      {/* #448 — full-screen MusicView replaces the small NowPlayingPill.
          onStop bundles BOTH responsibilities (stop the YouTube iframe +
          clear the React state) so the dismiss-stops-music guarantee
          can never desync — Bas's "music continues after closing"
          symptom was caused by separating those two concerns and
          having the visible state clear without the audio actually
          stopping. */}
      {nowPlaying && (
        <ActivityFrame
          status={stripDisplayStatus}
          reply={reply}
          transcript={transcript}
          showingUser={showingUser}
          companionName={companionName}
          onClose={() => {
            // #1089 — the frame's X owns dismiss; keep the #448 bundled
            // stop() + clear + notify so dismiss-stops-music can't desync.
            try {
              if (musicPlayerRef.current) musicPlayerRef.current.stop();
            } catch (e) {
              console.warn('[#448] musicPlayer.stop() failed (non-fatal):', e);
            }
            setNowPlaying(null);
            // Tell the server the user dismissed — pairs with the
            // existing music_stopped WS event in MusicPlayer's
            // onStateChange. Defensive: if the YouTube state-change
            // doesn't fire (race), the explicit send keeps server
            // state consistent.
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
              try {
                ws.send(JSON.stringify({ type: 'music_stopped' }));
              } catch (_) {}
            }
          }}
        >
          <MusicView title={nowPlaying.title} playerRef={musicPlayerRef} />
        </ActivityFrame>
      )}

      {/* #623 — old <AudioPlayer> mount-point removed. The single
          AudioPlayer is now rendered at the top of the
          <div style={styles.container}> in BOTH the calling and connected
          branches so React keeps it mounted across the call-state
          transition. See `handleAudioPlayerDone` comment above. */}
      <div style={styles.version}>
        v{VERSION}
        {activeAudioMode && (
          <> · audio: <span style={{ fontWeight: 600 }}>{activeAudioMode === 'pcm' ? 'PCM' : 'WebM'}</span></>
        )}
      </div>
      {/* Bedside-aware idle dim (#138) — see hooks/useIdleDim.js */}
      <IdleDimOverlay dimmed={dimmed} config={dimConfig} onWake={wakeIdleDim} />
    </div>
  );
}

const styles = {
  container: {
    // #281 — `100vh` on mobile browsers includes the dynamic UI bars
    // (URL bar, home-indicator gesture area), which clipped the
    // hang-up button on phones. `100dvh` (dynamic viewport height)
    // measures the *currently visible* viewport, so the layout
    // adapts when chrome appears/disappears. Older browsers fall
    // through to `100vh`.
    height: '100dvh',
    // #1016 — was '100vh'. On the A9 the system task/nav bar makes 100vh
    // TALLER than the visible viewport, so minHeight:100vh forced the root
    // past the bottom of the screen and clipped the last row (the new
    // transcription row). Use dvh so the root matches what's visible.
    minHeight: '100dvh',
    width: '100vw',
    overflow: 'hidden',
    background: 'linear-gradient(180deg, #fff8f0 0%, #fef3e2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },

  // ── Idle ──
  idleInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '48px',
  },
  callButton: {
    padding: '20px 48px',
    borderRadius: '40px',
    border: 'none',
    backgroundColor: '#10b981',
    color: '#fff',
    fontSize: '28px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 8px 32px rgba(16,185,129,0.35)',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
  },
  // #1058 — applied on top of callButton for POST_HANGUP_COOLDOWN_MS
  // after a call ends: a dimmed, non-interactive "just ended" state so
  // an accidental tap right after hang-up can't start a phantom call.
  callButtonCoolingDown: {
    opacity: 0.45,
    cursor: 'default',
    boxShadow: 'none',
  },

  // ── Calling ──
  callingInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '32px',
  },
  callingText: {
    fontSize: '28px',
    fontWeight: '500',
    color: '#78716c',
    animation: 'callingPulse 2s ease-in-out infinite',
  },

  // ── #137 redesigned call screen ──────────────────────────────
  // Three-zone responsive layout: header, scrollable body, footer.
  // The reply panel is the only scrollable region — header and footer
  // are fixed-height so neither ever covers reply text.

  callRoot: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    // #281 — see container.height comment. Use dvh on mobile so the
    // hang-up button (in the strip) stays inside the visible viewport
    // even when browser chrome appears/disappears.
    height: '100dvh',
    minHeight: '100dvh',  // #1016 — 100vh clipped the bottom row on the A9 (see container note)
    // Bas 2026-05-08 screenshot: on wide displays (≥1600px) the
    // focal area over-grew because the strip caps at min(360px, 32vw)
    // while the focal flex:1 ate everything else — left side ~85%,
    // right side ~15%. The 1280-px max-width keeps the split feeling
    // tablet-native on wide screens (focal ~920 + strip ~360 = ~1280)
    // while still letting smaller phones fill 100%. margin: 0 auto
    // centres the column when the viewport is wider than the cap.
    maxWidth: 1280,
    margin: '0 auto',
  },

  // ── Header ──
  callHeader: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 18px',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    background: 'rgba(255,248,240,0.85)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    // #852 raised this to 80px (bigger icon + gear tap target) for A9
    // readability. #1016 — the gear is gone (#1009) and the A9's short
    // viewport (esp. landscape, ~399px tall) needs the vertical space, so
    // trimmed to 52px. The brand word font is unchanged (still readable).
    minHeight: '52px',
  },
  callBrand: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#2C2C2C',
    textDecoration: 'none',
  },
  // #852 — call-header sizing raised for A9-portrait readability.
  // Original values were calibrated for desktop; on the A9 they
  // landed at 12-15 px which is below the comfortable arm's-length
  // reading floor for elderly users. New values target 18-22 px on
  // the same device while still looking proportional on desktop.
  callBrandIcon: {
    height: '36px',  // #1016 — 44 → 36 to shrink the in-call header (gear gone; the A9 needs the height)
    width: 'auto',
    display: 'block',
  },
  callBrandWord: {
    fontSize: '1.25rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: '#2C2C2C',
  },
  callCompanionWidget: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
  },
  callCompanionFace: {
    // Wrap CompanionFace and constrain to header size. The full-screen
    // avatar component renders proportionally in this small box; the
    // smiley/waveform/text-only switcher still controls how it draws.
    // #852 — 40 → 56 px.
    width: '56px',
    height: '56px',
    overflow: 'hidden',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  callCompanionLabel: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.15,
  },
  callCompanionName: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#2C2C2C',
  },
  callCompanionStatus: {
    fontSize: '1rem',
    color: '#78716c',
  },
  // #1009 — `callGear` removed: the settings gear no longer renders in
  // the in-call header (no settings changes mid-call). The idle screen
  // keeps its own gear with its own `gearButton` style.

  // ── Body (scrollable) ──
  callBody: {
    // (legacy — kept for the calling/intake screens that still render
    // the centred layout. Connected screen uses callSplitBody below.)
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '24px clamp(16px, 4vw, 32px)',
    overflow: 'hidden',
  },

  // ── #405 split body — focal area + CallStrip ──
  callSplitBody: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    overflow: 'hidden',
    width: '100%',
  },
  focalArea: {
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    padding: 'clamp(8px, 2vw, 24px)',
    // #1016 — add the device safe-area inset so the bottom transcription
    // row clears the Android nav/task bar instead of being clipped.
    paddingBottom: 'calc(clamp(8px, 2vw, 24px) + env(safe-area-inset-bottom, 0px))',
    gap: 'clamp(8px, 1.5vw, 16px)',
    overflow: 'hidden',
  },
  replyPanelSplit: {
    flex: '0 1 auto',
    maxHeight: '40%',
    overflowY: 'auto',
    overflowX: 'hidden',
    // #852 — clamp(16, 2.2vw, 20) → clamp(22, 3vw, 28) so the legacy
    // split reply panel meets the same readability floor as the
    // BertaReplyPanel that replaced it. This style block is still
    // referenced by setup / pre-call screens.
    fontSize: 'clamp(22px, 3vw, 28px)',
    lineHeight: 1.55,
    color: '#2C2C2C',
    textAlign: 'left',
    maxWidth: '700px',
    width: '100%',
    margin: '12px auto 0',
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.4)',
    borderRadius: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    WebkitOverflowScrolling: 'touch',
  },
  userTranscriptCard: {
    flex: '0 0 auto',
    background: 'rgba(0,0,0,0.04)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: '12px',
    padding: '12px 16px',
    maxWidth: '700px',
    width: '100%',
    margin: '0 auto',
  },
  userTranscriptLabel: {
    // #852 — bumped 0.72rem → 0.9rem.
    fontSize: '0.9rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#a8a29e',
    marginBottom: '4px',
  },
  userTranscriptText: {
    // #852 — 1rem → 1.2rem.
    fontSize: '1.2rem',
    color: '#44403c',
    lineHeight: 1.5,
    fontStyle: 'italic',
  },
  replyPanel: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    // #852 — clamp(18, 2.6vw, 22) → clamp(24, 3.2vw, 30). The
    // pre-CallStrip layout still uses this; alignment with the new
    // BertaReplyPanel floor (28 px on A9) is intentional.
    fontSize: 'clamp(24px, 3.2vw, 30px)',
    lineHeight: 1.6,
    color: '#2C2C2C',
    textAlign: 'left',
    maxWidth: '700px',
    width: '100%',
    margin: '0 auto',
    padding: '8px 4px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    // Touch-friendly scroll on tablets
    WebkitOverflowScrolling: 'touch',
  },

  // ── Footer ──
  callFooter: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // #281 — bottom padding includes safe-area inset so the hang-up
    // button isn't clipped by the home-indicator gesture bar on
    // iOS / Android. The fallback (16px) covers browsers without
    // env() support. Top padding stays at 16px.
    padding: '16px 20px calc(16px + env(safe-area-inset-bottom, 0px)) 20px',
    borderTop: '1px solid rgba(0,0,0,0.06)',
    background: 'rgba(255,248,240,0.95)',
    minHeight: '72px',
  },

  // ── Connected (legacy, kept for reference; no longer rendered) ──
  connectedInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '90vh',
    width: '100%',
    maxWidth: '600px',
    padding: '40px 20px',
    paddingBottom: '100px',
  },

  // ── Hang up ──
  hangUpButton: {
    padding: '14px 36px',
    borderRadius: '32px',
    border: 'none',
    backgroundColor: '#ef4444',
    color: '#fff',
    fontSize: '20px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(239,68,68,0.3)',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
  },
  hangUpButtonConfirm: {
    padding: '18px 44px',
    borderRadius: '32px',
    border: 'none',
    backgroundColor: '#dc2626',
    color: '#fff',
    fontSize: '22px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(220,38,38,0.45)',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
  },

  // ── Settings ──
  gearButton: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    // #852 — 24 → 30 px to match the in-call gear after the A9
    // readability bump. Idle screen consistency.
    fontSize: '30px',
    cursor: 'pointer',
    opacity: 0.5,
    padding: '8px',
    zIndex: 10,
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  pickerCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '28px',
    width: '320px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    // The overlay centres this card; cap it to the viewport and scroll
    // internally so sections below the fold (e.g. the Audio self-test
    // play-back button) stay reachable on short tablet viewports. Momentum
    // scroll on touch so it feels native on the A9.
    maxHeight: '90vh',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  pickerTitle: {
    // #852 — settings overlay text bumped for elderly readability.
    fontSize: '22px',
    fontWeight: '600',
    color: '#44403c',
    marginBottom: '20px',
    textAlign: 'center',
  },
  pickerSectionLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#a8a29e',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginTop: '20px',
    marginBottom: '10px',
  },
  pickerOption: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '2px solid',
    marginBottom: '10px',
    cursor: 'pointer',
    fontSize: '20px',
    fontWeight: '500',
    color: '#44403c',
    transition: 'all 0.15s ease',
  },

  // ── Incoming call ──
  incomingLabel: {
    fontSize: '28px',
    fontWeight: '500',
    color: '#78716c',
    animation: 'incomingPulse 2s ease-in-out infinite',
    textAlign: 'center',
  },
  answerButton: {
    padding: '20px 64px',
    borderRadius: '40px',
    border: 'none',
    backgroundColor: '#10b981',
    color: '#fff',
    fontSize: '28px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 8px 32px rgba(16,185,129,0.35)',
    animation: 'incomingPulse 2s ease-in-out infinite',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
  },
  dismissLink: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#a8a29e',
    cursor: 'pointer',
    padding: '8px 16px',
    textDecoration: 'underline',
    marginTop: '8px',
  },

  // ── Mic warning ──
  micWarning: {
    backgroundColor: '#fef3c7',
    border: '1px solid #f59e0b',
    borderRadius: '12px',
    padding: '12px 20px',
    // #852 — 16 → 18 px so an elderly user can read the warning on A9
    // without leaning in.
    fontSize: '18px',
    color: '#92400e',
    textAlign: 'center',
    maxWidth: '90%',
    lineHeight: '1.4',
  },

  // ── Common ──
  version: {
    position: 'fixed',
    bottom: '8px',
    right: '12px',
    fontSize: '11px',
    color: '#c7b8a8',
    opacity: 0.6,
  },
  setupInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '40px 24px',
    maxWidth: '480px',
  },
  setupLogo: {
    /* #238 PR-2 — was a 🎵 emoji at 80px font; now uses the logo-mark
       SVG. 110px height accounts for the wider aspect ratio
       (500w×360h) so visual weight matches the previous emoji. */
    height: '110px',
    width: 'auto',
    marginBottom: '24px',
    display: 'block',
  },
  setupHeading: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#44403c',
    margin: '0 0 16px 0',
  },
  setupText: {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#78716c',
    margin: '0 0 24px 0',
  },
  setupUrl: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#E8845A',
    backgroundColor: '#fff8f0',
    padding: '12px 24px',
    borderRadius: '12px',
    border: '2px solid #fed7aa',
  },
  // #1120 — setup-confirm buttons. Large primary tap target for the
  // elderly user / family member; subdued text cancel below it.
  setupConfirmBtn: {
    marginTop: '8px',
    background: '#E8845A',
    color: '#fff',
    border: 'none',
    borderRadius: '14px',
    padding: '18px 40px',
    fontSize: '20px',
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(232,132,90,0.35)',
  },
  setupCancelBtn: {
    marginTop: '14px',
    background: 'none',
    color: '#a8a29e',
    border: 'none',
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
};
